import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import { BufferGeometry, DoubleSide, Intersection, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, Vector3, SphereGeometry, LineBasicMaterial, BufferAttribute, Line } from 'three';
import { zipBorderArc } from '@three/common/halfEdgeMesh/operations/zipBorderArc';
import { equalizeSeamRims } from '@three/common/halfEdgeMesh/operations/seamRimEqualization';
import { fillArcWithConcentricRings } from '@segmentation/three/concentricRingFill';
import {
  WallFillFixture,
  captureWallFillFixture,
  downloadWallFillFixtures,
} from '@segmentation/dev/wallFillFixture';
import { consecutiveArchPairs, type XZ } from '@segmentation/three/gumTrimArch';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import InteractiveSpline from '@modules/three/common/curve/interactiveSpline/InteractiveSpline';
import { orderCutSegmentsIntoLoop } from '@modules/three/common/curve/orderCutSegmentsIntoLoop';
import { Vertex, Face, Halfedge } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { JawSegmentationCreate } from '@/schema/jawSegmentation';
import { ToothSegmentationCreate } from '@/schema/toothSegmentation';
import { geometryToPly } from '@modules/three/utils/meshExporter';
import { MeshAnchor } from '@/modules/three/common/MeshAnchorCollection';
import { teethSelectionStore } from '../store/teethSelectionStore';
import SplineControlPoint from '@/modules/three/common/curve/interactiveSpline/SplineControlPoint';
import aStar from '@modules/three/common/aStar';
import { getDevRuntime } from '@modules/dev/devRuntime';
import { SegmentationDebugStep } from '@segmentation/dev/segmentationDebug';
import type {
  SegmentationJawDevSnapshot,
  SegmentationSnapPartnerSnapshot,
  SegmentationToothDevSnapshot,
  Vec3Snapshot,
} from '@segmentation/dev/segmentationDevSnapshot';
import {
  loadRollbackSnapshotFromLocalStorage,
  saveRollbackSnapshotToLocalStorage,
  SEGMENTATION_DEV_SNAPSHOT_VERSION,
  vec3ToSnapshot,
} from '@segmentation/dev/segmentationDevSnapshot';
import { segmentationStore } from '@segmentation/store/segmentationStore';
import { SEGMENTATION_PROCESSING_PHASE } from '@segmentation/segmentationProcessingPhases';

interface InterdentalSidePath {
  startVertex: Vertex;
  endVertex: Vertex;
  vertices: Vertex[];
}

interface InterdentalBoundarySides {
  vestibular: InterdentalSidePath;
  lingual: InterdentalSidePath;
}

/** Anchor pair at an interdental gap - one vertex from each adjacent tooth */
interface InterdentalAnchorPair {
  toothAVertex: Vertex;  // Vertex from the "left" tooth (for tooth filling)
  toothBVertex: Vertex;  // Vertex from the "right" tooth (for tooth filling)
  gumAVertex: Vertex;    // Corresponding gum vertex for tooth A (for gum bridging)
  gumBVertex: Vertex;    // Corresponding gum vertex for tooth B (for gum bridging)
}

/** Anchors at an interdental gap - separated into vestibular and lingual */
interface InterdentalAnchors {
  vestibular: InterdentalAnchorPair;
  lingual: InterdentalAnchorPair;
}

/**
 * One interproximal contact from an unsnapped border strip: gum snap anchors at
 * the strip endpoints (adjacent snapped border verts) plus the strip for debug.
 */
interface PapillaSearchResult {
  toothIndex: number;
  pass: number;
  apex: Vertex;
  forwardWalk: Vertex[];
  backwardWalk: Vertex[];
  forwardAnchor: { gumVertex: Vertex; toothVertex: Vertex } | null;
  backwardAnchor: { gumVertex: Vertex; toothVertex: Vertex } | null;
  visitedIds: Set<number>;
  strip: Vertex[];
  stripStartLoopIdx: number;
  loop: Vertex[];
  snappedBefore: Vertex | null;
  snappedAfter: Vertex | null;
}

/** Extended snap info - which tooth vertex and which tooth index */
interface SnapInfo {
  toothVertex: Vertex;
  toothIndex: number;
  toothMesh: HalfEdgeMesh;
}

interface BorderStripCandidate {
  loop: Vertex[];
  loopIndex: number;
  strip: Vertex[];
  startIdx: number;
  stripStartLoopIdx: number;
  foundTransition: boolean;
}

export default class JawSegmentation extends HalfEdgeMesh {
  public teethEdges: Set<Vertex> = new Set();
  public toothBoundaryCurves: InteractiveSpline[] = [];
  private readonly IsUpper: boolean;
  /**
   * Faces this segmentation GENERATED rather than took from the scan — the
   * interproximal walls per tooth, and the interdental gum bridges on the gum
   * itself — are marked on the mesh that carries them
   * (`HalfEdgeMesh.generatedFaces`). They exist so the orthodontist sees a
   * closed model; the correction export deletes exactly them and rebuilds the
   * interproximal space between the posed crowns instead, guided by the single
   * index `HalfEdgeMesh.getGeneratedFacesFrom()` hands over.
   *
   * Clears the marks on the gum and every tooth so a re-run does not inherit
   * the previous run's.
   */
  private resetGeneratedFaces(teethHalfEdges: HalfEdgeMesh[]) {
    this.generatedFaces.clear();
    for (const tooth of teethHalfEdges) tooth.generatedFaces.clear();
  }
  private alignedBoundaryVerticesByTooth = new Map<number, Set<Vertex>>();
  private gumBoundaryToTooth = new Map<Vertex, number>();
  // Marks anchor indices where there is at least one missing tooth between
  // this anchor and the next present anchor. Used to skip interdental gum ops
  // between non-consecutive teeth in FDI sequence.
  private missingAfterAnchor: Set<number> = new Set();
  // Gap indices i (between tooth i and i+1) where STEP 2 found no interdental
  // corridor faces — the teeth are far apart and real gum remains between
  // them. Such gaps must skip papilla anchors and gum bridging entirely,
  // otherwise bogus anchors get paired across the gap and healthy gum is
  // collapsed into a bridge. Rebuilt on every segmentJaw run.
  private pairsWithoutCorridor: Set<number> = new Set();
  private preSegmentationGeometryBackup: BufferGeometry | null = null;
  private segmentedToothMeshes: HalfEdgeMesh[] = [];
  /** Verbose segmentation debug: groups, tables, styled headers in DevTools. */
  private papillaSearchDebug = false;
  /** Wall-fill snapshots gathered during STEP 6 when the dev toggle is on. */
  private wallFillFixtures: WallFillFixture[] = [];

  private static readonly SEG_STYLE = {
    step: 'color:#fff;background:#2563eb;font-weight:bold;padding:2px 6px;border-radius:3px',
    papilla: 'color:#67e8f9;font-weight:bold',
    vestLing: 'color:#fcd34d;font-weight:bold',
    fill: 'color:#86efac;font-weight:bold',
    pair: 'color:#c4b5fd;font-weight:bold',
    dim: 'color:#94a3b8;font-weight:normal',
    warn: 'color:#fca5a5;font-weight:bold',
  } as const;

  /**
   * Max number of intermediate gum-boundary vertices allowed between two
   * interdental anchors before `collapseVerticesBetweenAnchors` refuses to
   * collapse. A genuine papilla neck is a handful of verts; anything larger
   * means the boundary walk detoured the long way around a tooth hole and the
   * anchors are not actually adjacent across a thin gap.
   */
  private static readonly MAX_PAPILLA_COLLAPSE_PATH = 24;

  private segGroupStart(title: string, style: string, collapsed = true): void {
    if (!this.papillaSearchDebug) return;
    getDevRuntime().log({ kind: 'group', tag: 'segmentation', message: title });
    const fn = collapsed ? console.groupCollapsed : console.group;
    fn(`%c${title}`, style);
  }

  /** Updates the segmentation loading overlay and yields so React can paint. */
  private async reportProcessingPhase(phase: string): Promise<void> {
    if (!segmentationStore.getState().processingActive) return;
    segmentationStore.getState().setProcessingPhase(phase);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  private segGroupEnd(): void {
    if (!this.papillaSearchDebug) return;
    getDevRuntime().log({ kind: 'groupEnd', tag: 'segmentation', message: 'group end' });
    console.groupEnd();
  }

  private segLog(tag: string, style: string, message: string, detail?: unknown): void {
    if (!this.papillaSearchDebug) return;
    getDevRuntime().log({ kind: 'log', tag, message, data: detail });
    if (detail !== undefined) {
      console.log(`%c${tag}%c ${message}`, style, JawSegmentation.SEG_STYLE.dim, detail);
    } else {
      console.log(`%c${tag}%c ${message}`, style, JawSegmentation.SEG_STYLE.dim);
    }
  }

  private segWarn(tag: string, message: string, detail?: unknown): void {
    if (!this.papillaSearchDebug) return;
    getDevRuntime().log({ kind: 'warn', tag, message, data: detail });
    if (detail !== undefined) {
      console.warn(`%c${tag}%c ${message}`, JawSegmentation.SEG_STYLE.warn, 'color:inherit', detail);
    } else {
      console.warn(`%c${tag}%c ${message}`, JawSegmentation.SEG_STYLE.warn, 'color:inherit');
    }
  }

  private segTable(caption: string, rows: Record<string, unknown>[]): void {
    if (!this.papillaSearchDebug) return;
    getDevRuntime().log({ kind: 'table', tag: 'table', message: caption, data: rows });
    if (rows.length === 0) {
      console.log(caption, '(empty)');
      return;
    }
    console.log(caption);
    console.table(rows);
  }

  /** Ordered checkpoints — used for focus-tooth delta between phases. */
  private static readonly BORDER_HEALTH_PHASES = [
    'step1-after-extract',
    'step1-after-smooth',
    'step1-extract-smooth',
    'step1.5-strip-proximal',
    'step1.6-resmooth-strip',
    'step4-smooth-gum-boundary',
    'step5-snap',
    'step5.5-papilla',
  ] as const;

  private static readonly BORDER_HEALTH_STYLE =
    'color:#fde68a;background:#78350f;font-weight:bold;padding:2px 6px;border-radius:3px';

  /** Border topology snapshot for tracing fragmentation across pipeline steps. */
  private computeToothBorderHealth(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    snappedBorderCount?: number,
  ): Record<string, unknown> {
    toothMesh.calculateBorderVertices();
    const border = toothMesh.getBorderVertices();
    const borderIds = new Set(border.map((v) => v.id));
    const loops = toothMesh.getBorderLoops();
    const loopSizes = loops.map((l) => l.length).sort((a, b) => b - a);
    const mainLoopSize = loopSizes[0] ?? 0;
    const singletonLoopCount = loopSizes.filter((s) => s === 1).length;

    const borderNeighborsOf = (vertex: Vertex) =>
      vertex
        .getAdjacenceBoundaryVertices()
        .filter(
          (neighbor, index, all) =>
            borderIds.has(neighbor.id) &&
            all.findIndex((candidate) => candidate.id === neighbor.id) === index,
        );

    let isolatedVertexCount = 0;
    let spurVertexCount = 0;
    let branchVertexCount = 0;
    const degreeHistogram: Record<string, number> = { '0': 0, '1': 0, '2': 0, '3+': 0 };

    for (const vertex of border) {
      const deg = borderNeighborsOf(vertex).length;
      if (deg === 0) {
        isolatedVertexCount++;
        degreeHistogram['0']++;
      } else if (deg === 1) {
        spurVertexCount++;
        degreeHistogram['1']++;
      } else if (deg === 2) {
        degreeHistogram['2']++;
      } else {
        branchVertexCount++;
        degreeHistogram['3+']++;
      }
    }

    let adjacencyMismatchCount = 0;
    const mismatchSamples: Array<{
      vertexId: number;
      loopPredId: number | null;
      loopSuccId: number | null;
      graphNeighborIds: number[];
    }> = [];
    for (const loop of loops) {
      const n = loop.length;
      if (n < 2) continue;
      for (let i = 0; i < n; i++) {
        const vertex = loop[i];
        const loopPred = loop[(i + n - 1) % n];
        const loopSucc = loop[(i + 1) % n];
        const graphIds = new Set(borderNeighborsOf(vertex).map((v) => v.id));
        const predOk = graphIds.has(loopPred.id);
        const succOk = graphIds.has(loopSucc.id);
        if (!predOk || !succOk) {
          adjacencyMismatchCount++;
          if (mismatchSamples.length < 8) {
            mismatchSamples.push({
              vertexId: vertex.id,
              loopPredId: loopPred.id,
              loopSuccId: loopSucc.id,
              graphNeighborIds: [...graphIds],
            });
          }
        }
      }
    }

    const flags: string[] = [];
    if (border.length === 0) flags.push('no-border');
    if (isolatedVertexCount > 0) flags.push('isolated-verts');
    if (spurVertexCount > 0) flags.push('spurs');
    if (branchVertexCount > 0) flags.push('branch-points');
    if (singletonLoopCount >= 5) flags.push('many-singleton-loops');
    else if (singletonLoopCount > 0) flags.push('singleton-loops');
    if (loops.length > 3) flags.push('fragmented-loops');
    if (border.length > 0 && mainLoopSize < border.length * 0.5) flags.push('weak-main-loop');
    if (adjacencyMismatchCount > 0) flags.push('loop-graph-mismatch');

    let severity: 'ok' | 'warn' | 'bad' = 'ok';
    if (
      flags.includes('no-border') ||
      flags.includes('many-singleton-loops') ||
      flags.includes('fragmented-loops') ||
      flags.includes('isolated-verts')
    ) {
      severity = 'bad';
    } else if (flags.length > 0) {
      severity = 'warn';
    }

    return {
      tooth: toothIndex,
      borderVertexCount: border.length,
      loopCount: loops.length,
      loopSizes,
      mainLoopSize,
      singletonLoopCount,
      isolatedVertexCount,
      spurVertexCount,
      branchVertexCount,
      degreeHistogram,
      adjacencyMismatchCount,
      adjacencyMismatchSamples: mismatchSamples,
      snappedBorderCount: snappedBorderCount ?? null,
      flags,
      severity,
    };
  }

  private traceToothFillBorderLoops(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    phase: string,
    arc?: Vertex[],
  ): void {
    if (!this.papillaSearchDebug) return;

    const health = this.computeToothBorderHealth(toothMesh, toothIndex);
    const detail = {
      phase,
      arcLength: arc?.length ?? null,
      arcStartId: arc?.[0]?.id ?? null,
      arcEndId: arc?.[arc.length - 1]?.id ?? null,
      ...health,
    };

    const loopCount = Number(health.loopCount ?? 0);
    const message = `tooth ${toothIndex} · ${phase} · loops=${loopCount}`;
    if (loopCount === 1) {
      this.segLog('fill-border', JawSegmentation.BORDER_HEALTH_STYLE, message, detail);
    } else {
      this.segWarn('fill-border', message, detail);
    }
  }

  private findPreviousBorderHealthPhase(phase: string): string | null {
    const order = JawSegmentation.BORDER_HEALTH_PHASES as readonly string[];
    const idx = order.indexOf(phase);
    return idx > 0 ? order[idx - 1] : null;
  }

  /** Console + window.zubeeSegBorderHealth checkpoint after a pipeline step. */
  private logBorderHealthCheckpoint(
    phase: string,
    teethMeshes: HalfEdgeMesh[],
    meta?: { note?: string; snappedBorderCounts?: number[] },
  ): void {
    if (!this.papillaSearchDebug) return;

    const snapshots = teethMeshes.map((mesh, i) =>
      this.computeToothBorderHealth(mesh, i, meta?.snappedBorderCounts?.[i]),
    );

    const store = ((globalThis as Record<string, unknown>).zubeeSegBorderHealth ??= {}) as Record<
      string,
      Record<number, Record<string, unknown>>
    >;
    store[phase] = Object.fromEntries(snapshots.map((s) => [s.tooth as number, s]));

    this.segGroupStart(`border health · ${phase}`, JawSegmentation.BORDER_HEALTH_STYLE, false);
    if (meta?.note) {
      console.log(meta.note);
    }
    console.log(
      `All phases: window.zubeeSegBorderHealth["${phase}"] · focus: copy(JSON.stringify(window.zubeeSegBorderHealth["${phase}"][N]))`,
    );
    this.segTable(
      'border topology (all teeth)',
      snapshots.map((s) => ({
        tooth: s.tooth,
        border: s.borderVertexCount,
        loops: s.loopCount,
        mainLoop: s.mainLoopSize,
        singletons: s.singletonLoopCount,
        spurs: s.spurVertexCount,
        isolated: s.isolatedVertexCount,
        mismatches: s.adjacencyMismatchCount,
        snapped: s.snappedBorderCount,
        severity: s.severity,
        flags: (s.flags as string[]).join(', ') || '—',
      })),
    );

    for (const s of snapshots) {
      if (s.severity === 'ok') continue;
      this.segWarn(
        'border',
        `tooth ${s.tooth} [${s.severity}]: ${(s.flags as string[]).join(', ')} ` +
          `(loops=${s.loopCount}, main=${s.mainLoopSize}, singletons=${s.singletonLoopCount})`,
      );
    }

    const focus = getDevRuntime().segmentation.focusToothIndex;
    if (focus !== null && focus >= 0 && focus < teethMeshes.length) {
      const snap = snapshots[focus];
      const prevPhase = this.findPreviousBorderHealthPhase(phase);
      const prev = prevPhase ? store[prevPhase]?.[focus] : undefined;
      if (prev) {
        console.log(`%cFOCUS tooth ${focus} — delta since ${prevPhase}`, JawSegmentation.BORDER_HEALTH_STYLE, {
          loops: `${prev.loopCount} → ${snap.loopCount}`,
          mainLoop: `${prev.mainLoopSize} → ${snap.mainLoopSize}`,
          singletons: `${prev.singletonLoopCount} → ${snap.singletonLoopCount}`,
          spurs: `${prev.spurVertexCount} → ${snap.spurVertexCount}`,
          mismatches: `${prev.adjacencyMismatchCount} → ${snap.adjacencyMismatchCount}`,
        });
      }
      console.log(`=== COPY border health tooth ${focus} · ${phase} ===`);
      console.log(JSON.stringify(snap));
      console.log(`=== END COPY ===`);
    }

    this.segGroupEnd();
  }

  /** Per-tooth checkpoint (focus tooth only) — e.g. extract vs smooth within step 1. */
  private logFocusToothBorderHealth(phase: string, toothMesh: HalfEdgeMesh, toothIndex: number): void {
    if (!this.papillaSearchDebug || !this.shouldLogPapillaVerbose(toothIndex)) return;
    const snap = this.computeToothBorderHealth(toothMesh, toothIndex);
    const store = ((globalThis as Record<string, unknown>).zubeeSegBorderHealth ??= {}) as Record<
      string,
      Record<number, Record<string, unknown>>
    >;
    if (!store[phase]) store[phase] = {};
    store[phase][toothIndex] = snap;

    const prevPhase = this.findPreviousBorderHealthPhase(phase);
    const prev = prevPhase ? store[prevPhase]?.[toothIndex] : undefined;
    this.segLog(
      'border',
      JawSegmentation.BORDER_HEALTH_STYLE,
      `tooth ${toothIndex} · ${phase}`,
      prev
        ? {
            ...snap,
            deltaFromPrev: {
              phase: prevPhase,
              loops: `${prev.loopCount} → ${snap.loopCount}`,
              mainLoop: `${prev.mainLoopSize} → ${snap.mainLoopSize}`,
              singletons: `${prev.singletonLoopCount} → ${snap.singletonLoopCount}`,
            },
          }
        : snap,
    );
  }

  constructor(geometry: BufferGeometry, teethEdges: Array<number>, upper = false) {
    super(geometry);
    this.teethEdges = new Set(teethEdges.map((renderId) => this.halfEdges.renderIdToVertex.get(renderId)!));
    this.material = new MeshStandardMaterial({ vertexColors: true, side: DoubleSide, roughness: 0.15 });
    this.deleteAnchorsOnModeChange = false;
    this.IsUpper = upper;

    teethSelectionStore.subscribe(() => {
      this.updateTeethSelection();
    });
  }

  /**
   * Inform the segmentation that one or more FDI positions are missing between
   * anchor i and anchor i+1. Each entry in the provided collection marks the
   * anchor index i that must skip interdental processing with the next anchor.
   */
  public setMissingAfter(indices: Iterable<number>) {
    this.missingAfterAnchor = new Set(indices);
  }

  /**
   * Gap i (between tooth i and i+1) takes part in interdental processing:
   * it exists, no FDI position is missing across it, and STEP 2 found an
   * interdental corridor there (teeth close enough to share a papilla).
   */
  private isInterdentalGapActive(gapIndex: number, toothCount: number): boolean {
    if (gapIndex < 0 || gapIndex >= toothCount - 1) return false;
    return !this.missingAfterAnchor.has(gapIndex) && !this.pairsWithoutCorridor.has(gapIndex);
  }

  public getJawKind(): 'upper' | 'lower' {
    return this.IsUpper ? 'upper' : 'lower';
  }

  /** Saves contour state + mesh geometry before running segmentJaw (rollback target). */
  public saveRollbackCheckpoint(scanId: string | null): void {
    if (this.toothBoundaryCurves.length === 0) {
      throw new Error('No tooth contours to save');
    }
    const snapshot = this.exportDevSnapshot(scanId, this.getJawKind());
    saveRollbackSnapshotToLocalStorage(snapshot);
    this.preSegmentationGeometryBackup?.dispose();
    this.preSegmentationGeometryBackup = this.geometry.clone();
  }

  private trackSegmentedTooth(tooth: HalfEdgeMesh): void {
    if (!this.segmentedToothMeshes.includes(tooth)) {
      this.segmentedToothMeshes.push(tooth);
    }
  }

  private disposeSegmentedToothMesh(tooth: HalfEdgeMesh): void {
    this.rootState.scene.remove(tooth);
    this.remove(tooth);
    tooth.geometry.dispose();
    const mat = tooth.material;
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat?.dispose();
  }

  /** Removes segmented tooth meshes from the scene (e.g. when moving on to the other jaw). */
  public clearSegmentedTeethFromScene(): void {
    this.removeSceneObjectsByNamePrefix('debug-seg-tooth-border-');
    for (const tooth of this.segmentedToothMeshes) {
      this.disposeSegmentedToothMesh(tooth);
    }
    this.segmentedToothMeshes = [];
  }

  /** Dev layer: show/hide black spheres on segmented tooth border vertices. */
  public refreshToothBordersDebugLayer(): void {
    if (getDevRuntime().segmentation.sceneLayers.toothBorders) {
      this.visualizeAllToothBorderVertices(this.segmentedToothMeshes);
    } else {
      this.removeSceneObjectsByNamePrefix('debug-seg-tooth-border-');
    }
  }

  /** Restores pre-segmentation gum mesh + refined contours from rollback storage. */
  public revertSegmentation(scanId: string | null): void {
    this.clearSegmentedTeethFromScene();
    if (!this.preSegmentationGeometryBackup) {
      throw new Error('No pre-segmentation geometry backup');
    }
    this.setNewGeometry(this.preSegmentationGeometryBackup.clone());

    const snapshot = loadRollbackSnapshotFromLocalStorage(scanId, this.getJawKind());
    if (!snapshot) {
      throw new Error('No rollback snapshot in local storage');
    }
    this.importDevSnapshot(snapshot);
    this.setBoundaryCurvesInteraction(true);
    this.isAnchorInsertionEnabled = false;
  }

  public raycastHit(intersection: Intersection<Object3D>) {
      if (!this.isAnchorInsertionEnabled) {
        return;
      }
      if (!intersection.face) {
        throw new Error('No face detected'); //TODO handle this error
      }
      const cameraWorldPosition = new Vector3();
      this.rootState.camera.getWorldPosition(cameraWorldPosition);
      const intersectVertex = this.halfEdges.renderIdToVertex.get(intersection.face.a);
      this.anchorCollection.addAnchor(intersection.point, intersectVertex!, cameraWorldPosition);
      const interactiveSpline = new InteractiveSpline([], this);
      interactiveSpline.addToScene();
      interactiveSpline.setCanInteract(false);
      this.toothBoundaryCurves.push(interactiveSpline);
      this.updateTeethSelection();
  }

  private updateTeethSelection() {
    if (this.anchorCollection.length === 0) return;
    const startStop = teethSelectionStore.getState().startStop;
    const endStop = teethSelectionStore.getState().endStop;
    this.findTooth(this.anchorCollection[this.anchorCollection.length - 1], this.anchorCollection.length - 1, startStop, endStop);
  }

 
  /**
     * Computes and updates the tooth boundary curve for a given anchor.
     *
     * Workflow:
     * 1) Grow a vertex region from the anchor's intersected vertex using BFS
     *    constrained by `startStop`/`endStop` and the current `teethEdges` stop set.
     * 2) Build a Convex Hull around the grown vertices, pad it slightly by scaling
     *    around its centroid to ensure robust overlap with the target tooth surface.
     * 3) Intersect the padded hull against this jaw mesh (BVH-accelerated) to collect
     *    edge intersection records.
     * 4) Map the intersections to an array of vertexIntersection and creates
     *    a new line representing the tooth boundary curve.
     *
     * @param anchor Anchor that defines the tooth seed; its `intersectVertex` is used as BFS start.
     * @param index Index of the boundary curve to update inside `this.toothBoundaryCurves`.
     * @param startStop Minimum region size before stopping expansion on stop vertices (warm-up distance).
     * @param endStop Hard cap of processed vertices for the BFS growth.
     * @throws {Error} If BFS cannot start because the start vertex lacks half-edges.
     * @see {@link JawSegmentation.findToothVerticesUsingBFS}
     * @see {@link HalfEdgeMesh.intersectMesh}
     * @see {@link InteractiveSpline.createLine}
  */
  private findTooth(anchor: MeshAnchor, index: number, startStop: number, endStop: number) {
    const tooth = this.findToothVerticesUsingBFS(anchor.intersectVertex, this.teethEdges, startStop, endStop);
    const convexGeometry = new ConvexGeometry(tooth.map((vertex) => vertex.position));
    const convexMesh = new Mesh(convexGeometry, new MeshBasicMaterial({ color: 0xff0000 }));
    const center = new Vector3();
    convexMesh.geometry.computeBoundingBox();
    convexMesh.geometry.boundingBox?.getCenter(center);
    convexMesh.geometry.translate(-center.x, -center.y, -center.z);
    convexMesh.geometry.scale(1.05, 1.05, 1.05);
    convexMesh.geometry.translate(center.x, center.y, center.z);
    convexMesh.geometry.computeBoundsTree();
    const segments = this.intersectMeshSegments(convexMesh);
    let orderedPoints = orderCutSegmentsIntoLoop(segments);
    if (orderedPoints.length < 2) {
      const intersections = this.intersectMesh(convexMesh);
      orderedPoints = intersections.map(({ halfEdge }) => halfEdge.vertex.position);
      this.toothBoundaryCurves[index].createLine(orderedPoints);
      return;
    }
    this.toothBoundaryCurves[index].createLine(orderedPoints, { preordered: true });
  }

  public finishAnchorInsertion() {
    this.isAnchorInsertionEnabled = false;
    this.anchorCollection.setArrowsVisible(false);
    this.setBoundaryCurvesInteraction(true);
    this.alignCloseBoundaryCurves(1);
  }

  public setBoundaryCurvesInteraction(enable: boolean) {
    this.toothBoundaryCurves.forEach((curve) => curve.setCanInteract(enable));
  }

  public alignCloseBoundaryCurves(threshold: number) {
    const alignedPoints = new Set<SplineControlPoint>();
    this.toothBoundaryCurves.forEach((curveA, indexA) => {
      const closeBoundaryLoops: InteractiveSpline[] = [];
      if (indexA < this.toothBoundaryCurves.length - 1) {
        closeBoundaryLoops.push(this.toothBoundaryCurves[indexA + 1]);
      }
      if (indexA > 0) {
        closeBoundaryLoops.push(this.toothBoundaryCurves[indexA - 1]);
      }
      curveA.linePoints.forEach((pointA) => {
        closeBoundaryLoops.forEach((curveB) => {
          curveB.linePoints.forEach((pointB) => {
            const distance = pointA.position.distanceTo(pointB.position);
            if (distance < threshold && !alignedPoints.has(pointA) && !alignedPoints.has(pointB)) {
              const midPoint = new Vector3().addVectors(pointA.position, pointB.position).multiplyScalar(0.5);
              pointA.position.copy(midPoint);
              pointB.position.copy(midPoint);
              alignedPoints.add(pointA);
              alignedPoints.add(pointB);
              pointA.setSnappedPoint(pointB);
              pointB.setSnappedPoint(pointA);
            }
          });
        });
      });
    });
    this.toothBoundaryCurves.forEach((curve) => curve.createLine());
  }

  /**
   * Finds and returns an array of vertex indices representing a tooth starting from a given vertex.
   * The search is performed using a breadth-first search (BFS) approach that stops when either border vertices
   * are reached or a specified number of vertices has been processed.
   *
   * @param start - The starting vertex index for the search.
   * @param stopVertices - A set of vertices that should be considered as stopping points.
   * @param startStop - The number of vertices to process before stopping.
   * @param endStop - The maximum number of vertices to process.
   * @returns An array of vertex indices representing the tooth.
   * @throws An error if the starting vertex or its adjacent vertices do not have halfEdges.
   */
  private findToothVerticesUsingBFS(start: Vertex, stopVertices: Set<Vertex>, startStop: number, endStop: number): Array<Vertex> {
    const startingHalfEdgeA = start.halfedge;
    const startingHalfEdgeB = startingHalfEdgeA?.next;
    const startingHalfEdgeC = startingHalfEdgeA?.prev;
    if (!startingHalfEdgeA || !startingHalfEdgeB || !startingHalfEdgeC) {
      throw new Error('One of the vertices does not have a halfEdge');
    }

    const queue = [startingHalfEdgeA, startingHalfEdgeB, startingHalfEdgeC];
    const visited = new Set<Halfedge>();
    const result = [startingHalfEdgeA.vertex, startingHalfEdgeB.vertex, startingHalfEdgeC.vertex];

    while (queue.length > 0 && result.length < endStop) {
      const actualHalfEdge = queue.shift()!;
      if ((result.length > startStop && stopVertices.has(actualHalfEdge.vertex)) || visited.has(actualHalfEdge)) {
        if (stopVertices.has(actualHalfEdge.vertex)) {
          result.push(actualHalfEdge.vertex);
        }
        continue;
      }

      const halfEdgeTwin = actualHalfEdge.twin;
      visited.add(actualHalfEdge);
      visited.add(halfEdgeTwin);
      result.push(actualHalfEdge.vertex);

      if (!visited.has(halfEdgeTwin.next)) {
        queue.push(halfEdgeTwin.next);
      }
      if (!visited.has(halfEdgeTwin.prev)) {
        queue.push(halfEdgeTwin.prev);
      }
    }
    return result;
  }

  /**
   * Segments the jaw mesh into teeth and gum.
   *
   * Using editableLine get points and get individual teeth
   *
   * The gum is computed by finding the connected components of the jaw mesh
   * that are not part of any tooth.
   *
   * The result is an object with two properties: `gum` and `teeth`.
   *
   * The `gum` property is an object with two properties: `indices` and `positions`.
   * The `indices` property is an array of indices of the gum mesh.
   * The `positions` property is an array of positions of the gum mesh.
   *
   * The `teeth` property is an array of objects with two properties: `indices` and `positions`.
   * The `indices` property is an array of indices of the tooth mesh.
   * The `positions` property is an array of positions of the tooth mesh.
   *
   * @returns The segmented jaw mesh.
   */
  public async segmentJaw(): Promise<{ data: JawSegmentationCreate; teethModels: Blob[]; gumModel: Blob }> {
    const devRuntime = getDevRuntime();
    const debugConfig = devRuntime.segmentation;
    this.papillaSearchDebug = devRuntime.isEnabled && debugConfig.logLevel !== 'off';
    if (devRuntime.isEnabled) {
      this.registerSegmentationDebugSceneCleaners();
    }
    if (devRuntime.isEnabled) {
      devRuntime.clearLogs();
      devRuntime.log({
        kind: 'log',
        tag: 'segmentJaw',
        message: 'run started',
        data: {
          stopAfter: debugConfig.stopAfter,
          logLevel: debugConfig.logLevel,
        },
      });
    }

    const teethData: ToothSegmentationCreate[] = [];
    const teethModels: Blob[] = [];
    const teethOldFaces: Face[] = [];
    const teethHalfEdges: HalfEdgeMesh[] = [];
    this.segmentedToothMeshes = [];

    const finalizeRun = async (reason: 'complete' | SegmentationDebugStep) => {
      if (devRuntime.isEnabled && reason !== 'complete') {
        devRuntime.log({
          kind: 'stop',
          tag: 'segmentJaw',
          message: `stopped after ${reason}`,
        });
      }
      this.updateGeometryFromHalfedges();
      teethModels.length = 0;
      for (const [index, tooth] of teethHalfEdges.entries()) {
        tooth.updateGeometryFromHalfedges();
        // The PLY MUST come from a fresh toBufferGeometry() build, never from
        // tooth.geometry: updateGeometryFromHalfedges runs computeBoundsTree,
        // and three-mesh-bvh REORDERS THE INDEX BUFFER IN PLACE (spatial
        // sort). A PLY written from the live geometry has BVH leaf order —
        // the generated faces scattered through it — and the single-integer
        // marker below then makes the correction export cut a random band
        // out of the crown instead of removing the wall.
        const toothBlob = await geometryToPly(tooth.toBufferGeometry());
        teethModels.push(toothBlob);
        // Where this tooth's generated wall starts, in the very face order
        // geometryToPly just wrote — this is what lets the export delete the
        // display-only interproximal geometry again.
        const from = tooth.getGeneratedFacesFrom();
        if (teethData[index]) teethData[index].generatedFacesFrom = from >= 0 ? from : null;
        this.trackSegmentedTooth(tooth);
        if (!this.rootState.scene.children.includes(tooth)) {
          this.add(tooth);
        }
      }
      const gumGeneratedFrom = this.getGeneratedFacesFrom();
      return {
        data: {
          teethSegmentation: teethData,
          generatedFacesFrom: gumGeneratedFrom >= 0 ? gumGeneratedFrom : null,
        },
        // Fresh build for the same reason as the teeth above.
        gumModel: await geometryToPly(this.toBufferGeometry()),
        teethModels,
      };
    };

    const shouldStopAfter = (step: SegmentationDebugStep) =>
      getDevRuntime().isEnabled && getDevRuntime().segmentation.stopAfter === step;
    const pauseAfter = async (step: SegmentationDebugStep) => {
      if (!shouldStopAfter(step)) return;
      this.updateGeometryFromHalfedges();
      for (let i = 0; i < teethHalfEdges.length; i++) {
        if (!this.rootState.scene.children.includes(teethHalfEdges[i])) {
          teethHalfEdges[i].name = `tooth-${i}`;
          this.rootState.scene.add(teethHalfEdges[i]);
          this.trackSegmentedTooth(teethHalfEdges[i]);
        }
      }
      await getDevRuntime().pauseAtSegmentationStep(step);
    };

    this.teethEdges.clear();

    this.segGroupStart('segmentJaw', JawSegmentation.SEG_STYLE.step, false);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.starting);

    // ===== STEP 1: Extract teeth (precise face-flood cut) =====
    this.segGroupStart('STEP 1 — extract teeth & smooth borders', JawSegmentation.SEG_STYLE.step);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.extractTeeth);
    for (let i = 0; i < this.toothBoundaryCurves.length; i++) {
      const tooth = this.extractTooth(i);
      teethData.push(tooth.toothData);
      teethOldFaces.push(...tooth.toothOldFaces);

      this.logFocusToothBorderHealth('step1-after-extract', tooth.halfEdgeMesh, i);

      // Smooth tooth border immediately after extraction
      await tooth.halfEdgeMesh.smoothBorderVertices();
      tooth.halfEdgeMesh.updateGeometryFromHalfedges();

      this.logFocusToothBorderHealth('step1-after-smooth', tooth.halfEdgeMesh, i);

      teethHalfEdges.push(tooth.halfEdgeMesh);
    }
    this.logBorderHealthCheckpoint('step1-extract-smooth', teethHalfEdges);
    this.segGroupEnd();
    if (shouldStopAfter(SegmentationDebugStep.ExtractTeeth)) {
      await pauseAfter(SegmentationDebugStep.ExtractTeeth);
    }

    // ===== STEP 1.5: Strip faces on each tooth that penetrate / sit inside the
    // adjacent tooth mesh (BVH distance). Corridor-only cuts rarely shrink the
    // tooth meshes themselves; this removes a thin band of the worst overlaps.
    const jawMeanEdge = Math.max(this.halfEdges.getMeanEdgeLength?.() ?? 0.05, 1e-6);
    const facesToStripByTooth = new Map<number, Set<Face>>();
    for (let i = 0; i < teethHalfEdges.length - 1; i++) {
      if (this.missingAfterAnchor.has(i)) continue;
      this.collectAdjacentTeethMutualProximityStripFaces(
        teethHalfEdges,
        i,
        i + 1,
        jawMeanEdge,
        facesToStripByTooth,
      );
    }

    let totalStripped = 0;
    for (let ti = 0; ti < teethHalfEdges.length; ti++) {
      const faces = facesToStripByTooth.get(ti);
      if (!faces?.size) continue;

      const beforeHealth = this.papillaSearchDebug
        ? this.computeToothBorderHealth(teethHalfEdges[ti], ti)
        : null;
      const { removedCount } = teethHalfEdges[ti].removeFaceSet(faces);
      totalStripped += removedCount;

      if (this.papillaSearchDebug && removedCount > 0) {
        const afterHealth = this.computeToothBorderHealth(teethHalfEdges[ti], ti);
        this.segLog(
          'strip',
          JawSegmentation.BORDER_HEALTH_STYLE,
          `tooth ${ti}: applied removeFaceSet (${removedCount} face(s))`,
          beforeHealth
            ? {
                borderDelta: {
                  loops: `${beforeHealth.loopCount} → ${afterHealth.loopCount}`,
                  mainLoop: `${beforeHealth.mainLoopSize} → ${afterHealth.mainLoopSize}`,
                  singletons: `${beforeHealth.singletonLoopCount} → ${afterHealth.singletonLoopCount}`,
                  isolated: `${beforeHealth.isolatedVertexCount} → ${afterHealth.isolatedVertexCount}`,
                  mismatches: `${beforeHealth.adjacencyMismatchCount} → ${afterHealth.adjacencyMismatchCount}`,
                },
                afterFlags: afterHealth.flags,
              }
            : undefined,
        );
      }
    }
    if (totalStripped > 0) {
      this.segLog('STEP 1.5', JawSegmentation.SEG_STYLE.step, `stripped ${totalStripped} tooth face(s) near adjacent surfaces`);
    }
    this.logBorderHealthCheckpoint('step1.5-strip-proximal', teethHalfEdges, {
      note: totalStripped > 0 ? `stripped ${totalStripped} face(s)` : 'no faces stripped',
    });

    // ===== STEP 1.6: Re-smooth open borders after strip (strip leaves jagged edges) =====
    if (totalStripped > 0) {
      await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.refineProximal);
      this.segLog('STEP 1.6', JawSegmentation.SEG_STYLE.step, 'smoothing interproximal strip borders');
      for (const toothMesh of teethHalfEdges) {
        await toothMesh.smoothBorderVertices({
          smoothingFactor: 0.38,
          iterations: 6,
        });
        toothMesh.updateGeometryFromHalfedges();
      }
      this.logBorderHealthCheckpoint('step1.6-resmooth-strip', teethHalfEdges);
    }

    if (shouldStopAfter(SegmentationDebugStep.StripProximal)) {
      await pauseAfter(SegmentationDebugStep.StripProximal);
    }

    // ===== STEP 2: Build gum (jaw \ teeth \ interdental corridors) =====
    this.segGroupStart('STEP 2 — gum mesh + corridors', JawSegmentation.SEG_STYLE.step);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.gumMesh);

    // Corridor removes gum between contacts; tooth stripping (1.5) handles
    // tooth-tooth overlap. Corridor kept moderate so we do not over-delete gum.
    const corridorSumMult = 5;
    const corridorPerCurveMult = 3;
    const corridorExpandRings = 0;
    const corridorSumThreshold = jawMeanEdge * corridorSumMult;
    const corridorPerCurveThreshold = jawMeanEdge * corridorPerCurveMult;

    const allToothFaces = new Set<Face>(teethOldFaces);
    const corridorFaces = new Set<Face>();
    this.pairsWithoutCorridor.clear();
    for (let i = 0; i < this.toothBoundaryCurves.length - 1; i++) {
      if (this.missingAfterAnchor.has(i)) continue;
      const pairCorridor = this.findCorridorFacesForPair(
        i,
        corridorSumThreshold,
        corridorPerCurveThreshold,
        allToothFaces,
      );
      if (pairCorridor.size === 0) {
        this.pairsWithoutCorridor.add(i);
        this.segWarn(
          'STEP 2',
          `gap [${i}-${i + 1}] has no interdental corridor — teeth far apart, ` +
            `gum between them stays and the gap skips papilla anchors + gum bridge`,
        );
        continue;
      }
      for (const f of pairCorridor) corridorFaces.add(f);
    }
    const corridorCoreCount = corridorFaces.size;
    this.expandCorridorFacesIntoNonToothMesh(corridorFaces, allToothFaces, corridorExpandRings);
    const remainingGumFaces = this.collectRemainingGumFaces(teethOldFaces, corridorFaces);
    const { removed: islandFaces, componentCount } = this.pruneSmallGumIslands(remainingGumFaces);
    for (const f of islandFaces) corridorFaces.add(f);

    this.segLog(
      'STEP 2',
      JawSegmentation.SEG_STYLE.step,
      `corridor ${corridorCoreCount} face(s) removed; ` +
        `islands pruned ${islandFaces.size} face(s) (${componentCount} gum component(s))`,
    );

    const gumGeom = this.segmentGum(teethOldFaces, corridorFaces);
    this.setNewGeometry(gumGeom);

    this.toothBoundaryCurves.forEach((line) => {
      line.dispose();
    });
    this.toothBoundaryCurves = [];

    this.segGroupEnd();
    if (shouldStopAfter(SegmentationDebugStep.GumMesh)) {
      await pauseAfter(SegmentationDebugStep.GumMesh);
    }

    const meanEdge = Math.max(this.halfEdges.getMeanEdgeLength?.() ?? 0.05, 1e-6);

    // ===== STEP 4: Smooth gum boundary =====
    this.segGroupStart('STEP 4 — smooth gum boundary', JawSegmentation.SEG_STYLE.step);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.smoothGum);
    this.smoothGumBoundaryVertices(teethHalfEdges.length);
    this.updateGeometryFromHalfedges();
    this.logBorderHealthCheckpoint('step4-smooth-gum-boundary', teethHalfEdges);
    this.segGroupEnd();
    if (shouldStopAfter(SegmentationDebugStep.SmoothGumBoundary)) {
      await pauseAfter(SegmentationDebugStep.SmoothGumBoundary);
    }

    // ===== STEP 5: Snap gum to teeth with ownership tracking (BEFORE filling) =====
    this.segGroupStart('STEP 5 — snap gum to teeth', JawSegmentation.SEG_STYLE.step);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.snapGum);
    const gumToSnap = this.snapGumToTeethWithOwnership(teethHalfEdges, 2 * meanEdge);
    if (debugConfig.sceneLayers.snappedGum) {
      this.visualizeAllSnappedGumVertices(gumToSnap);
    }
    // Equalize the rims: after this, wherever gum and tooth meet, both border
    // loops share the identical vertex sequence — the export fusion welds the
    // seam exactly instead of splitting T-junctions into sliver triangles.
    const rimStats = equalizeSeamRims(this, teethHalfEdges, gumToSnap);
    this.segLog(
      'snap',
      JawSegmentation.SEG_STYLE.step,
      `rim equalization: ${rimStats.exactPairs} exact pairs | gum slid ${rimStats.gumSlid}, ` +
        `collapsed ${rimStats.gumCollapsed}, stuck ${rimStats.gumStuck} | tooth collapsed ` +
        `${rimStats.toothCollapsed}, stuck ${rimStats.toothStuck} | runs kept ${rimStats.runsKept} | ` +
        `slivers welded ${rimStats.sliversWelded}, merged ${rimStats.sliversCollapsed}`,
    );
    this.updateGeometryFromHalfedges();
    teethHalfEdges.forEach((tooth) => tooth.updateGeometryFromHalfedges());
    const snappedBorderCounts = teethHalfEdges.map((_, i) =>
      this.buildSnappedToothBorderVertexSet(gumToSnap, i).size,
    );
    this.logBorderHealthCheckpoint('step5-snap', teethHalfEdges, { snappedBorderCounts });
    this.segGroupEnd();
    if (shouldStopAfter(SegmentationDebugStep.SnapGum)) {
      await pauseAfter(SegmentationDebugStep.SnapGum);
    }

    // ===== STEP 5.5: Find interdental anchors between adjacent teeth =====
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.papilla);
    this.logBorderHealthCheckpoint('step5.5-papilla', teethHalfEdges, { snappedBorderCounts });
    const interdentalAnchors = this.findAllInterdentalAnchors(teethHalfEdges, gumToSnap);
    if (shouldStopAfter(SegmentationDebugStep.PapillaAnchors)) {
      await pauseAfter(SegmentationDebugStep.PapillaAnchors);
    }

    this.segGroupStart('STEP 6 — fill tooth sides', JawSegmentation.SEG_STYLE.fill);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.fillTeeth);
    this.resetGeneratedFaces(teethHalfEdges);
    for (let i = 0; i < teethHalfEdges.length; i++) {
      this.segGroupStart(`tooth ${i}`, JawSegmentation.SEG_STYLE.fill);
      await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.fillTooth(i, teethHalfEdges.length));
      await this.fillToothSidesWithAnchors(teethHalfEdges[i], i, interdentalAnchors, teethHalfEdges);
      this.segGroupEnd();
      if (!this.rootState.scene.children.includes(teethHalfEdges[i])) {
        teethHalfEdges[i].name = `tooth-${i}`;
        this.rootState.scene.add(teethHalfEdges[i]);
        this.trackSegmentedTooth(teethHalfEdges[i]);
      }
    }
    this.updateGeometryFromHalfedges();
    this.anchorCollection.removeAll();
    this.segGroupEnd();
    if (this.wallFillFixtures.length > 0) {
      const jaw = this.IsUpper ? 'upper' : 'lower';
      this.segLog('fill', JawSegmentation.SEG_STYLE.fill, `dumping ${this.wallFillFixtures.length} wall fixtures`);
      downloadWallFillFixtures(this.wallFillFixtures, `wall-fixtures-${jaw}.json`);
      this.wallFillFixtures = [];
    }
    if (shouldStopAfter(SegmentationDebugStep.FillTeeth)) {
      await pauseAfter(SegmentationDebugStep.FillTeeth);
    }

    this.segGroupStart('STEP 7 — interdental gum bridges', JawSegmentation.SEG_STYLE.step);
    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.gumBridges);
    // Fresh BVHs WITH the walls: the membrane tests its chords and relaxed
    // vertices against the teeth as they now stand, not the bare crowns.
    teethHalfEdges.forEach((tooth) => tooth.geometry.computeBoundsTree());
    for (let i = 0; i < teethHalfEdges.length - 1; i++) {
      if (!this.isInterdentalGapActive(i, teethHalfEdges.length)) continue;
      const anchors = interdentalAnchors.get(i);
      if (!anchors) continue;
      const bridgeSides: InterdentalBoundarySides = {
        vestibular: {
          startVertex: anchors.vestibular.gumAVertex,
          endVertex: anchors.vestibular.gumBVertex,
          vertices: [anchors.vestibular.gumAVertex, anchors.vestibular.gumBVertex],
        },
        lingual: {
          startVertex: anchors.lingual.gumAVertex,
          endVertex: anchors.lingual.gumBVertex,
          vertices: [anchors.lingual.gumAVertex, anchors.lingual.gumBVertex],
        },
      };
      this.createInterdentalGumBridge(
        bridgeSides,
        teethHalfEdges[i],
        teethHalfEdges[i + 1],
        i,
        i + 1,
      );
    }
    this.segGroupEnd();
    this.segGroupEnd();
    if (shouldStopAfter(SegmentationDebugStep.GumBridges)) {
      await pauseAfter(SegmentationDebugStep.GumBridges);
    }

    await this.reportProcessingPhase(SEGMENTATION_PROCESSING_PHASE.exporting);
    if (debugConfig.sceneLayers.toothBorders) {
      this.visualizeAllToothBorderVertices(teethHalfEdges);
    }
    return finalizeRun('complete');
  }
    

  /**
   * Builds an ordered loop of mesh vertices that traces the tooth boundary
   * curve as tightly as possible. Each consecutive pair in the returned array
   * is connected by a mesh edge, and the loop is implicitly closed (last
   * vertex connects back to the first).
   */
  private buildOrderedBoundaryPath(toothIndex: number): Vertex[] {
    const curve = this.toothBoundaryCurves[toothIndex]?.getCurve();
    if (!curve) return [];

    // Snap each curve sample to the nearest mesh vertex, dropping consecutive duplicates.
    const seedVertices: Vertex[] = [];
    for (const samplePoint of curve.getPoints(500)) {
      const v = this.getClosestVertexToPoint(samplePoint);
      if (!v) continue;
      if (seedVertices.length === 0 || seedVertices[seedVertices.length - 1] !== v) {
        seedVertices.push(v);
      }
    }
    if (seedVertices.length < 2) return seedVertices;

    const ordered: Vertex[] = [];
    const pushSegment = (segment: Vertex[] | null) => {
      if (!segment) return;
      for (const v of segment) {
        if (ordered.length === 0 || ordered[ordered.length - 1] !== v) {
          ordered.push(v);
        }
      }
    };

    for (let i = 0; i < seedVertices.length - 1; i++) {
      pushSegment(aStar(seedVertices[i], seedVertices[i + 1]));
    }
    pushSegment(aStar(seedVertices[seedVertices.length - 1], seedVertices[0]));

    // Drop the duplicate vertex at the end of the closed loop.
    if (ordered.length > 1 && ordered[0] === ordered[ordered.length - 1]) {
      ordered.pop();
    }

    return ordered;
  }

  /**
   * From an ordered closed loop of mesh vertices, returns the set of halfedges
   * (both directions) that form the cut. These halfedges block face flood
   * fills so a tooth region cannot leak through its own boundary.
   */
  private buildBlockedHalfedgesFromPath(path: Vertex[]): Set<Halfedge> {
    const blocked = new Set<Halfedge>();
    if (path.length < 2) return blocked;

    for (let i = 0; i < path.length; i++) {
      const a = path[i];
      const b = path[(i + 1) % path.length];
      if (a === b) continue;
      const he = a.getHalfedgeToVertex(b);
      if (!he) continue;
      blocked.add(he);
      if (he.twin) blocked.add(he.twin);
    }

    return blocked;
  }

  /**
   * Flood-fills mesh faces starting from any face incident to the anchor
   * vertex. Traversal cannot cross any halfedge in `blocked`, so the result
   * is exactly the connected face component on the anchor's side of the cut.
   */
  private floodFillFacesFromAnchor(
    anchorVertex: Vertex,
    blocked: Set<Halfedge>,
    maxFaces: number,
  ): Set<Face> {
    const visited = new Set<Face>();

    let startFace: Face | null = null;
    for (const he of anchorVertex.loopCW()) {
      if (he.face) {
        startFace = he.face;
        break;
      }
    }
    if (!startFace) return visited;

    const queue: Face[] = [startFace];
    visited.add(startFace);

    while (queue.length > 0 && visited.size < maxFaces) {
      const face = queue.shift()!;
      for (const he of face.halfedge.nextLoop()) {
        if (blocked.has(he)) continue;
        const twin = he.twin;
        if (!twin) continue;
        const nbr = twin.face;
        if (!nbr || visited.has(nbr)) continue;
        // Defensive: also skip if the twin halfedge itself is blocked.
        if (blocked.has(twin)) continue;
        visited.add(nbr);
        queue.push(nbr);
      }
    }

    return visited;
  }

  /**
   * Extracts a single tooth from the jaw model WITHOUT filling it.
   *
   * This uses face-level flood fill bounded by the discrete cut formed by the
   * tooth boundary curve. Unlike vertex-level BFS with a stop set, the cut
   * follows the curve as tightly as the mesh edges allow and cannot leak
   * across because traversal is blocked at the boundary halfedges
   * themselves.
   *
   * @param {number} toothIndex - The index of the tooth to be extracted.
   * @returns The extracted tooth data including unfilled HalfEdgeMesh and
   *   the set of original-jaw faces that were assigned to this tooth.
   */
  private extractTooth(toothIndex: number): {
    toothData: ToothSegmentationCreate;
    toothOldFaces: Array<Face>;
    toothFaces: Set<Face>;
    halfEdgeMesh: HalfEdgeMesh;
  } {
    const toothCenter = this.anchorCollection[toothIndex];

    const orderedPath = this.buildOrderedBoundaryPath(toothIndex);
    const blockedHalfedges = this.buildBlockedHalfedgesFromPath(orderedPath);

    // Track boundary path vertices so downstream code that uses teethEdges
    // (e.g. findTooth) keeps working.
    for (const v of orderedPath) {
      this.teethEdges.add(v);
    }

    const toothFaces = this.floodFillFacesFromAnchor(
      toothCenter.intersectVertex,
      blockedHalfedges,
      100000,
    );

    // Build sub-geometry from the face set. We use excludedBufferGeometry
    // because it is face-driven (an outside face never leaks in just because
    // its three vertices happen to lie on the boundary path).
    const excludedFaces = new Set<Face>();
    for (const f of this.halfEdges.faces) {
      if (!toothFaces.has(f)) excludedFaces.add(f);
    }
    const subGeometry = this.halfEdges.excludedBufferGeometry(excludedFaces);

    const cameraPosition = toothCenter.cameraPosition;
    const toothPosition = toothCenter.position;

    const unfilledMesh = new HalfEdgeMesh(subGeometry.newGeometry, false);
    unfilledMesh.name = `tooth-${toothIndex}-unfilled`;

    return {
      toothData: {
        fdi: toothIndex,
        cameraPosition: cameraPosition.toArray(),
        center: toothPosition.toArray(),
      },
      toothOldFaces: subGeometry.oldFaces,
      toothFaces,
      halfEdgeMesh: unfilledMesh,
    };
  }

    /**
   * Fills tooth sides using anchor-based approach.
   *
   * Middle teeth: walk the border from vestLeft until lingLeft (without
   * crossing vestRight) and fill that arc; same for vestRight → lingRight.
   * Edge teeth: walk the shorter border from vest to ling and fill that arc.
   *
   * The arcs are captured from the original border before any fill mutates
   * topology, then filled directly with `fillArcWithFanTriangulation`.
   */
  private async fillToothSidesWithAnchors(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    interdentalAnchors: Map<number, InterdentalAnchors>,
    teethHalfEdges: HalfEdgeMesh[] = [],
  ): Promise<void> {
    // The chord-chain guide asks both crowns for closest surface points; sync
    // this tooth's geometry buffer + BVH to the DS BEFORE any wall exists, so
    // its own side of the medial is the bare crown for both arcs.
    toothMesh.updateGeometryFromHalfedges();
    toothMesh.geometry.computeBoundsTree();
    // Snapshot of the pre-fill border: after the fills, the boundary direction
    // leaving an anchor OFF this set is the wall's bottom chain.
    toothMesh.calculateBorderVertices();
    const preFillBorder = new Set(toothMesh.getBorderVertices());
    // Get anchors from left (i-1) and right (i) interdental gaps
    const leftAnchors = interdentalAnchors.get(toothIndex - 1);
    const rightAnchors = interdentalAnchors.get(toothIndex);

    // Determine anchor vertices on this tooth's border
    let vestLeftAnchor: Vertex | null = null;
    let vestRightAnchor: Vertex | null = null;
    let lingLeftAnchor: Vertex | null = null;
    let lingRightAnchor: Vertex | null = null;

    if (leftAnchors) {
      vestLeftAnchor = leftAnchors.vestibular.toothBVertex;
      lingLeftAnchor = leftAnchors.lingual.toothBVertex;
    }
    if (rightAnchors) {
      vestRightAnchor = rightAnchors.vestibular.toothAVertex;
      lingRightAnchor = rightAnchors.lingual.toothAVertex;
    }

    this.segTable(`fill · tooth ${toothIndex} · anchors`, [
      { role: 'vestLeft', id: vestLeftAnchor?.id ?? '—' },
      { role: 'vestRight', id: vestRightAnchor?.id ?? '—' },
      { role: 'lingLeft', id: lingLeftAnchor?.id ?? '—' },
      { role: 'lingRight', id: lingRightAnchor?.id ?? '—' },
    ]);
    this.traceToothFillBorderLoops(toothMesh, toothIndex, 'before-fill-tooth');

    // Close a single arc with concentric rings.
    //
    // This wall is DISPLAY GEOMETRY, nothing more: the export deletes it and
    // bridges the two neighbouring crowns directly, once they sit in their
    // final posed positions. Building it for the scan positions and then
    // trying to make it survive treatment movement was the whole source of
    // the interproximal trouble — walls crossing the gum, membranes spanning
    // gaps, chains hanging in space. So the segmentation wall only has to
    // close the opening and look plausible, and the simplest construction
    // that does that is the right one.
    const fillArc = (arc: Vertex[], side: string): boolean => {
      if (arc.length < 3) {
        this.segLog('fill', JawSegmentation.SEG_STYLE.fill, `tooth ${toothIndex} · ${side} arc too short (${arc.length})`);
        return false;
      }

      const orientedArc = this.orientArcAlongFreeBoundary(arc, `${toothIndex}/${side}`);

      this.segLog('fill', JawSegmentation.SEG_STYLE.fill, `tooth ${toothIndex} · filling ${side} arc (${orientedArc.length} verts)`);
      this.traceToothFillBorderLoops(toothMesh, toothIndex, `before-${side}-arc`, orientedArc);
      if (getDevRuntime().segmentation.sceneLayers.fillArcs) {
        this.visualizeFillingArc(toothMesh, toothIndex, side === 'left' ? 0 : 1, orientedArc);
      }

      const firstFace = toothMesh.halfEdges.faces.length;
      const filled = fillArcWithConcentricRings(toothMesh, orientedArc, 3);
      toothMesh.markGeneratedFacesFrom(firstFace);
      if (!filled) {
        console.warn(`fillToothSidesWithAnchors [${toothIndex}]: ${side} ring fill produced no faces`);
      }
      this.traceToothFillBorderLoops(toothMesh, toothIndex, `after-${side}-arc`, orientedArc);
      return filled;
    };

    // Check if this is an edge tooth (only one side has anchors)
    const isLeftEdge = !leftAnchors && rightAnchors;
    const isRightEdge = leftAnchors && !rightAnchors;

    // All arcs are located on the ORIGINAL border, before any fill mutates
    // topology — that is also the state a fixture must capture.
    const pending: Array<{ side: string; arc: Vertex[] }> = [];

    if (isLeftEdge || isRightEdge) {
      const vestAnchor = isLeftEdge ? vestRightAnchor : vestLeftAnchor;
      const lingAnchor = isLeftEdge ? lingRightAnchor : lingLeftAnchor;

      this.segLog('fill', JawSegmentation.SEG_STYLE.fill, `tooth ${toothIndex} · edge (${isLeftEdge ? 'left' : 'right'})`);

      if (vestAnchor && lingAnchor) {
        // For edge teeth, walk directly from vest to ling (no other vest anchor to avoid)
        const arc = this.walkBoundaryToTarget(vestAnchor, lingAnchor);
        if (arc) {
          this.segLog('fill', JawSegmentation.SEG_STYLE.fill, `tooth ${toothIndex} · edge arc ${arc.length} verts`);
          pending.push({ side: 'edge', arc });
        } else {
          console.warn(`fillToothSidesWithAnchors [${toothIndex}]: could not find edge arc from vest ${vestAnchor.id} to ling ${lingAnchor.id}`);
        }
      }
    } else {
      const leftArc =
        vestLeftAnchor && lingLeftAnchor
          ? this.findArcBetweenAnchorsAvoiding(
              vestLeftAnchor,
              lingLeftAnchor,
              [vestRightAnchor, lingRightAnchor],
              `tooth ${toothIndex} · left`,
            )
          : null;

      const rightArc =
        vestRightAnchor && lingRightAnchor
          ? this.findArcBetweenAnchorsAvoiding(
              vestRightAnchor,
              lingRightAnchor,
              [vestLeftAnchor, lingLeftAnchor],
              `tooth ${toothIndex} · right`,
            )
          : null;

      if (leftArc) pending.push({ side: 'left', arc: leftArc });
      if (rightArc) pending.push({ side: 'right', arc: rightArc });
    }

    if (getDevRuntime().segmentation.sceneLayers.dumpWallFixtures && pending.length > 0) {
      this.wallFillFixtures.push(
        captureWallFillFixture(toothMesh, this.IsUpper ? 'upper' : 'lower', toothIndex, pending),
      );
    }

    for (const { side, arc } of pending) {
      fillArc(arc, side);
    }
    this.segLog(
      'fill',
      JawSegmentation.SEG_STYLE.fill,
      `tooth ${toothIndex} · ${toothMesh.generatedFaces.size} generated wall faces ` +
        `(from index ${toothMesh.getGeneratedFacesFrom()} of ${toothMesh.halfEdges.faces.length})`,
    );

    toothMesh.updateGeometryFromHalfedges();
    this.traceToothFillBorderLoops(toothMesh, toothIndex, 'after-update-geometry');
    toothMesh.material = new MeshStandardMaterial({
      vertexColors: true,
      side: DoubleSide,
      roughness: 0.15,
    });
  }


  /**
   * Debug visualization: draws a purple line for the filling arc (closed loop).
   */
  private visualizeFillingArc(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    arcIndex: number,
    closedLoop: Vertex[],
  ): void {
    if (closedLoop.length < 2) return;
    
    // Create positions array for the line (include closing segment back to start)
    const positions = new Float32Array((closedLoop.length + 1) * 3);
    
    closedLoop.forEach((v, i) => {
      const worldPos = toothMesh.localToWorld(v.position.clone());
      positions[i * 3] = worldPos.x;
      positions[i * 3 + 1] = worldPos.y;
      positions[i * 3 + 2] = worldPos.z;
    });
    
    // Close the loop - add first vertex at the end
    const firstWorldPos = toothMesh.localToWorld(closedLoop[0].position.clone());
    positions[closedLoop.length * 3] = firstWorldPos.x;
    positions[closedLoop.length * 3 + 1] = firstWorldPos.y;
    positions[closedLoop.length * 3 + 2] = firstWorldPos.z;
    
    const lineGeom = new BufferGeometry();
    lineGeom.setAttribute('position', new BufferAttribute(positions, 3));
    
    // Purple color for filling arcs
    const lineMat = new LineBasicMaterial({ color: 0x8800ff, linewidth: 3 });
    const line = new Line(lineGeom, lineMat);
    line.name = `debug-tooth${toothIndex}-filling-arc${arcIndex}`;
    this.rootState.scene.add(line);
    
    // Small balls at start and end
    const ballRadius = 0.3;
    
    // Start ball (green)
    const startGeom = new SphereGeometry(ballRadius, 8, 8);
    const startMat = new MeshBasicMaterial({ color: 0x00ff00 });
    const startBall = new Mesh(startGeom, startMat);
    startBall.position.copy(firstWorldPos);
    startBall.name = `debug-tooth${toothIndex}-arc${arcIndex}-start`;
    this.rootState.scene.add(startBall);
    
    // End ball (red)
    const endPos = toothMesh.localToWorld(closedLoop[closedLoop.length - 1].position.clone());
    const endGeom = new SphereGeometry(ballRadius, 8, 8);
    const endMat = new MeshBasicMaterial({ color: 0xff0000 });
    const endBall = new Mesh(endGeom, endMat);
    endBall.position.copy(endPos);
    endBall.name = `debug-tooth${toothIndex}-arc${arcIndex}-end`;
    this.rootState.scene.add(endBall);
    
    console.log(`visualizeFillingArc [${toothIndex}]: arc ${arcIndex} with ${closedLoop.length} vertices`);
  }




  /**
   * Orients an arc so that `arc[i] → arc[i+1]` matches the free border half-edge
   * direction. Without this, `createFace(outer0, outer1, inner1)` may create a
   * parallel edge (because the existing boundary half-edge goes the other way),
   * leaving the original border arc still on the boundary inside the new fill.
   */
  private orientArcAlongFreeBoundary(arc: Vertex[], debugTag: string): Vertex[] {
    if (arc.length < 2) return arc;

    let forwardFree = 0;
    let backwardFree = 0;
    let forwardBlocked = 0;
    let backwardBlocked = 0;

    for (let i = 0; i < arc.length - 1; i++) {
      const a = arc[i];
      const b = arc[i + 1];
      const ab = a.getHalfedgeToVertex(b);
      const ba = b.getHalfedgeToVertex(a);
      if (ab && ab.face === null) forwardFree++;
      else if (ab) forwardBlocked++;
      if (ba && ba.face === null) backwardFree++;
      else if (ba) backwardBlocked++;
    }

    const reverse = backwardFree > forwardFree;
    if (this.papillaSearchDebug) {
      this.segLog(
        'fill-orient',
        JawSegmentation.SEG_STYLE.fill,
        `arc ${debugTag} · fwdFree=${forwardFree} backFree=${backwardFree} fwdBlocked=${forwardBlocked} backBlocked=${backwardBlocked} reverse=${reverse}`,
      );
    }
    return reverse ? [...arc].reverse() : arc;
  }

  /** Adds an edge between `a` and `b` if it does not already exist. */
  private ensureEdgeBetween(mesh: HalfEdgeMesh, a: Vertex, b: Vertex, debugTag: string): void {
    try {
      for (const he of a.loopCW()) {
        if (he.twin.vertex === b) return;
      }
      mesh.halfEdges.addEdge(a, b);
      console.log(`ensureEdgeBetween [${debugTag}]: created edge ${a.id} ↔ ${b.id}`);
    } catch (e) {
      console.warn(`ensureEdgeBetween [${debugTag}]: failed to create edge ${a.id} ↔ ${b.id}`, e);
    }
  }


  /**
   * Closes an open boundary arc by creating 2 closing faces at the endpoints.
   * Creates triangles: (end -> startNext -> start) and (start -> endPrev -> end)
   * This effectively makes start and end interior vertices.
   * 
   * @param mesh The mesh containing the arc
   * @param arc The open arc vertices [start, v1, v2, ..., vN, end]
   * @returns The inner vertices [v1, v2, ..., vN] which now form a closed loop, or null if closing failed
   */
  /**
   * Segments the gum portion of the jaw model.
   *
   * Builds the gum geometry by excluding the union of all tooth faces and any
   * additional interdental corridor faces. Anything still present in the
   * resulting geometry is by construction part of the gum, so there are no
   * straddle triangles connecting two teeth.
   *
   * @param teethOldFaces Faces assigned to teeth on the original jaw mesh.
   * @param corridorFaces Faces in interdental corridors that should be
   *   removed (the thin bridge of triangles connecting two adjacent teeth).
   * @returns {BufferGeometry} - The geometry representing the gum of the jaw.
   */
  private segmentGum(teethOldFaces: Array<Face>, corridorFaces: Set<Face> = new Set()): BufferGeometry {
    const excluded = new Set<Face>(teethOldFaces);
    for (const f of corridorFaces) {
      excluded.add(f);
    }
    return this.halfEdges.excludedBufferGeometry(excluded).newGeometry;
  }

  /**
   * For a pair of adjacent teeth `(pairIndex, pairIndex + 1)`, returns the
   * set of mesh faces that lie in the interdental corridor — i.e. faces
   * close to BOTH boundary curves at once. These are the connecting bridge
   * of triangles that originally tied the two teeth together on the jaw.
   *
   * A face qualifies when its centroid is within `perCurveThreshold` of each
   * curve AND the sum of those distances is below `sumThreshold`. The sum
   * test makes the corridor a tubular region between the curves; the per-
   * curve test rejects faces that happen to sit close to one curve only.
   *
   * When several disconnected candidate patches qualify, only the connected
   * component nearest the interdental contact midpoint is kept (largest on tie).
   *
   * Faces that already belong to a tooth are not included.
   */
  private findCorridorFacesForPair(
    pairIndex: number,
    sumThreshold: number,
    perCurveThreshold: number,
    excluded: Set<Face>,
  ): Set<Face> {
    const candidates = new Set<Face>();
    const curveA = this.toothBoundaryCurves[pairIndex]?.getCurve();
    const curveB = this.toothBoundaryCurves[pairIndex + 1]?.getCurve();
    if (!curveA || !curveB) return candidates;

    const aPoints = curveA.getPoints(300);
    const bPoints = curveB.getPoints(300);
    if (aPoints.length === 0 || bPoints.length === 0) return candidates;

    const centroid = new Vector3();
    for (const face of this.halfEdges.faces) {
      if (excluded.has(face)) continue;

      const v0 = face.halfedge.vertex.position;
      const v1 = face.halfedge.next.vertex.position;
      const v2 = face.halfedge.prev.vertex.position;
      centroid.set(
        (v0.x + v1.x + v2.x) / 3,
        (v0.y + v1.y + v2.y) / 3,
        (v0.z + v1.z + v2.z) / 3,
      );

      let dA = Infinity;
      for (let i = 0; i < aPoints.length; i++) {
        const d = centroid.distanceTo(aPoints[i]);
        if (d < dA) dA = d;
      }
      if (dA > perCurveThreshold) continue;

      let dB = Infinity;
      for (let i = 0; i < bPoints.length; i++) {
        const d = centroid.distanceTo(bPoints[i]);
        if (d < dB) dB = d;
      }
      if (dB > perCurveThreshold) continue;

      if (dA + dB < sumThreshold) {
        candidates.add(face);
      }
    }

    if (candidates.size === 0) return candidates;

    const contactMidpoint = new Vector3();
    this.computeInterdentalContactMidpoint(aPoints, bPoints, contactMidpoint);
    return this.selectCorridorConnectedComponent(candidates, contactMidpoint);
  }

  /** Midpoint of the closest pair of samples between two tooth boundary curves. */
  private computeInterdentalContactMidpoint(
    aPoints: Vector3[],
    bPoints: Vector3[],
    out: Vector3,
  ): void {
    let minDistSq = Infinity;
    const closestA = new Vector3();
    const closestB = new Vector3();

    for (let i = 0; i < aPoints.length; i++) {
      const pa = aPoints[i];
      for (let j = 0; j < bPoints.length; j++) {
        const distSq = pa.distanceToSquared(bPoints[j]);
        if (distSq < minDistSq) {
          minDistSq = distSq;
          closestA.copy(pa);
          closestB.copy(bPoints[j]);
        }
      }
    }

    out.copy(closestA).add(closestB).multiplyScalar(0.5);
  }

  /** Gum faces left on the original jaw after teeth and corridor faces are excluded. */
  private collectRemainingGumFaces(
    teethOldFaces: Array<Face>,
    excludedFaces: Set<Face>,
  ): Set<Face> {
    const excluded = new Set<Face>(teethOldFaces);
    for (const face of excludedFaces) excluded.add(face);

    const remaining = new Set<Face>();
    for (const face of this.halfEdges.faces) {
      if (!excluded.has(face)) remaining.add(face);
    }
    return remaining;
  }

  /**
   * Face-adjacent connected components within `faces` (via twin halfedges).
   */
  private findFaceConnectedComponents(faces: Set<Face>): Face[][] {
    if (faces.size === 0) return [];

    const visited = new Set<Face>();
    const components: Face[][] = [];

    for (const seed of faces) {
      if (visited.has(seed)) continue;

      const component: Face[] = [];
      const queue: Face[] = [seed];
      visited.add(seed);

      while (queue.length > 0) {
        const face = queue.pop()!;
        component.push(face);

        for (const he of face.halfedge.nextLoop()) {
          const nbr = he.twin?.face;
          if (!nbr || !faces.has(nbr) || visited.has(nbr)) continue;
          visited.add(nbr);
          queue.push(nbr);
        }
      }

      components.push(component);
    }

    return components;
  }

  /**
   * Drops tiny disconnected gum islands while keeping the main arch component.
   * Interdental corridor holes remain open boundaries on the largest component.
   */
  private pruneSmallGumIslands(
    remainingFaces: Set<Face>,
    options: { minFaces?: number; minFraction?: number } = {},
  ): { kept: Set<Face>; removed: Set<Face>; componentCount: number } {
    const empty = { kept: remainingFaces, removed: new Set<Face>(), componentCount: 0 };
    if (remainingFaces.size === 0) return empty;

    const components = this.findFaceConnectedComponents(remainingFaces);
    if (components.length <= 1) {
      return { kept: remainingFaces, removed: new Set(), componentCount: components.length };
    }

    const total = remainingFaces.size;
    const minFaces = options.minFaces ?? 30;
    const minFraction = options.minFraction ?? 0.01;
    const minKeep = Math.max(minFaces, Math.floor(total * minFraction));

    const sorted = [...components].sort((a, b) => b.length - a.length);
    const largest = sorted[0]!;

    const kept = new Set<Face>();
    const removed = new Set<Face>();

    for (const component of sorted) {
      const target = component === largest || component.length >= minKeep ? kept : removed;
      for (const face of component) target.add(face);
    }

    return { kept, removed, componentCount: components.length };
  }

  /**
   * From corridor candidate faces, keep one face-adjacent connected component:
   * nearest to `contactMidpoint`, breaking ties by component size.
   */
  private selectCorridorConnectedComponent(
    candidates: Set<Face>,
    contactMidpoint: Vector3,
  ): Set<Face> {
    if (candidates.size <= 1) return new Set(candidates);

    const components = this.findFaceConnectedComponents(candidates);
    if (components.length === 1) return new Set(components[0]);

    const centroid = new Vector3();
    let bestComponent = components[0];
    let bestDistSq = Infinity;
    let bestSize = 0;

    for (const component of components) {
      let minDistSq = Infinity;
      for (const face of component) {
        const v0 = face.halfedge.vertex.position;
        const v1 = face.halfedge.next.vertex.position;
        const v2 = face.halfedge.prev.vertex.position;
        centroid.set(
          (v0.x + v1.x + v2.x) / 3,
          (v0.y + v1.y + v2.y) / 3,
          (v0.z + v1.z + v2.z) / 3,
        );
        const dSq = centroid.distanceToSquared(contactMidpoint);
        if (dSq < minDistSq) minDistSq = dSq;
      }

      const size = component.length;
      if (minDistSq < bestDistSq || (minDistSq === bestDistSq && size > bestSize)) {
        bestDistSq = minDistSq;
        bestSize = size;
        bestComponent = component;
      }
    }

    return new Set(bestComponent);
  }

  /**
   * Grows `corridor` by `numRings` layers of face-adjacent triangles that are
   * not part of any tooth. Pulls the cut slightly away from both contacts so
   * teeth do not share the same sliver of gum and reduces vertex penetration
   * without vertex motion or merges.
   */
  private expandCorridorFacesIntoNonToothMesh(
    corridor: Set<Face>,
    toothFaces: Set<Face>,
    numRings: number,
  ): void {
    if (numRings <= 0) return;

    for (let ring = 0; ring < numRings; ring++) {
      const frontier = Array.from(corridor);
      const toAdd: Face[] = [];
      for (const face of frontier) {
        for (const he of face.halfedge.nextLoop()) {
          const twin = he.twin;
          if (!twin?.face) continue;
          const nbr = twin.face;
          if (corridor.has(nbr) || toothFaces.has(nbr)) continue;
          toAdd.push(nbr);
        }
      }
      for (const f of toAdd) {
        if (!toothFaces.has(f)) corridor.add(f);
      }
    }
  }

  /** Max faces to strip per tooth per adjacent neighbor (avoids gutting a tooth). */
  private maxProximityStripFaceBudget(mesh: HalfEdgeMesh): number {
    const n = mesh.halfEdges.faces.length;
    return Math.min(800, Math.max(50, Math.floor(n * 0.2)));
  }

  /** Minimum distance from `worldPoint` to the polyline of boundary curve `curveIndex` (world space). */
  private minDistToBoundaryCurveWorld(curveIndex: number, worldPoint: Vector3, scratch: Vector3): number {
    const spline = this.toothBoundaryCurves[curveIndex];
    if (!spline) return Infinity;
    const curve = spline.getCurve();
    if (!curve) return Infinity;
    spline.updateWorldMatrix(true, false);
    const pts = curve.getPoints(100);
    let minD = Infinity;
    for (let i = 0; i < pts.length; i++) {
      scratch.copy(pts[i]).applyMatrix4(spline.matrixWorld);
      const d = scratch.distanceTo(worldPoint);
      if (d < minD) minD = d;
    }
    return minD;
  }

  /**
   * Collects faces on `stripper` whose centroids lie in the embrasure between
   * curves `curveIdxA`/`curveIdxB` and are within `maxDistWorld` of the
   * `neighbor` mesh surface. Does not modify the mesh — caller applies
   * `HalfEdgeMesh.removeFaceSet()` once per tooth.
   */
  private collectProximityStripFacesForTooth(
    stripper: HalfEdgeMesh,
    neighbor: HalfEdgeMesh,
    stripperToothIndex: number,
    neighborToothIndex: number,
    maxDistWorld: number,
    maxFacesToRemove: number,
    curveIdxA: number,
    curveIdxB: number,
    curveGateSum: number,
    facesToStripByTooth: Map<number, Set<Face>>,
  ): number {
    this.updateWorldMatrix(true, false);
    stripper.updateWorldMatrix(true, false);
    neighbor.updateWorldMatrix(true, false);

    const scratch = new Vector3();
    const candidates = stripper.collectFacesNearMeshSurface(neighbor, maxDistWorld, (_face, worldC) => {
      const dCurveA = this.minDistToBoundaryCurveWorld(curveIdxA, worldC, scratch);
      const dCurveB = this.minDistToBoundaryCurveWorld(curveIdxB, worldC, scratch);
      return dCurveA + dCurveB < curveGateSum;
    });

    const limit = Math.min(maxFacesToRemove, candidates.length);
    let collected = 0;
    const bucket = facesToStripByTooth.get(stripperToothIndex) ?? new Set<Face>();

    for (let k = 0; k < limit; k++) {
      const face = candidates[k].face;
      if (face.halfedge?.face === face && !bucket.has(face)) {
        bucket.add(face);
        collected++;
      }
    }

    if (collected > 0) {
      facesToStripByTooth.set(stripperToothIndex, bucket);
    }

    if (this.papillaSearchDebug && (collected > 0 || candidates.length > 0)) {
      const distances = candidates.slice(0, limit).map((hit) => hit.distance);
      const minDistance = distances.length ? Math.min(...distances) : null;
      const maxDistance = distances.length ? Math.max(...distances) : null;
      this.segLog(
        'strip',
        JawSegmentation.BORDER_HEALTH_STYLE,
        `tooth ${stripperToothIndex} vs ${neighborToothIndex}: candidates=${candidates.length}, collected=${collected}`,
        {
          maxDistWorld: +maxDistWorld.toFixed(5),
          curveGateSum: +curveGateSum.toFixed(5),
          maxFacesToRemove,
          totalQueuedForTooth: bucket.size,
          removedDistanceRange:
            minDistance === null || maxDistance === null
              ? null
              : { min: +minDistance.toFixed(5), max: +maxDistance.toFixed(5) },
        },
      );
    }

    return collected;
  }

  /**
   * Queues a thin overlap band from each of two adjacent tooth meshes for
   * later removal via `HalfEdgeMesh.removeFaceSet()`.
   */
  private collectAdjacentTeethMutualProximityStripFaces(
    teethHalfEdges: HalfEdgeMesh[],
    iLeft: number,
    iRight: number,
    jawMeanEdge: number,
    facesToStripByTooth: Map<number, Set<Face>>,
  ): void {
    const maxDistWorld = jawMeanEdge * 0.55;
    const curveGateSum = jawMeanEdge * 13;
    this.collectProximityStripFacesForTooth(
      teethHalfEdges[iLeft],
      teethHalfEdges[iRight],
      iLeft,
      iRight,
      maxDistWorld,
      this.maxProximityStripFaceBudget(teethHalfEdges[iLeft]),
      iLeft,
      iRight,
      curveGateSum,
      facesToStripByTooth,
    );
    this.collectProximityStripFacesForTooth(
      teethHalfEdges[iRight],
      teethHalfEdges[iLeft],
      iRight,
      iLeft,
      maxDistWorld,
      this.maxProximityStripFaceBudget(teethHalfEdges[iRight]),
      iLeft,
      iRight,
      curveGateSum,
      facesToStripByTooth,
    );
  }


  private getAlignedBoundaryVerticesForTooth(toothIndex: number): Array<Vertex> {
    const aligned = this.alignedBoundaryVerticesByTooth.get(toothIndex);
    if (!aligned) {
      return [];
    }
    return Array.from(aligned).filter((vertex) => this.gumBoundaryToTooth.get(vertex) === toothIndex);
  }

  /**
   * Smooths and simplifies gum boundary vertices to reduce zig-zag patterns.
   * Applies Laplacian smoothing and collapses very close vertices.
   */
  private smoothGumBoundaryVertices(numTeeth: number): void {
    this.calculateBorderVertices();
    const borderVertices = this.getBorderVertices();
    
    if (borderVertices.length === 0) return;

    const meanEdge = this.halfEdges.getMeanEdgeLength?.() ?? 0.1;
    const smoothingIterations = 3;
    const smoothingFactor = 0.3;

    // Apply Laplacian smoothing to each tooth's boundary
    for (let t = 0; t < numTeeth; t++) {
      const toothBoundary = this.getAlignedBoundaryVerticesForTooth(t);
      if (toothBoundary.length < 4) continue;

      // Multiple smoothing passes
      for (let iter = 0; iter < smoothingIterations; iter++) {
        for (const v of toothBoundary) {
          // Get boundary neighbors
          const neighbors = v.getAdjacenceBoundaryVertices().filter(n => toothBoundary.includes(n));
          if (neighbors.length < 2) continue;

          // Compute average position of neighbors
          const avgPos = new Vector3();
          for (const n of neighbors) {
            avgPos.add(n.position);
          }
          avgPos.divideScalar(neighbors.length);

          // Move towards average (Laplacian smoothing)
          v.position.lerp(avgPos, smoothingFactor);
        }
      }
    }

    // Collapse very close boundary vertices
    const collapseThreshold = meanEdge * 0.3;
    const collapseThresholdSq = collapseThreshold * collapseThreshold;
    
    let collapsed = 0;
    const maxCollapseIterations = 50;
    
    for (let iter = 0; iter < maxCollapseIterations; iter++) {
      this.calculateBorderVertices();
      const currentBorder = this.getBorderVertices();
      let foundCollapse = false;
      
      for (const v of currentBorder) {
        const neighbors = v.getAdjacenceBoundaryVertices();
        for (const n of neighbors) {
          if (v.position.distanceToSquared(n.position) < collapseThresholdSq) {
            // Check they belong to the same tooth
            const vTooth = this.gumBoundaryToTooth.get(v);
            const nTooth = this.gumBoundaryToTooth.get(n);
            if (vTooth === nTooth) {
              if (this.halfEdges.edgeCollapse(v, n, true)) {
                collapsed++;
                foundCollapse = true;
                break;
              }
            }
          }
        }
        if (foundCollapse) break;
      }
      
      if (!foundCollapse) break;
    }

    // Rebuild boundary sets after modifications
    this.calculateBorderVertices();
    this.rebuildAlignedBoundarySets();

    console.log(`smoothGumBoundaryVertices: smoothed and collapsed ${collapsed} vertices`);
  }

  /**
   * Snaps gum boundary vertices to closest tooth border and tracks ownership.
   * Collapses duplicates when multiple gum vertices would snap to the same position.
   * Returns a map of gumVertex -> SnapInfo (toothVertex, toothIndex, toothMesh).
   */
  private snapGumToTeethWithOwnership(
    teethHalfEdges: HalfEdgeMesh[],
    snapThreshold: number,
  ): Map<Vertex, SnapInfo> {
    this.calculateBorderVertices();
    const gumBorderVertices = this.getBorderVertices();
    
    const gumToSnap = new Map<Vertex, SnapInfo>();
    const positionKey = (p: Vector3) =>
      `${Math.round(p.x * 100)}:${Math.round(p.y * 100)}:${Math.round(p.z * 100)}`;

    const occupiedPositions = new Map<string, Vertex>();
    const toCollapse: Array<Vertex> = [];

    // Pre-calculate tooth border vertices, plus each tooth's border POLYLINE
    // in world space. A gum vertex sitting opposite the middle of a tooth
    // border edge has no tooth VERTEX within threshold when the two meshes
    // sample the seam at different densities — the exact case that used to
    // stay unsnapped and reach the export as an open slit. Projecting onto
    // the border segments catches it; the export's T-junction splitter then
    // welds a vertex that lies ON a crown border edge exactly.
    interface BorderSegment {
      a: Vector3; // world
      b: Vector3; // world
      aVertex: Vertex;
      bVertex: Vertex;
      toothIndex: number;
      toothMesh: HalfEdgeMesh;
    }
    const segments: BorderSegment[] = [];
    for (let ti = 0; ti < teethHalfEdges.length; ti++) {
      const tooth = teethHalfEdges[ti];
      tooth.calculateBorderVertices();
      for (const loop of tooth.getBorderLoops()) {
        for (let i = 0; i < loop.length; i++) {
          const aVertex = loop[i];
          const bVertex = loop[(i + 1) % loop.length];
          segments.push({
            a: tooth.localToWorld(aVertex.position.clone()),
            b: tooth.localToWorld(bVertex.position.clone()),
            aVertex,
            bVertex,
            toothIndex: ti,
            toothMesh: tooth,
          });
        }
      }
    }

    const segmentScratch = new Vector3();
    const segmentDelta = new Vector3();
    const closestOnSegment = (s: BorderSegment, p: Vector3, out: Vector3): number => {
      segmentDelta.subVectors(s.b, s.a);
      const lengthSq = segmentDelta.lengthSq();
      const t =
        lengthSq > 1e-20
          ? Math.min(1, Math.max(0, segmentScratch.subVectors(p, s.a).dot(segmentDelta) / lengthSq))
          : 0;
      out.copy(s.a).addScaledVector(segmentDelta, t);
      return t;
    };

    const projected = new Vector3();
    const projectedBest = new Vector3();
    let segmentSnaps = 0;
    for (const gumVertex of gumBorderVertices) {
      const gumPosWorld = this.localToWorld(gumVertex.position.clone());

      let closestToothVertex: Vertex | null = null;
      let closestDist = snapThreshold;
      let closestToothIndex = -1;
      let closestToothMesh: HalfEdgeMesh | null = null;

      for (let ti = 0; ti < teethHalfEdges.length; ti++) {
        const tooth = teethHalfEdges[ti];

        for (const toothVertex of tooth.getBorderVertices()) {
          const toothPosWorld = tooth.localToWorld(toothVertex.position.clone());
          const dist = gumPosWorld.distanceTo(toothPosWorld);
          if (dist < closestDist) {
            closestDist = dist;
            closestToothVertex = toothVertex;
            closestToothIndex = ti;
            closestToothMesh = tooth;
          }
        }
      }

      // Fallback: no tooth vertex in range — try the border polyline itself.
      let viaSegment = false;
      if (!closestToothVertex) {
        let bestDist = snapThreshold;
        for (const segment of segments) {
          const t = closestOnSegment(segment, gumPosWorld, projected);
          const dist = gumPosWorld.distanceTo(projected);
          if (dist < bestDist) {
            bestDist = dist;
            projectedBest.copy(projected);
            closestToothVertex = t < 0.5 ? segment.aVertex : segment.bVertex;
            closestToothIndex = segment.toothIndex;
            closestToothMesh = segment.toothMesh;
            viaSegment = true;
          }
        }
      }

      if (closestToothVertex && closestToothIndex >= 0 && closestToothMesh) {
        const targetPosWorld = viaSegment
          ? projectedBest.clone()
          : closestToothMesh.localToWorld(closestToothVertex.position.clone());
        const targetPosLocal = this.worldToLocal(targetPosWorld);
        const key = positionKey(targetPosLocal);

        if (occupiedPositions.has(key)) {
          // Another gum vertex already snapped here -> mark for collapse
          toCollapse.push(gumVertex);
        }
        else {
          // Snap position and mark as occupied
          gumVertex.position.copy(targetPosLocal);
          occupiedPositions.set(key, gumVertex);
          gumToSnap.set(gumVertex, {
            toothVertex: closestToothVertex,
            toothIndex: closestToothIndex,
            toothMesh: closestToothMesh,
          });
          if (viaSegment) segmentSnaps++;
        }
      }
    }
    if (segmentSnaps > 0) {
      console.log(
        `snapGumToTeethWithOwnership: ${segmentSnaps} gum vertex(ices) snapped onto border ` +
          `segments (no tooth vertex within threshold — sampling mismatch)`,
      );
    }
    
    // Post-processing: collapse unsnapped boundary neighbors of snapped vertices
    // This ensures ALL boundary vertices around teeth are snapped
    const snappedSet = new Set(gumToSnap.keys());
    
    // Keep collapsing until no unsnapped boundary neighbors exist
    let loopDetection = 0;
    while (toCollapse.length > 0) {
      loopDetection++;
      if (loopDetection > 1000) {
        console.warn(`snapGumToTeethWithOwnership: loop detected, stopping`);
        break;
      }
      const vertexToCollapse = toCollapse[0];
      console.log(`snapGumToTeethWithOwnership: vertexToCollapse: ${vertexToCollapse.id}`);
      if (!vertexToCollapse) continue;
      const boundaryNeighbors = vertexToCollapse.getAdjacenceBoundaryVertices();
      let wasCollapsed = false;
      for (const neighbor of boundaryNeighbors) {
        if (snappedSet.has(neighbor)) {
          this.halfEdges.edgeCollapse(vertexToCollapse, neighbor, false);
          console.log(`snapGumToTeethWithOwnership: collapsed ${vertexToCollapse.id} with ${neighbor.id}`);
          toCollapse.shift();
          wasCollapsed = true;
          break;
        } 
      }
      if (!wasCollapsed) {
        toCollapse.shift();
        toCollapse.push(vertexToCollapse);
      }
    }

    if (toCollapse.length > 0) {
      console.warn(
        `snapGumToTeethWithOwnership: ${toCollapse.length} gum vertex(ices) could not be collapsed`,
        toCollapse.map((v) => v.id),
      );
    }

    this.updateGeometryFromHalfedges();
    return gumToSnap;
  }




  

  /**
   * Rebuilds the per-tooth boundary vertex sets after modifications.
   */
  private rebuildAlignedBoundarySets(): void {
    const borderVertices = this.getBorderVertices();
    
    // Clear and rebuild
    for (const [toothIdx, vertexSet] of this.alignedBoundaryVerticesByTooth) {
      const remaining = new Set<Vertex>();
      for (const v of vertexSet) {
        if (borderVertices.includes(v)) {
          remaining.add(v);
        }
      }
      this.alignedBoundaryVerticesByTooth.set(toothIdx, remaining);
    }
  }




  /** Tooth border vertices that receive at least one gum snap for `toothIndex`. */
  private buildSnappedToothBorderVertexSet(gumToSnap: Map<Vertex, SnapInfo>, toothIndex: number): Set<Vertex> {
    const set = new Set<Vertex>();
    for (const info of gumToSnap.values()) {
      if (info.toothIndex === toothIndex) set.add(info.toothVertex);
    }
    return set;
  }

  private firstGumSnapOnToothBorder(
    gumToSnap: Map<Vertex, SnapInfo>,
    toothIndex: number,
    toothVertex: Vertex,
  ): { gumVertex: Vertex; toothVertex: Vertex } | null {
    for (const [gumV, info] of gumToSnap.entries()) {
      if (info.toothIndex !== toothIndex) continue;
      if (info.toothVertex === toothVertex || info.toothVertex.id === toothVertex.id) {
        return { gumVertex: gumV, toothVertex: info.toothVertex };
      }
    }
    return null;
  }

  /**
   * Border-loop neighbors of `v`, deduped by vertex id. On a clean half-edge
   * border loop a vertex always has exactly two boundary neighbors.
   */
  private getBorderNeighbors(v: Vertex): Vertex[] {
    const out: Vertex[] = [];
    const seenIds = new Set<number>();
    for (const n of v.getAdjacenceBoundaryVertices()) {
      if (seenIds.has(n.id)) continue;
      seenIds.add(n.id);
      out.push(n);
    }
    return out;
  }

  private logPapilla(toothIndex: number, message: string, detail?: Record<string, unknown>): void {
    this.segLog(`papilla · tooth ${toothIndex}`, JawSegmentation.SEG_STYLE.papilla, message, detail);
  }

  private logVestLing(toothIndex: number, message: string, detail?: Record<string, unknown>): void {
    this.segLog(`vest-ling · tooth ${toothIndex}`, JawSegmentation.SEG_STYLE.vestLing, message, detail);
  }

  /** Verbose marker/border-walk logs — requires explicit Focus tooth in dev panel. */
  private shouldLogPapillaVerbose(toothIndex: number): boolean {
    if (!this.papillaSearchDebug) return false;
    const focus = getDevRuntime().segmentation.focusToothIndex;
    return focus !== null && focus === toothIndex;
  }

  /** One-time legend for segmentation debug markers (console). */
  private logSegmentationDebugMarkerLegend(): void {
    if (!this.papillaSearchDebug) return;
    this.segGroupStart('debug marker legend', JawSegmentation.SEG_STYLE.papilla, false);
    console.log(
      [
        'black (small) — snapped gum boundary verts (layer: snappedGum)',
        'yellow line + cyan/magenta — unsnapped border strip polyline + first/last (layer: borderStrips or papilla)',
        'light green / light blue dots — papilla walk from strip midpoint (layer: papilla)',
        'bright green / blue — strip apex = midpoint of unsnapped run (layer: papilla)',
        'orange / dark orange — gum snap at strip ends (forward/backward anchor)',
        'white — loop predecessor expected snapped border vert at strip start',
        'yellow — loop successor expected snapped border vert at strip end',
        'purple line + large green/red — fill arc start/end (layer: fillArcs, step 6 only; NOT the papilla strip)',
      ].join('\n'),
    );
    this.segGroupEnd();
  }

  private debugColorLabel(hex: number): string {
    const known: Record<number, string> = {
      0x111111: 'black (snapped gum)',
      0x00ffff: 'cyan (strip-first)',
      0xff00aa: 'magenta (strip-last)',
      0xffffff: 'white (loop-pred)',
      0xffff00: 'yellow (loop-succ)',
      0x00ff44: 'bright-green (apex pass0)',
      0x3399ff: 'blue (apex pass1)',
      0x98fb98: 'light-green (walk pass0)',
      0x99ccff: 'light-blue (walk pass1)',
      0xff9900: 'orange (anchorF)',
      0xff6600: 'dark-orange (anchorB — often mistaken for red)',
      0x00ff00: 'green (fill-arc start, step 6)',
      0xff0000: 'red (fill-arc end, step 6)',
    };
    return known[hex] ?? `#${hex.toString(16).padStart(6, '0')}`;
  }

  private vertexWorldRecord(mesh: HalfEdgeMesh, v: Vertex): {
    id: number;
    x: number;
    y: number;
    z: number;
  } {
    const p = mesh.localToWorld(v.position.clone());
    return { id: v.id, x: +p.x.toFixed(4), y: +p.y.toFixed(4), z: +p.z.toFixed(4) };
  }

  /** Console trace when a debug sphere/line is added to the scene. */
  private logPlacedDebugMarker(
    toothIndex: number | null,
    kind: 'sphere' | 'line',
    name: string,
    colorHex: number,
    radius: number | null,
    world: Vector3,
    reason: string,
    extra?: Record<string, unknown>,
  ): void {
    if (!this.papillaSearchDebug) return;
    if (toothIndex !== null && !this.shouldLogPapillaVerbose(toothIndex)) return;
    console.log(
      `%c[debug-marker] ${name}`,
      JawSegmentation.SEG_STYLE.papilla,
      {
        kind,
        tooth: toothIndex,
        color: this.debugColorLabel(colorHex),
        colorHex: `0x${colorHex.toString(16)}`,
        radius,
        world: { x: +world.x.toFixed(4), y: +world.y.toFixed(4), z: +world.z.toFixed(4) },
        reason,
        ...extra,
      },
    );
  }

  private logPapillaSearchMarkers(
    toothMesh: HalfEdgeMesh,
    s: PapillaSearchResult,
    snappedIds: Set<number>,
  ): void {
    if (!this.papillaSearchDebug || !this.shouldLogPapillaVerbose(s.toothIndex)) return;

    const n = s.loop.length;
    const endLoopIdx = n > 0 ? (s.stripStartLoopIdx + s.strip.length - 1) % n : -1;
    const loopPredIdx = n > 0 ? (s.stripStartLoopIdx + n - 1) % n : -1;
    const loopSuccIdx = n > 0 ? (endLoopIdx + 1) % n : -1;
    const loopPred = loopPredIdx >= 0 ? s.loop[loopPredIdx] : null;
    const loopSucc = loopSuccIdx >= 0 ? s.loop[loopSuccIdx] : null;
    const w = (v: Vertex) => {
      const p = toothMesh.localToWorld(v.position.clone());
      return { id: v.id, x: +p.x.toFixed(3), y: +p.y.toFixed(3), z: +p.z.toFixed(3) };
    };

    this.segGroupStart(`markers · tooth ${s.toothIndex} · pass ${s.pass}`, JawSegmentation.SEG_STYLE.papilla);
    this.segTable('papilla search markers', [
      { role: 'strip-first (cyan)', vertex: w(s.strip[0]) },
      { role: 'strip-last (magenta)', vertex: w(s.strip[s.strip.length - 1]) },
      { role: 'apex (green/blue)', vertex: w(s.apex) },
      {
        role: 'loop-pred @ strip start (white)',
        vertex: loopPred ? w(loopPred) : null,
        snapped: loopPred ? snappedIds.has(loopPred.id) : null,
      },
      {
        role: 'loop-succ @ strip end (yellow)',
        vertex: loopSucc ? w(loopSucc) : null,
        snapped: loopSucc ? snappedIds.has(loopSucc.id) : null,
      },
      {
        role: 'snappedBefore (anchorF)',
        vertex: s.snappedBefore ? w(s.snappedBefore) : null,
        gumPaired: !!s.forwardAnchor,
      },
      {
        role: 'snappedAfter (anchorB)',
        vertex: s.snappedAfter ? w(s.snappedAfter) : null,
        gumPaired: !!s.backwardAnchor,
      },
    ]);
    console.log('strip vertex ids (unsnapped run)', s.strip.map((v) => v.id));
    this.segGroupEnd();
  }

  /**
   * When a wrap-around unsnapped arc crosses the loop array origin, a single rotation
   * from the first snapped→unsnapped transition can split it into head/tail strips
   * (e.g. [10,11] + [0,1]). Merge pairs whose end is loop-adjacent to the other's start.
   */
  private mergeWrapSplitBorderStrips(
    loopStrips: BorderStripCandidate[],
    loop: Vertex[],
  ): BorderStripCandidate[] {
    const n = loop.length;
    if (n === 0 || loopStrips.length < 2) return loopStrips;

    let list = [...loopStrips];

    let changed = true;
    while (changed) {
      changed = false;
      outer: for (let i = 0; i < list.length; i++) {
        for (let j = 0; j < list.length; j++) {
          if (i === j) continue;
          const a = list[i];
          const b = list[j];
          const endA = (a.stripStartLoopIdx + a.strip.length - 1) % n;
          if ((endA + 1) % n !== b.stripStartLoopIdx) continue;

          list = list.filter((_, idx) => idx !== i && idx !== j);
          list.push({
            ...a,
            strip: [...a.strip, ...b.strip],
            stripStartLoopIdx: a.stripStartLoopIdx,
          });
          changed = true;
          break outer;
        }
      }
    }

    return list.filter((candidate, i) => {
      const candidateIds = new Set(candidate.strip.map((v) => v.id));
      return !list.some((other, j) => {
        if (j === i || other.strip.length <= candidate.strip.length) return false;
        const otherIds = new Set(other.strip.map((v) => v.id));
        for (const id of candidateIds) {
          if (!otherIds.has(id)) return false;
        }
        return true;
      });
    });
  }

  /**
   * Maximal contiguous runs of unsnapped border vertices on the tooth loop.
   */
  private findUnsnappedBorderStrips(
    toothMesh: HalfEdgeMesh,
    snappedIds: Set<number>,
  ): {
    loops: Vertex[][];
    walkedVertices: Vertex[];
    strips: BorderStripCandidate[];
  } {
    const isUnsnapped = (v: Vertex) => !snappedIds.has(v.id);
    const loops = toothMesh.getBorderLoops();
    const strips: BorderStripCandidate[] = [];

    for (let loopIndex = 0; loopIndex < loops.length; loopIndex++) {
      const loop = loops[loopIndex];
      const n = loop.length;
      if (n === 0) continue;

      let startIdx = 0;
      let foundTransition = false;
      for (let i = 0; i < n; i++) {
        if (isUnsnapped(loop[i]) && !isUnsnapped(loop[(i + n - 1) % n])) {
          startIdx = i;
          foundTransition = true;
          break;
        }
      }

      const loopStrips: BorderStripCandidate[] = [];
      let current: Vertex[] = [];
      let currentStartIdx = -1;
      for (let k = 0; k < n; k++) {
        const loopIdx = (startIdx + k) % n;
        const v = loop[loopIdx];
        if (isUnsnapped(v)) {
          if (current.length === 0) currentStartIdx = loopIdx;
          current.push(v);
        } else if (current.length > 0) {
          loopStrips.push({
            loop,
            loopIndex,
            strip: current,
            startIdx,
            stripStartLoopIdx: currentStartIdx,
            foundTransition,
          });
          current = [];
          currentStartIdx = -1;
        }
      }
      if (current.length > 0) {
        loopStrips.push({
          loop,
          loopIndex,
          strip: current,
          startIdx,
          stripStartLoopIdx: currentStartIdx,
          foundTransition,
        });
      }

      strips.push(...this.mergeWrapSplitBorderStrips(loopStrips, loop));
    }

    return { loops, walkedVertices: loops.flat(), strips };
  }

  /** Explains loop walk coverage, strip segmentation, and anchor lookup for one strip. */
  private logBorderStripDiagnostics(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    pass: number,
    loop: Vertex[],
    strip: Vertex[],
    stripStartLoopIdx: number,
    snappedIds: Set<number>,
    startIdx: number,
    foundTransition: boolean,
    borderCount: number,
    snappedBorderCount: number,
  ): void {
    const n = loop.length;
    const loopIdSet = new Set(loop.map((v) => v.id));
    const borderVerts = toothMesh.getBorderVertices();
    const borderIdsNotInLoop = borderVerts.filter((v) => !loopIdSet.has(v.id)).map((v) => v.id);
    const snappedOnLoop = loop.filter((v) => snappedIds.has(v.id)).length;

    const endLoopIdx = n > 0 ? (stripStartLoopIdx + strip.length - 1) % n : -1;
    const loopPredIdx = n > 0 ? (stripStartLoopIdx + n - 1) % n : -1;
    const loopSuccIdx = n > 0 ? (endLoopIdx + 1) % n : -1;
    const loopPred = loopPredIdx >= 0 ? loop[loopPredIdx] : null;
    const loopSucc = loopSuccIdx >= 0 ? loop[loopSuccIdx] : null;

    const first = strip[0];
    const last = strip[strip.length - 1];
    const second = strip.length > 1 ? strip[1] : null;
    const beforeLast = strip.length > 1 ? strip[strip.length - 2] : null;
    const graphNeighborsFirst = this.getBorderNeighbors(first);
    const graphNeighborsLast = this.getBorderNeighbors(last);

    const snappedBeforeGraph = graphNeighborsFirst.find(
      (neighbor) => snappedIds.has(neighbor.id) && (!second || neighbor.id !== second.id),
    );
    const snappedAfterGraph = graphNeighborsLast.find(
      (neighbor) => snappedIds.has(neighbor.id) && (!beforeLast || neighbor.id !== beforeLast.id),
    );

    const stripSpansFullLoop = strip.length === n;
    const loopPredSnapped = loopPred ? snappedIds.has(loopPred.id) : false;
    const loopSuccSnapped = loopSucc ? snappedIds.has(loopSucc.id) : false;

    let stopReason: string;
    if (n === 0) {
      stopReason = 'empty_border_loop';
    } else if (stripSpansFullLoop) {
      stopReason = foundTransition
        ? 'strip_equals_full_walked_loop'
        : 'no_snapped_to_unsnapped_transition_entire_walked_loop_unsnapped';
    } else {
      stopReason = 'strip_bounded_by_snapped_vertices_on_walked_loop';
    }

    const anchorMissReason: string[] = [];
    if (!snappedBeforeGraph) {
      if (loopPredSnapped && loopPred && (!second || loopPred.id !== second.id)) {
        anchorMissReason.push(
          'loop_pred_is_snapped_but_getBorderNeighbors_missed_it (adjacency mismatch)',
        );
      } else if (stripSpansFullLoop) {
        anchorMissReason.push('full_loop_strip: loop_pred_is_also_unsnapped (wrap-around)');
      } else if (loopPred && !loopPredSnapped) {
        anchorMissReason.push(`loop_pred ${loopPred.id} exists but is unsnapped`);
      } else {
        anchorMissReason.push('no_snapped_graph_neighbor_at_strip_start');
      }
    }
    if (!snappedAfterGraph) {
      if (loopSuccSnapped && loopSucc && (!beforeLast || loopSucc.id !== beforeLast.id)) {
        anchorMissReason.push(
          'loop_succ_is_snapped_but_getBorderNeighbors_missed_it (adjacency mismatch)',
        );
      } else if (stripSpansFullLoop) {
        anchorMissReason.push('full_loop_strip: loop_succ_is_also_unsnapped (wrap-around)');
      } else if (loopSucc && !loopSuccSnapped) {
        anchorMissReason.push(`loop_succ ${loopSucc.id} exists but is unsnapped`);
      } else {
        anchorMissReason.push('no_snapped_graph_neighbor_at_strip_end');
      }
    }

    this.logPapilla(toothIndex, `pass ${pass} strip boundary diagnostics (${strip.length} verts)`, {
      stopReason,
      walkedLoopLength: n,
      getBorderVerticesLength: borderCount,
      borderVertsMissingFromWalk: borderIdsNotInLoop.length,
      borderVertsMissingFromWalkSample: borderIdsNotInLoop.slice(0, 12),
      snappedBorderVertsTotal: snappedBorderCount,
      snappedVertsOnWalkedLoop: snappedOnLoop,
      stripStartLoopIdx,
      stripEndLoopIdx: endLoopIdx,
      segmentationStartIdx: startIdx,
      foundSnappedToUnsnappedTransition: foundTransition,
      loopPred: loopPred ? { id: loopPred.id, snapped: loopPredSnapped } : null,
      loopSucc: loopSucc ? { id: loopSucc.id, snapped: loopSuccSnapped } : null,
      stripFirstId: first.id,
      stripLastId: last.id,
      graphNeighborsFirst: graphNeighborsFirst.map((v) => ({
        id: v.id,
        snapped: snappedIds.has(v.id),
        isStripSecond: second?.id === v.id,
      })),
      graphNeighborsLast: graphNeighborsLast.map((v) => ({
        id: v.id,
        snapped: snappedIds.has(v.id),
        isStripBeforeLast: beforeLast?.id === v.id,
      })),
      snappedBeforeGraphId: snappedBeforeGraph?.id ?? null,
      snappedAfterGraphId: snappedAfterGraph?.id ?? null,
      anchorMissReason,
    });

    if (anchorMissReason.length > 0) {
      this.segWarn(
        `papilla · tooth ${toothIndex}`,
        `pass ${pass}: strip has ${strip.length} verts but no gum anchors — ${stopReason}`,
        { anchorMissReason, walkedLoopLength: n, borderCount },
      );
    }
  }

  /**
   * Builds one papilla search from an unsnapped border strip: gum anchors at the
   * snapped neighbors on each side of the strip.
   */
  private papillaSearchFromStrip(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    pass: number,
    gumToSnap: Map<Vertex, SnapInfo>,
    strip: Vertex[],
    snappedIds: Set<number>,
    stripDebug?: {
      loop: Vertex[];
      stripStartLoopIdx: number;
      startIdx: number;
      foundTransition: boolean;
      borderCount: number;
      snappedBorderCount: number;
    },
  ): PapillaSearchResult | null {
    if (strip.length === 0) return null;

    const first = strip[0];
    const last = strip[strip.length - 1];
    const second = strip.length > 1 ? strip[1] : null;
    const beforeLast = strip.length > 1 ? strip[strip.length - 2] : null;

    const loop = stripDebug?.loop;
    const endLoopIdx =
      loop && stripDebug ? (stripDebug.stripStartLoopIdx + strip.length - 1) % loop.length : -1;
    const loopPred =
      loop && stripDebug ? loop[(stripDebug.stripStartLoopIdx + loop.length - 1) % loop.length] : null;
    const loopSucc = loop && endLoopIdx >= 0 ? loop[(endLoopIdx + 1) % loop.length] : null;

    const snappedBeforeFromLoop =
      loopPred && snappedIds.has(loopPred.id) && loopPred.id !== second?.id ? loopPred : null;
    const snappedAfterFromLoop =
      loopSucc && snappedIds.has(loopSucc.id) && loopSucc.id !== beforeLast?.id ? loopSucc : null;

    const snappedBefore = snappedBeforeFromLoop ?? this.getBorderNeighbors(first).find(
      (n) => snappedIds.has(n.id) && (!second || n.id !== second.id),
    );
    const snappedAfter = snappedAfterFromLoop ?? this.getBorderNeighbors(last).find(
      (n) => snappedIds.has(n.id) && (!beforeLast || n.id !== beforeLast.id),
    );

    const forwardAnchor = snappedBefore
      ? this.firstGumSnapOnToothBorder(gumToSnap, toothIndex, snappedBefore)
      : null;
    const backwardAnchor = snappedAfter
      ? this.firstGumSnapOnToothBorder(gumToSnap, toothIndex, snappedAfter)
      : null;

    const mid = Math.floor(strip.length / 2);
    const apex = strip[mid];
    const visitedIds = new Set<number>();
    for (const v of strip) visitedIds.add(v.id);
    if (snappedBefore) visitedIds.add(snappedBefore.id);
    if (snappedAfter) visitedIds.add(snappedAfter.id);

    this.logPapilla(toothIndex, `pass ${pass} strip (${strip.length} verts)`, {
      stripVertexIds: strip.map((v) => v.id),
      snappedBeforeId: snappedBefore?.id ?? null,
      snappedAfterId: snappedAfter?.id ?? null,
      snappedBeforeSource: snappedBeforeFromLoop ? 'loop' : snappedBefore ? 'graph-neighbor' : null,
      snappedAfterSource: snappedAfterFromLoop ? 'loop' : snappedAfter ? 'graph-neighbor' : null,
      forwardGumPaired: !!forwardAnchor,
      backwardGumPaired: !!backwardAnchor,
    });

    if (stripDebug && (!forwardAnchor || !backwardAnchor)) {
      this.logBorderStripDiagnostics(
        toothMesh,
        toothIndex,
        pass,
        stripDebug.loop,
        strip,
        stripDebug.stripStartLoopIdx,
        snappedIds,
        stripDebug.startIdx,
        stripDebug.foundTransition,
        stripDebug.borderCount,
        stripDebug.snappedBorderCount,
      );
    }

    return {
      toothIndex,
      pass,
      apex,
      forwardWalk: strip.slice(0, mid),
      backwardWalk: strip.slice(mid + 1),
      forwardAnchor,
      backwardAnchor,
      visitedIds,
      strip,
      stripStartLoopIdx: stripDebug?.stripStartLoopIdx ?? 0,
      loop: stripDebug?.loop ?? [],
      snappedBefore: snappedBefore ?? null,
      snappedAfter: snappedAfter ?? null,
    };
  }

  /**
   * Builds a paste-friendly border-loop walk report for debugging (focus tooth).
   */
  private buildToothBorderWalkReport(
    toothMesh: HalfEdgeMesh,
    toothIndex: number,
    gumToSnap: Map<Vertex, SnapInfo>,
    snappedIds: Set<number>,
    loops: Vertex[][],
    selected: BorderStripCandidate[],
    searches: PapillaSearchResult[],
    teethMeshes: HalfEdgeMesh[],
    gapBuckets?: {
      vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    jawCentroid?: Vector3,
  ): Record<string, unknown> {
    const gumIdsByToothVertex = new Map<number, number[]>();
    for (const [gumV, info] of gumToSnap.entries()) {
      if (info.toothIndex !== toothIndex) continue;
      const tid = info.toothVertex.id;
      if (!gumIdsByToothVertex.has(tid)) gumIdsByToothVertex.set(tid, []);
      gumIdsByToothVertex.get(tid)!.push(gumV.id);
    }

    const vertexPapilla = new Map<number, { pass: number; roles: string[] }>();
    const addRole = (vid: number, pass: number, role: string) => {
      const cur = vertexPapilla.get(vid) ?? { pass, roles: [] };
      if (!cur.roles.includes(role)) cur.roles.push(role);
      vertexPapilla.set(vid, cur);
    };

    for (const s of searches) {
      const pass = s.pass;
      for (const v of s.strip) {
        addRole(
          v.id,
          pass,
          v.id === s.strip[0].id ? 'strip-first' : v.id === s.strip[s.strip.length - 1].id ? 'strip-last' : 'on-strip',
        );
      }
      if (s.apex) addRole(s.apex.id, pass, 'apex');
      if (s.snappedBefore) addRole(s.snappedBefore.id, pass, 'snappedBefore/anchorF-side');
      if (s.snappedAfter) addRole(s.snappedAfter.id, pass, 'snappedAfter/anchorB-side');
      if (s.forwardAnchor) addRole(s.forwardAnchor.toothVertex.id, pass, 'anchorF');
      if (s.backwardAnchor) addRole(s.backwardAnchor.toothVertex.id, pass, 'anchorB');
    }

    const vertexGap = new Map<number, string>();
    if (gapBuckets) {
      const assign = (arr: Array<{ toothVertex: Vertex }>, bucket: string) => {
        for (const c of arr) vertexGap.set(c.toothVertex.id, bucket);
      };
      assign(gapBuckets.vestMesial, 'vestMesial');
      assign(gapBuckets.lingMesial, 'lingMesial');
      assign(gapBuckets.vestDistal, 'vestDistal');
      assign(gapBuckets.lingDistal, 'lingDistal');
    }

    const passContactSide = new Map<number, string>();
    for (const s of searches) {
      const cands = [
        ...(s.forwardAnchor ? [s.forwardAnchor] : []),
        ...(s.backwardAnchor ? [s.backwardAnchor] : []),
      ];
      if (cands.length > 0) {
        passContactSide.set(s.pass, this.papillaPassContactSide(cands, toothIndex, teethMeshes));
      }
    }

    const topStrips = selected.map((stripCand, pass) => {
      const loop = stripCand.loop;
      const n = loop.length;
      const start = stripCand.stripStartLoopIdx;
      const end = n > 0 ? (start + stripCand.strip.length - 1) % n : -1;
      const loopPred = n > 0 ? loop[(start + n - 1) % n] : null;
      const loopSucc = n > 0 && end >= 0 ? loop[(end + 1) % n] : null;
      const search = searches.find((s) => s.pass === pass);
      return {
        pass,
        rank: pass,
        length: stripCand.strip.length,
        contactSide: passContactSide.get(pass) ?? null,
        stripStartLoopIdx: start,
        stripEndLoopIdx: end,
        firstId: stripCand.strip[0]?.id ?? null,
        lastId: stripCand.strip[stripCand.strip.length - 1]?.id ?? null,
        loopPredId: loopPred?.id ?? null,
        loopPredSnapped: loopPred ? snappedIds.has(loopPred.id) : null,
        loopSuccId: loopSucc?.id ?? null,
        loopSuccSnapped: loopSucc ? snappedIds.has(loopSucc.id) : null,
        anchorFToothId: search?.forwardAnchor?.toothVertex.id ?? null,
        anchorFGumId: search?.forwardAnchor?.gumVertex.id ?? null,
        anchorBToothId: search?.backwardAnchor?.toothVertex.id ?? null,
        anchorBGumId: search?.backwardAnchor?.gumVertex.id ?? null,
        vertexIds: stripCand.strip.map((v) => v.id),
      };
    });

    const loopWalks: Record<string, unknown>[] = [];

    for (let loopIndex = 0; loopIndex < loops.length; loopIndex++) {
      const loop = loops[loopIndex];
      const n = loop.length;
      if (n === 0) continue;

      let startIdx = 0;
      for (let i = 0; i < n; i++) {
        const unsnapped = !snappedIds.has(loop[i].id);
        const predSnapped = snappedIds.has(loop[(i + n - 1) % n].id);
        if (unsnapped && predSnapped) {
          startIdx = i;
          break;
        }
      }

      for (let k = 0; k < n; k++) {
        const loopIdx = (startIdx + k) % n;
        const v = loop[loopIdx];
        const pred = loop[(loopIdx + n - 1) % n];
        const succ = loop[(loopIdx + 1) % n];
        const graphNeighbors = this.getBorderNeighbors(v).map((nb) => nb.id);
        const papilla = vertexPapilla.get(v.id);
        const world = toothMesh.localToWorld(v.position.clone());
        const gumIds = gumIdsByToothVertex.get(v.id) ?? [];
        const gumVertexForDist =
          gumIds.length > 0 ? [...gumToSnap.keys()].find((g) => gumIds.includes(g.id)) : undefined;

        loopWalks.push({
          loop: loopIndex,
          walkIdx: k,
          loopIdx,
          vertexId: v.id,
          state: snappedIds.has(v.id) ? 'snapped' : 'unsnapped',
          loopPredId: pred.id,
          loopSuccId: succ.id,
          graphNeighborIds: graphNeighbors,
          gumIds: gumIds.length > 0 ? gumIds : null,
          papillaPass: papilla?.pass ?? null,
          papillaRoles: papilla?.roles.length ? papilla.roles : null,
          gapBucket: vertexGap.get(v.id) ?? null,
          distToJawArchXZ:
            jawCentroid && gumVertexForDist
              ? +Math.sqrt(this.gumDistanceXZSquaredToWorld(gumVertexForDist, jawCentroid)).toFixed(4)
              : null,
          world: { x: +world.x.toFixed(4), y: +world.y.toFixed(4), z: +world.z.toFixed(4) },
        });
      }
    }

    return {
      tooth: toothIndex,
      borderVertexCount: toothMesh.getBorderVertices().length,
      loopCount: loops.length,
      loopSizes: loops.map((l) => l.length),
      snappedBorderCount: snappedIds.size,
      topStrips,
      walkHighlights: this.pickBorderWalkHighlights(loopWalks),
      walkVertexCount: loopWalks.length,
      walk: loopWalks,
    };
  }

  /** Vertices worth pasting: strip ends, anchors, snapped↔unsnapped transitions, gap buckets. */
  private pickBorderWalkHighlights(
    walk: Array<Record<string, unknown>>,
  ): Array<Record<string, unknown>> {
    const out: Array<Record<string, unknown>> = [];
    for (let i = 0; i < walk.length; i++) {
      const row = walk[i];
      const prev = i > 0 ? walk[i - 1] : null;
      const roles = row.papillaRoles as string[] | null;
      const stateChange = prev && prev.state !== row.state;
      const interesting =
        (roles && roles.length > 0) ||
        row.gapBucket ||
        stateChange ||
        roles?.some((r) => r.includes('strip-first') || r.includes('strip-last') || r.includes('anchor'));
      if (interesting) {
        out.push({
          walkIdx: row.walkIdx,
          vertexId: row.vertexId,
          state: row.state,
          loopPredId: row.loopPredId,
          loopSuccId: row.loopSuccId,
          graphNeighborIds: row.graphNeighborIds,
          papillaPass: row.papillaPass,
          papillaRoles: row.papillaRoles,
          gapBucket: row.gapBucket,
          gumIds: row.gumIds,
          distToJawArchXZ: row.distToJawArchXZ,
        });
      }
    }
    return out;
  }

  /** Logs border walk report — compact summary + copy JSON. Full walk on window.zubeeSegBorderWalk. */
  private logToothBorderWalkReport(
    toothIndex: number,
    report: Record<string, unknown>,
    phase: 'papilla' | 'vest-ling',
  ): void {
    if (!this.papillaSearchDebug || !this.shouldLogPapillaVerbose(toothIndex)) return;

    const store = ((globalThis as Record<string, unknown>).zubeeSegBorderWalk ??= {}) as Record<
      number,
      Record<string, unknown>
    >;
    if (!store[toothIndex]) store[toothIndex] = {};
    store[toothIndex][phase] = report;

    const pastePayload = {
      tooth: report.tooth,
      phase,
      borderVertexCount: report.borderVertexCount,
      loopSizes: report.loopSizes,
      snappedBorderCount: report.snappedBorderCount,
      topStrips: report.topStrips,
      walkHighlights: report.walkHighlights,
      walkVertexCount: report.walkVertexCount,
    };

    this.segGroupStart(
      `★ border walk · tooth ${toothIndex} · ${phase} (paste me)`,
      JawSegmentation.SEG_STYLE.papilla,
      false,
    );
    console.log(
      `%cFOCUS TOOTH ${toothIndex} — copy JSON below or run: copy(JSON.stringify(window.zubeeSegBorderWalk[${toothIndex}]["${phase}"]))`,
      JawSegmentation.SEG_STYLE.papilla,
    );
    console.log(`=== COPY tooth ${toothIndex} ${phase} (highlights + strips) ===`);
    console.log(JSON.stringify(pastePayload));
    console.log(`=== END COPY ===`);
    console.log(
      `Full ${report.walkVertexCount} vertex walk: window.zubeeSegBorderWalk[${toothIndex}]["${phase}"].walk`,
    );
    this.segTable('top strips', report.topStrips as Record<string, unknown>[]);
    this.segTable('walk highlights', report.walkHighlights as Record<string, unknown>[]);
    this.segGroupEnd();
  }

  /**
   * Picks the longest unsnapped border strip(s) on a tooth and converts each to a papilla search.
   * Middle teeth: 2 strips; corner / single-neighbor teeth: 1 strip.
   */
  private runPapillaSearchesForTooth(
    teethMeshes: HalfEdgeMesh[],
    toothIndex: number,
    gumToSnap: Map<Vertex, SnapInfo>,
    numStrips: number,
  ): PapillaSearchResult[] {
    const toothMesh = teethMeshes[toothIndex];
    const isMiddle = toothIndex > 0 && toothIndex < teethMeshes.length - 1;
    this.segGroupStart(
      `papilla · tooth ${toothIndex} (${isMiddle ? 'middle' : 'corner'}, up to ${numStrips} strip${numStrips > 1 ? 's' : ''})`,
      JawSegmentation.SEG_STYLE.papilla,
    );

    toothMesh.calculateBorderVertices();
    const borderArr = toothMesh.getBorderVertices();
    if (borderArr.length < 4) {
      this.logPapilla(toothIndex, 'abort: border too small', { borderVerts: borderArr.length });
      this.segGroupEnd();
      return [];
    }

    const snappedSet = this.buildSnappedToothBorderVertexSet(gumToSnap, toothIndex);
    if (snappedSet.size === 0) {
      this.logPapilla(toothIndex, 'abort: no snapped border verts');
      this.segGroupEnd();
      return [];
    }
    const snappedIds = new Set<number>();
    for (const v of snappedSet) snappedIds.add(v.id);

    const {
      loops,
      walkedVertices,
      strips,
    } = this.findUnsnappedBorderStrips(toothMesh, snappedIds);
    strips.sort((a, b) => b.strip.length - a.strip.length);
    const selected = strips.slice(0, numStrips);

    if (this.papillaSearchDebug && this.shouldLogPapillaVerbose(toothIndex)) {
      this.segGroupStart(`strip catalog · tooth ${toothIndex}`, JawSegmentation.SEG_STYLE.papilla);
      this.segTable(
        'all unsnapped strips (sorted)',
        strips.map((s, rank) => ({
          rank,
          length: s.strip.length,
          stripStartLoopIdx: s.stripStartLoopIdx,
          firstId: s.strip[0]?.id,
          lastId: s.strip[s.strip.length - 1]?.id,
          vertexIds: s.strip.map((v) => v.id).join(','),
          selected: rank < numStrips,
        })),
      );
      this.segGroupEnd();
    }

    const loopIdSet = new Set(walkedVertices.map((v) => v.id));
    const borderIdsNotInLoop = borderArr.filter((v) => !loopIdSet.has(v.id)).map((v) => v.id);
    const snappedOnLoop = walkedVertices.filter((v) => snappedIds.has(v.id)).length;

    this.logPapilla(toothIndex, 'unsnapped strips', {
      totalStrips: strips.length,
      stripSizes: strips.map((s) => s.strip.length),
      selected: selected.map((s) => s.strip.length),
      loopCount: loops.length,
      loopSizes: loops.map((loop) => loop.length),
      singletonLoopCount: loops.filter((loop) => loop.length === 1).length,
      smallLoopSizes: loops.filter((loop) => loop.length <= 3).map((loop) => loop.length),
      walkedLoopLength: walkedVertices.length,
      getBorderVerticesLength: borderArr.length,
      borderVertsMissingFromWalk: borderIdsNotInLoop.length,
      snappedBorderVertsTotal: snappedSet.size,
      snappedVertsOnWalkedLoop: snappedOnLoop,
    });

    if (walkedVertices.length < borderArr.length) {
      this.segWarn(
        `papilla · tooth ${toothIndex}`,
        `border loop collector visited ${walkedVertices.length}/${borderArr.length} border verts — adjacency may be incomplete`,
        { missingSample: borderIdsNotInLoop.slice(0, 16) },
      );
    }

    const stripDebugBase = {
      borderCount: borderArr.length,
      snappedBorderCount: snappedSet.size,
    };

    const searches: PapillaSearchResult[] = [];
    for (let pass = 0; pass < selected.length; pass++) {
      const search = this.papillaSearchFromStrip(
        toothMesh,
        toothIndex,
        pass,
        gumToSnap,
        selected[pass].strip,
        snappedIds,
        {
          ...stripDebugBase,
          loop: selected[pass].loop,
          stripStartLoopIdx: selected[pass].stripStartLoopIdx,
          startIdx: selected[pass].startIdx,
          foundTransition: selected[pass].foundTransition,
        },
      );
      if (search) searches.push(search);
    }

    this.segGroupEnd();
    return searches;
  }

  /**
   * Per-tooth papilla search visualization.
   *   - pass 0: bright-green apex, light-green walk dots
   *   - pass 1: blue apex, light-blue walk dots
   *   - anchors (snapped border hits): orange (forward) / dark-orange (backward)
   */
  private visualizePapillaSearches(
    teethMeshes: HalfEdgeMesh[],
    searchesByTooth: Array<PapillaSearchResult[]>,
  ): void {
    this.removeSceneObjectsByNamePrefix('debug-papilla-');
    let id = 0;
    const add = (world: Vector3, color: number, radius: number, suffix: string) => {
      const m = new Mesh(new SphereGeometry(radius, 8, 8), new MeshBasicMaterial({ color }));
      m.position.copy(world);
      m.name = `debug-papilla-${id++}-${suffix}`;
      this.rootState.scene.add(m);
    };
    for (let ti = 0; ti < searchesByTooth.length; ti++) {
      const mesh = teethMeshes[ti];
      for (const s of searchesByTooth[ti]) {
        const apexColor = s.pass === 0 ? 0x00ff44 : 0x3399ff;
        const walkColor = s.pass === 0 ? 0x98fb98 : 0x99ccff;
        add(mesh.localToWorld(s.apex.position.clone()), apexColor, 0.18, `t${ti}-p${s.pass}-apex`);
        for (const v of s.forwardWalk) {
          add(mesh.localToWorld(v.position.clone()), walkColor, 0.055, `t${ti}-p${s.pass}-fwd`);
        }
        for (const v of s.backwardWalk) {
          add(mesh.localToWorld(v.position.clone()), walkColor, 0.055, `t${ti}-p${s.pass}-bwd`);
        }
        if (s.forwardAnchor) {
          add(
            mesh.localToWorld(s.forwardAnchor.toothVertex.position.clone()),
            0xff9900,
            0.12,
            `t${ti}-p${s.pass}-anchorF`,
          );
        }
        if (s.backwardAnchor) {
          add(
            mesh.localToWorld(s.backwardAnchor.toothVertex.position.clone()),
            0xff6600,
            0.12,
            `t${ti}-p${s.pass}-anchorB`,
          );
        }
      }
    }
  }

  /** Snap anchor candidates from one or more papilla searches on a tooth. */
  private collectPapillaAnchorCandidates(
    searches: PapillaSearchResult[],
  ): Array<{ gumVertex: Vertex; toothVertex: Vertex; passIndex: number }> {
    const out: Array<{ gumVertex: Vertex; toothVertex: Vertex; passIndex: number }> = [];
    for (const s of searches) {
      if (s.forwardAnchor) out.push({ ...s.forwardAnchor, passIndex: s.pass });
      if (s.backwardAnchor) out.push({ ...s.backwardAnchor, passIndex: s.pass });
    }
    return out;
  }

  /**
   * World-space centroid of the dental arch — average of every tooth mesh's
   * centroid. Used to label anchors as vestibular (further from arch center)
   * vs lingual (closer to arch center).
   */
  private computeJawArchCentroidWorld(teethMeshes: HalfEdgeMesh[]): Vector3 {
    const out = new Vector3();
    if (teethMeshes.length === 0) return out;
    for (const mesh of teethMeshes) {
      out.add(mesh.localToWorld(mesh.getCentroid().clone()));
    }
    return out.divideScalar(teethMeshes.length);
  }

  /** Squared XZ distance between two world-space points (ignores Y). */
  private distanceXZSquared(a: Vector3, b: Vector3): number {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    return dx * dx + dz * dz;
  }

  /** Squared XZ distance from a gum vertex to a world reference point. */
  private gumDistanceXZSquaredToWorld(gumVertex: Vertex, refWorld: Vector3): number {
    const gumWorld = this.localToWorld(gumVertex.position.clone());
    return this.distanceXZSquared(gumWorld, refWorld);
  }

  /**
   * Splits anchor candidates into vestibular (farther from jaw arch center in XZ)
   * and lingual (closer to arch center). The closest floor(n/2) → lingual.
   */
  private splitCandidatesVestLing<T extends { gumVertex: Vertex; toothVertex: Vertex; passIndex?: number }>(
    candidates: T[],
    jawArchCentroid: Vector3,
    debug?: { toothIndex: number; group: string },
  ): { vest: T[]; ling: T[] } {
    if (candidates.length === 0) return { vest: [], ling: [] };
    const scored = candidates.map((c) => ({
      c,
      dArchXZSq: this.gumDistanceXZSquaredToWorld(c.gumVertex, jawArchCentroid),
    }));
    scored.sort((a, b) => a.dArchXZSq - b.dArchXZSq);
    const nLing = Math.max(1, Math.floor(candidates.length / 2));
    const lingSet = new Set(scored.slice(0, nLing).map((x) => x.c));
    const vest: T[] = [];
    const ling: T[] = [];
    for (const c of candidates) {
      if (lingSet.has(c)) ling.push(c);
      else vest.push(c);
    }

    if (debug && this.papillaSearchDebug) {
      this.segGroupStart(
        `vest-ling · tooth ${debug.toothIndex} · ${debug.group}`,
        JawSegmentation.SEG_STYLE.vestLing,
      );
      console.log('rule: closer to jaw arch centroid (XZ) → lingual; farther → vestibular');
      console.log('split', { nLing, nVest: candidates.length - nLing, candidateCount: candidates.length });
      this.segTable(
        'ranked candidates',
        scored.map((row, rank) => ({
          rank,
          label: lingSet.has(row.c) ? 'lingual' : 'vestibular',
          pass: row.c.passIndex ?? '—',
          gumId: row.c.gumVertex.id,
          toothId: row.c.toothVertex.id,
          distToJawArchXZ: +Math.sqrt(row.dArchXZSq).toFixed(4),
        })),
      );
      this.segTable('summary', [
        { side: 'lingual', toothIds: ling.map((c) => c.toothVertex.id).join(', ') || '—' },
        { side: 'vestibular', toothIds: vest.map((c) => c.toothVertex.id).join(', ') || '—' },
      ]);
      this.segGroupEnd();
    }

    return { vest, ling };
  }

  private toothCentroidWorld(toothMesh: HalfEdgeMesh): Vector3 {
    return toothMesh.localToWorld(toothMesh.getCentroid().clone());
  }

  private gumPairMidpointWorld(gumA: Vertex, gumB: Vertex): Vector3 {
    const aw = this.localToWorld(gumA.position.clone());
    const bw = this.localToWorld(gumB.position.clone());
    return aw.add(bw).multiplyScalar(0.5);
  }

  /**
   * Pair vest×vest and ling×ling across a gap using pre-partitioned buckets.
   */
  private pairGapAnchorsFromBuckets(
    partsA: {
      vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    partsB: {
      vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
  ): {
    vestPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
    lingPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
  } | null {
    const vestPair = this.findClosestSnapPair(partsA.vestDistal, partsB.vestMesial);
    const lingPair = this.findClosestSnapPair(partsA.lingDistal, partsB.lingMesial);
    if (!vestPair || !lingPair) return null;
    return { vestPair, lingPair };
  }

  /**
   * Later gaps: within each vest/ling bucket, pick the pair whose midpoint
   * stays closest to the previous gap's reference (arch continuity).
   */
  private pairGapAnchorsChainedFromBuckets(
    partsA: {
      vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    partsB: {
      vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    vestRef: Vector3,
    lingRef: Vector3,
  ): {
    vestPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
    lingPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
  } | null {
    const vestPair = this.findClosestSnapPairByMidpointRef(partsA.vestDistal, partsB.vestMesial, vestRef);
    const lingPair = this.findClosestSnapPairByMidpointRef(partsA.lingDistal, partsB.lingMesial, lingRef);
    if (!vestPair || !lingPair) return null;
    return { vestPair, lingPair };
  }

  /** Closest gum–gum pair whose midpoint is nearest to `refWorld`. */
  private findClosestSnapPairByMidpointRef(
    setA: { gumVertex: Vertex; toothVertex: Vertex }[],
    setB: { gumVertex: Vertex; toothVertex: Vertex }[],
    refWorld: Vector3,
  ): { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } } | null {
    if (setA.length === 0 || setB.length === 0) return null;

    let bestA: (typeof setA)[0] | null = null;
    let bestB: (typeof setB)[0] | null = null;
    let bestDistSq = Infinity;

    for (const a of setA) {
      for (const b of setB) {
        const mid = this.gumPairMidpointWorld(a.gumVertex, b.gumVertex);
        const dSq = mid.distanceToSquared(refWorld);
        if (dSq < bestDistSq) {
          bestDistSq = dSq;
          bestA = a;
          bestB = b;
        }
      }
    }

    return bestA && bestB ? { a: bestA, b: bestB } : null;
  }

  /**
   * If vestibular pair sits closer to the jaw arch center than lingual, swap them.
   */
  private correctVestLingSwapIfNeeded(
    vestPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } },
    lingPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } },
    jawArchCentroid: Vector3,
  ): {
    vestPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
    lingPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } };
    swapped: boolean;
  } {
    const meanArchXZ = (pair: typeof vestPair) => {
      const dA = this.gumDistanceXZSquaredToWorld(pair.a.gumVertex, jawArchCentroid);
      const dB = this.gumDistanceXZSquaredToWorld(pair.b.gumVertex, jawArchCentroid);
      return (dA + dB) * 0.5;
    };

    const vestMean = meanArchXZ(vestPair);
    const lingMean = meanArchXZ(lingPair);
    if (vestMean < lingMean) {
      return { vestPair: lingPair, lingPair: vestPair, swapped: true };
    }
    return { vestPair, lingPair, swapped: false };
  }

  /** Mesial vs distal for a pass's candidates (mean gum position vs neighbor centroids). */
  private papillaPassContactSide(
    passCandidates: Array<{ gumVertex: Vertex; toothVertex: Vertex }>,
    toothIndex: number,
    teethMeshes: HalfEdgeMesh[],
  ): 'mesial' | 'distal' {
    const prevMesh = toothIndex > 0 ? teethMeshes[toothIndex - 1] : null;
    const nextMesh = toothIndex < teethMeshes.length - 1 ? teethMeshes[toothIndex + 1] : null;
    if (!prevMesh) return 'distal';
    if (!nextMesh) return 'mesial';
    const prevCent = prevMesh.localToWorld(prevMesh.getCentroid().clone());
    const nextCent = nextMesh.localToWorld(nextMesh.getCentroid().clone());
    const mean = new Vector3();
    for (const c of passCandidates) {
      mean.add(this.localToWorld(c.gumVertex.position.clone()));
    }
    mean.divideScalar(passCandidates.length);
    return mean.distanceToSquared(nextCent) < mean.distanceToSquared(prevCent) ? 'distal' : 'mesial';
  }

  /**
   * Partition papilla candidates into {mesial, distal} × {vest, ling}.
   * Middle teeth with two passes: pass 0 vs pass 1 → opposite contacts,
   * then vest/ling split within each pass using the jaw arch centroid (XZ).
   */
  private partitionToothCandidatesByGap(
    candidates: Array<{ gumVertex: Vertex; toothVertex: Vertex; passIndex?: number }>,
    toothIndex: number,
    teethMeshes: HalfEdgeMesh[],
    jawArchCentroid: Vector3,
    activeSides: { mesial: boolean; distal: boolean },
  ): {
    vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
  } {
    const empty = { vestMesial: [], vestDistal: [], lingMesial: [], lingDistal: [] };
    if (candidates.length === 0) return empty;
    if (!activeSides.mesial && !activeSides.distal) return empty;

    const isMiddle = toothIndex > 0 && toothIndex < teethMeshes.length - 1;
    const pass0 = candidates.filter((c) => c.passIndex === 0);
    const pass1 = candidates.filter((c) => c.passIndex === 1);

    const debugCtx = { toothIndex };

    if (activeSides.mesial && activeSides.distal && pass0.length > 0 && pass1.length > 0) {
      const side0 = this.papillaPassContactSide(pass0, toothIndex, teethMeshes);
      const mesialPass = side0 === 'mesial' ? pass0 : pass1;
      const distalPass = side0 === 'distal' ? pass0 : pass1;
      if (this.papillaSearchDebug) {
        this.logVestLing(toothIndex, 'mesial/distal pass assignment', {
          pass0Side: side0,
          mesialPassCount: mesialPass.length,
          distalPassCount: distalPass.length,
        });
      }
      const mesialSplit = this.splitCandidatesVestLing(mesialPass, jawArchCentroid, {
        ...debugCtx,
        group: 'mesial-pass',
      });
      const distalSplit = this.splitCandidatesVestLing(distalPass, jawArchCentroid, {
        ...debugCtx,
        group: 'distal-pass',
      });
      return {
        vestMesial: mesialSplit.vest,
        lingMesial: mesialSplit.ling,
        vestDistal: distalSplit.vest,
        lingDistal: distalSplit.ling,
      };
    }

    const prevMesh = toothIndex > 0 ? teethMeshes[toothIndex - 1] : null;
    const nextMesh = toothIndex < teethMeshes.length - 1 ? teethMeshes[toothIndex + 1] : null;
    const prevCent = prevMesh ? prevMesh.localToWorld(prevMesh.getCentroid().clone()) : null;
    const nextCent = nextMesh ? nextMesh.localToWorld(nextMesh.getCentroid().clone()) : null;

    const vestLing = this.splitCandidatesVestLing(candidates, jawArchCentroid, {
      ...debugCtx,
      group: isMiddle ? 'single-pass-fallback' : 'end-tooth',
    });
    const out: {
      vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    } = { vestMesial: [], vestDistal: [], lingMesial: [], lingDistal: [] };
    const assignSide = (c: (typeof candidates)[0], isVest: boolean) => {
      const g = this.localToWorld(c.gumVertex.position.clone());
      let isDistal: boolean;
      let mesialDistalReason: string;
      if (!activeSides.mesial) {
        isDistal = true;
        mesialDistalReason = 'only distal gap active → distal';
      } else if (!activeSides.distal) {
        isDistal = false;
        mesialDistalReason = 'only mesial gap active → mesial';
      } else if (prevCent && nextCent) {
        const dPrev = g.distanceToSquared(prevCent);
        const dNext = g.distanceToSquared(nextCent);
        isDistal = dNext < dPrev;
        mesialDistalReason = `closer to next tooth centroid (dNext=${Math.sqrt(dNext).toFixed(3)} < dPrev=${Math.sqrt(dPrev).toFixed(3)})`;
      } else if (nextCent) {
        isDistal = true;
        mesialDistalReason = 'first tooth → distal (gap toward tooth 1)';
      } else {
        isDistal = false;
        mesialDistalReason = 'last tooth → mesial (gap toward previous tooth)';
      }
      const bucket =
        isVest && isDistal
          ? 'vestDistal'
          : isVest
            ? 'vestMesial'
            : isDistal
              ? 'lingDistal'
              : 'lingMesial';
      if (this.papillaSearchDebug) {
        this.segLog(
          `vest-ling · tooth ${toothIndex}`,
          JawSegmentation.SEG_STYLE.vestLing,
          `→ ${bucket} (${isVest ? 'vest' : 'ling'}, ${isDistal ? 'distal' : 'mesial'}) · gum ${c.gumVertex.id} · dJawArchXZ ${Math.sqrt(this.gumDistanceXZSquaredToWorld(c.gumVertex, jawArchCentroid)).toFixed(4)}`,
        );
        console.log('  reason:', mesialDistalReason);
      }
      if (isVest) {
        if (isDistal) out.vestDistal.push(c);
        else out.vestMesial.push(c);
      } else if (isDistal) {
        out.lingDistal.push(c);
      } else {
        out.lingMesial.push(c);
      }
    };
    for (const c of vestLing.vest) assignSide(c, true);
    for (const c of vestLing.ling) assignSide(c, false);
    return out;
  }

  /** Closest gum–gum pair between two candidate sets (gap pairing). */
  private findClosestSnapPair(
    setA: { gumVertex: Vertex; toothVertex: Vertex }[],
    setB: { gumVertex: Vertex; toothVertex: Vertex }[],
  ): { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } } | null {
    if (setA.length === 0 || setB.length === 0) return null;
    let minDist = Infinity;
    let bestA: { gumVertex: Vertex; toothVertex: Vertex } | null = null;
    let bestB: { gumVertex: Vertex; toothVertex: Vertex } | null = null;
    for (const a of setA) {
      const aWorld = this.localToWorld(a.gumVertex.position.clone());
      for (const b of setB) {
        const dist = aWorld.distanceToSquared(this.localToWorld(b.gumVertex.position.clone()));
        if (dist < minDist) {
          minDist = dist;
          bestA = a;
          bestB = b;
        }
      }
    }
    return bestA && bestB ? { a: bestA, b: bestB } : null;
  }

  private segDistToPoint(gumVertex: Vertex, ref: Vector3): number {
    return Math.sqrt(this.localToWorld(gumVertex.position.clone()).distanceToSquared(ref));
  }

  /**
   * Gap-level vest/ling diagnostics: bucket candidates, cross-pairs, tooth-centroid check.
   */
  private logGapVestLingDiagnostics(
    gapIndex: number,
    teethMeshes: HalfEdgeMesh[],
    partsA: {
      vestDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingDistal: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    partsB: {
      vestMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
      lingMesial: Array<{ gumVertex: Vertex; toothVertex: Vertex }>;
    },
    vestPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } },
    lingPair: { a: { gumVertex: Vertex; toothVertex: Vertex }; b: { gumVertex: Vertex; toothVertex: Vertex } },
    pairingMode: 'bucket-first' | 'chained-from-previous-gap',
    vestRef: Vector3 | null,
    lingRef: Vector3 | null,
  ): void {
    if (!this.papillaSearchDebug) return;

    const selectedGum = new Set([
      vestPair.a.gumVertex.id,
      vestPair.b.gumVertex.id,
      lingPair.a.gumVertex.id,
      lingPair.b.gumVertex.id,
    ]);

    const centA = this.toothCentroidWorld(teethMeshes[gapIndex]);
    const centB = this.toothCentroidWorld(teethMeshes[gapIndex + 1]);

    const bucketRows: Record<string, unknown>[] = [];
    const pushBucket = (
      tooth: number,
      bucket: string,
      arr: Array<{ gumVertex: Vertex; toothVertex: Vertex }>,
      jawArchCentroid: Vector3,
    ) => {
      for (const c of arr) {
        bucketRows.push({
          tooth,
          bucket,
          toothId: c.toothVertex.id,
          gumId: c.gumVertex.id,
          distToJawArchXZ: +Math.sqrt(this.gumDistanceXZSquaredToWorld(c.gumVertex, jawArchCentroid)).toFixed(4),
          inFinalPair: selectedGum.has(c.gumVertex.id) ? 'yes' : '',
        });
      }
    };
    const jawCent = this.computeJawArchCentroidWorld(teethMeshes);
    pushBucket(gapIndex, 'vestDistal', partsA.vestDistal, jawCent);
    pushBucket(gapIndex, 'lingDistal', partsA.lingDistal, jawCent);
    pushBucket(gapIndex + 1, 'vestMesial', partsB.vestMesial, jawCent);
    pushBucket(gapIndex + 1, 'lingMesial', partsB.lingMesial, jawCent);
    console.log('pairing mode', pairingMode, { vestRef, lingRef });
    this.segTable(`gap [${gapIndex}-${gapIndex + 1}] · bucket candidates`, bucketRows);

    type Tagged = { gumVertex: Vertex; toothVertex: Vertex; bucket: string };
    const distal: Tagged[] = [
      ...partsA.vestDistal.map((c) => ({ ...c, bucket: 'vestDistal' })),
      ...partsA.lingDistal.map((c) => ({ ...c, bucket: 'lingDistal' })),
    ];
    const mesial: Tagged[] = [
      ...partsB.vestMesial.map((c) => ({ ...c, bucket: 'vestMesial' })),
      ...partsB.lingMesial.map((c) => ({ ...c, bucket: 'lingMesial' })),
    ];

    const crossRows: Record<string, unknown>[] = [];
    for (const a of distal) {
      const aWorld = this.localToWorld(a.gumVertex.position.clone());
      const dToothA = this.segDistToPoint(a.gumVertex, centA);
      for (const b of mesial) {
        const dToothB = this.segDistToPoint(b.gumVertex, centB);
        const gumDist = aWorld.distanceTo(this.localToWorld(b.gumVertex.position.clone()));
        const mid = this.gumPairMidpointWorld(a.gumVertex, b.gumVertex);
        const aIsVest = a.bucket.startsWith('vest');
        const bIsVest = b.bucket.startsWith('vest');
        crossRows.push({
          A: a.bucket,
          B: b.bucket,
          gumDist: +gumDist.toFixed(4),
          dToothA: +dToothA.toFixed(4),
          dToothB: +dToothB.toFixed(4),
          dPrevVest: vestRef ? +mid.distanceTo(vestRef).toFixed(4) : '—',
          dPrevLing: lingRef ? +mid.distanceTo(lingRef).toFixed(4) : '—',
          labelIfUsed: aIsVest === bIsVest ? (aIsVest ? 'vest×vest' : 'ling×ling') : 'cross (vest×ling)',
          isChosenVest:
            (a.gumVertex.id === vestPair.a.gumVertex.id && b.gumVertex.id === vestPair.b.gumVertex.id) ||
            (a.gumVertex.id === vestPair.b.gumVertex.id && b.gumVertex.id === vestPair.a.gumVertex.id)
              ? 'VEST'
              : '',
          isChosenLing:
            (a.gumVertex.id === lingPair.a.gumVertex.id && b.gumVertex.id === lingPair.b.gumVertex.id) ||
            (a.gumVertex.id === lingPair.b.gumVertex.id && b.gumVertex.id === lingPair.a.gumVertex.id)
              ? 'LING'
              : '',
        });
      }
    }
    crossRows.sort((x, y) => (x.gumDist as number) - (y.gumDist as number));
    this.segTable(`gap [${gapIndex}-${gapIndex + 1}] · distal×mesial (sorted by gumDist)`, crossRows);

    const vestMeanArchXZ = Math.sqrt(
      (this.gumDistanceXZSquaredToWorld(vestPair.a.gumVertex, jawCent) +
        this.gumDistanceXZSquaredToWorld(vestPair.b.gumVertex, jawCent)) *
        0.5,
    );
    const lingMeanArchXZ = Math.sqrt(
      (this.gumDistanceXZSquaredToWorld(lingPair.a.gumVertex, jawCent) +
        this.gumDistanceXZSquaredToWorld(lingPair.b.gumVertex, jawCent)) *
        0.5,
    );
    const swapSuspected = vestMeanArchXZ < lingMeanArchXZ;

    console.log(`gap [${gapIndex}-${gapIndex + 1}] · jaw-arch-centroid check (XZ)`, {
      vestMeanDistToJawArchXZ: +vestMeanArchXZ.toFixed(4),
      lingMeanDistToJawArchXZ: +lingMeanArchXZ.toFixed(4),
      rule: 'vest farther from jaw arch centroid (XZ) than ling',
      swapSuspected,
    });
  }

  /**
   * Per-tooth unsnapped border strips, then pair distal of tooth i with mesial of tooth i+1
   * for vestibular and lingual at each interdental gap.
   */
  private findAllInterdentalAnchors(
    teethMeshes: HalfEdgeMesh[],
    gumToSnap: Map<Vertex, SnapInfo>,
  ): Map<number, InterdentalAnchors> {
    const result = new Map<number, InterdentalAnchors>();
    const toothCount = teethMeshes.length;
    const searchesByTooth: PapillaSearchResult[][] = teethMeshes.map(() => []);

    this.segGroupStart('STEP 5.5 — papilla search & anchors', JawSegmentation.SEG_STYLE.step);

    // One strip per ACTIVE adjacent gap. A middle tooth next to a wide gap
    // (no corridor) or a missing tooth behaves like a corner tooth on that
    // side — searching for a strip there would only pick up snap noise and
    // produce bogus anchors.
    for (let idx = 0; idx < toothCount; idx++) {
      const numStrips =
        (this.isInterdentalGapActive(idx - 1, toothCount) ? 1 : 0) +
        (this.isInterdentalGapActive(idx, toothCount) ? 1 : 0);
      if (numStrips === 0) {
        this.segLog(
          'papilla',
          JawSegmentation.SEG_STYLE.papilla,
          `tooth ${idx} has no active interdental gap — skipping papilla search`,
        );
        searchesByTooth[idx] = [];
        continue;
      }
      searchesByTooth[idx] = this.runPapillaSearchesForTooth(teethMeshes, idx, gumToSnap, numStrips);
    }

    if (getDevRuntime().segmentation.sceneLayers.papilla) {
      this.visualizePapillaSearches(teethMeshes, searchesByTooth);
    }

    const candidatesByTooth = searchesByTooth.map((searches) =>
      this.collectPapillaAnchorCandidates(searches),
    );
    const jawCentroid = this.computeJawArchCentroidWorld(teethMeshes);

    this.segGroupStart('vest-ling · jaw arch centroid', JawSegmentation.SEG_STYLE.vestLing);
    console.log('centroid (world)', {
      x: +jawCentroid.x.toFixed(4),
      y: +jawCentroid.y.toFixed(4),
      z: +jawCentroid.z.toFixed(4),
    });
    this.segTable(
      'per-tooth centroids',
      teethMeshes.map((m, i) => {
        const w = m.localToWorld(m.getCentroid().clone());
        return {
          tooth: i,
          x: +w.x.toFixed(4),
          y: +w.y.toFixed(4),
          z: +w.z.toFixed(4),
          distToJaw: +w.distanceTo(jawCentroid).toFixed(4),
        };
      }),
    );
    this.segGroupEnd();

    const partsByTooth = candidatesByTooth.map((cs, idx) =>
      this.partitionToothCandidatesByGap(cs, idx, teethMeshes, jawCentroid, {
        mesial: this.isInterdentalGapActive(idx - 1, toothCount),
        distal: this.isInterdentalGapActive(idx, toothCount),
      }),
    );

    for (let ti = 0; ti < teethMeshes.length; ti++) {
      const part = partsByTooth[ti];
      const bucketDetail = (arr: Array<{ gumVertex: Vertex; toothVertex: Vertex }>) =>
        arr.map((c) => ({
          toothId: c.toothVertex.id,
          gumId: c.gumVertex.id,
          distToJawArchXZ: +Math.sqrt(this.gumDistanceXZSquaredToWorld(c.gumVertex, jawCentroid)).toFixed(4),
        }));
      this.segGroupStart(`partition · tooth ${ti}`, JawSegmentation.SEG_STYLE.papilla);
      console.log('counts', {
        raw: candidatesByTooth[ti].length,
        pass: candidatesByTooth[ti].map((c) => c.passIndex),
        vestMesial: part.vestMesial.length,
        lingMesial: part.lingMesial.length,
        vestDistal: part.vestDistal.length,
        lingDistal: part.lingDistal.length,
      });
      if (part.vestMesial.length) this.segTable('vestMesial', bucketDetail(part.vestMesial));
      if (part.lingMesial.length) this.segTable('lingMesial', bucketDetail(part.lingMesial));
      if (part.vestDistal.length) this.segTable('vestDistal', bucketDetail(part.vestDistal));
      if (part.lingDistal.length) this.segTable('lingDistal', bucketDetail(part.lingDistal));
      this.segGroupEnd();
    }

    this.segGroupStart('gap pairing (front → back)', JawSegmentation.SEG_STYLE.pair);

    let vestRef: Vector3 | null = null;
    let lingRef: Vector3 | null = null;

    for (let i = 0; i < teethMeshes.length - 1; i++) {
      if (!this.isInterdentalGapActive(i, toothCount)) {
        this.segLog(
          'pair',
          JawSegmentation.SEG_STYLE.pair,
          `gap [${i}-${i + 1}] inactive (no corridor or missing tooth) — no anchors`,
        );
        continue;
      }
      const A = partsByTooth[i];
      const B = partsByTooth[i + 1];
      const candA = candidatesByTooth[i];
      const candB = candidatesByTooth[i + 1];
      if (candA.length === 0 || candB.length === 0) {
        const emptyTooth = candA.length === 0 ? i : i + 1;
        this.segWarn('pair', `gap [${i}-${i + 1}] tooth ${emptyTooth} has no papilla candidates`);
        continue;
      }

      const hasDistal = A.vestDistal.length + A.lingDistal.length > 0;
      const hasMesial = B.vestMesial.length + B.lingMesial.length > 0;
      if (!hasDistal || !hasMesial) {
        this.segWarn('pair', `gap [${i}-${i + 1}] no distal/mesial contact candidates`);
        continue;
      }

      const pairingMode: 'bucket-first' | 'chained-from-previous-gap' =
        i === 0 ? 'bucket-first' : 'chained-from-previous-gap';

      const paired =
        i === 0
          ? this.pairGapAnchorsFromBuckets(A, B)
          : vestRef && lingRef
            ? this.pairGapAnchorsChainedFromBuckets(A, B, vestRef, lingRef)
            : null;

      if (!paired) {
        this.segWarn(
          'pair',
          `gap [${i}-${i + 1}] could not pair — vestDistal=${A.vestDistal.length} lingDistal=${A.lingDistal.length} ` +
            `vestMesial=${B.vestMesial.length} lingMesial=${B.lingMesial.length} mode=${pairingMode}`,
        );
        continue;
      }

      const { vestPair: rawVest, lingPair: rawLing } = paired;
      const { vestPair, lingPair, swapped } = this.correctVestLingSwapIfNeeded(rawVest, rawLing, jawCentroid);
      if (swapped && this.papillaSearchDebug) {
        this.segLog('pair', JawSegmentation.SEG_STYLE.pair, `gap [${i}-${i + 1}] corrected vest/ling swap (jaw arch XZ)`);
      }
      const prevVestRef = vestRef;
      const prevLingRef = lingRef;
      vestRef = this.gumPairMidpointWorld(vestPair.a.gumVertex, vestPair.b.gumVertex);
      lingRef = this.gumPairMidpointWorld(lingPair.a.gumVertex, lingPair.b.gumVertex);

      result.set(i, {
        vestibular: {
          toothAVertex: vestPair.a.toothVertex,
          toothBVertex: vestPair.b.toothVertex,
          gumAVertex: vestPair.a.gumVertex,
          gumBVertex: vestPair.b.gumVertex,
        },
        lingual: {
          toothAVertex: lingPair.a.toothVertex,
          toothBVertex: lingPair.b.toothVertex,
          gumAVertex: lingPair.a.gumVertex,
          gumBVertex: lingPair.b.gumVertex,
        },
      });
      const gumDist = (a: Vertex, b: Vertex) =>
        Math.sqrt(
          this.localToWorld(a.position.clone()).distanceToSquared(this.localToWorld(b.position.clone())),
        );
      this.segGroupStart(`gap [${i}-${i + 1}] paired · ${pairingMode}`, JawSegmentation.SEG_STYLE.pair);
      this.logGapVestLingDiagnostics(
        i,
        teethMeshes,
        { vestDistal: A.vestDistal, lingDistal: A.lingDistal },
        { vestMesial: B.vestMesial, lingMesial: B.lingMesial },
        vestPair,
        lingPair,
        pairingMode,
        prevVestRef,
        prevLingRef,
      );
      this.segTable('chosen anchors', [
        {
          side: 'vestibular',
          toothA: vestPair.a.toothVertex.id,
          toothB: vestPair.b.toothVertex.id,
          gumDist: +gumDist(vestPair.a.gumVertex, vestPair.b.gumVertex).toFixed(4),
          dJawArchXZ: +Math.sqrt(
            (this.gumDistanceXZSquaredToWorld(vestPair.a.gumVertex, jawCentroid) +
              this.gumDistanceXZSquaredToWorld(vestPair.b.gumVertex, jawCentroid)) *
              0.5,
          ).toFixed(4),
        },
        {
          side: 'lingual',
          toothA: lingPair.a.toothVertex.id,
          toothB: lingPair.b.toothVertex.id,
          gumDist: +gumDist(lingPair.a.gumVertex, lingPair.b.gumVertex).toFixed(4),
          dJawArchXZ: +Math.sqrt(
            (this.gumDistanceXZSquaredToWorld(lingPair.a.gumVertex, jawCentroid) +
              this.gumDistanceXZSquaredToWorld(lingPair.b.gumVertex, jawCentroid)) *
              0.5,
          ).toFixed(4),
        },
      ]);
      this.segGroupEnd();
    }

    this.segGroupEnd();
    this.segGroupEnd();

    return result;
  }
  
  /** Removes prior debug meshes whose names start with `prefix` (safe while traversing). */
  private removeSceneObjectsByNamePrefix(prefix: string): void {
    const toRemove: Object3D[] = [];
    this.rootState.scene.traverse((obj) => {
      if (obj.name.startsWith(prefix)) {
        toRemove.push(obj);
      }
    });
    for (const o of toRemove) {
      o.parent?.remove(o);
    }
  }

  private removeSceneObjectsByNamePrefixes(prefixes: string[]): void {
    const toRemove: Object3D[] = [];
    this.rootState.scene.traverse((obj) => {
      if (prefixes.some((prefix) => obj.name.startsWith(prefix))) {
        toRemove.push(obj);
      }
    });
    for (const o of toRemove) {
      o.parent?.remove(o);
    }
  }

  private registerSegmentationDebugSceneCleaners(): void {
    const runtime = getDevRuntime();
    runtime.setSegmentationSceneCleaner('papilla', () => {
      this.removeSceneObjectsByNamePrefix('debug-papilla-');
    });
    runtime.setSegmentationSceneCleaner('snappedGum', () => {
      this.removeSceneObjectsByNamePrefix('debug-snapped-gum-');
    });
    runtime.setSegmentationSceneCleaner('fillArcs', () => {
      this.removeSceneObjectsByNamePrefixes(['debug-tooth']);
    });
    runtime.setSegmentationSceneCleaner('toothBorders', () => {
      this.removeSceneObjectsByNamePrefix('debug-seg-tooth-border-');
    });
  }

  /**
   * Debug: small black spheres on every border vertex of segmented teeth.
   */
  private visualizeAllToothBorderVertices(teethMeshes: HalfEdgeMesh[]): void {
    this.removeSceneObjectsByNamePrefix('debug-seg-tooth-border-');
    if (teethMeshes.length === 0) return;

    const radius = 0.08;
    const geom = new SphereGeometry(radius, 6, 6);
    const mat = new MeshBasicMaterial({ color: 0x000000 });
    let markerIndex = 0;

    teethMeshes.forEach((tooth, toothIndex) => {
      tooth.calculateBorderVertices();
      for (const vertex of tooth.getBorderVertices()) {
        const mesh = new Mesh(geom, mat);
        mesh.position.copy(tooth.localToWorld(vertex.position.clone()));
        mesh.name = `debug-seg-tooth-border-${toothIndex}-${markerIndex++}`;
        this.rootState.scene.add(mesh);
      }
    });
  }

  /**
   * Debug: small black spheres at every gum boundary vertex that snapped to a tooth
   * (all keys of `gumToSnap`). Clears any previous `debug-snapped-gum-*` markers first.
   */
  private visualizeAllSnappedGumVertices(gumToSnap: Map<Vertex, SnapInfo>): void {
    this.removeSceneObjectsByNamePrefix('debug-snapped-gum-');
    const r = 0.075;
    const geom = new SphereGeometry(r, 6, 6);
    const mat = new MeshBasicMaterial({ color: 0x111111 });
    let k = 0;
    for (const gumVertex of gumToSnap.keys()) {
      const w = this.localToWorld(gumVertex.position.clone());
      const m = new Mesh(geom, mat);
      m.position.copy(w);
      m.name = `debug-snapped-gum-${k++}`;
      this.rootState.scene.add(m);
    }
  }

  /**
   * Finds a specific side-fill arc without crossing the opposite interdental side.
   */
  private findArcBetweenAnchorsAvoiding(
    start: Vertex,
    target: Vertex,
    blockedAnchors: Array<Vertex | null>,
    debugTag: string,
  ): Vertex[] | null {
    const blocked = new Set(blockedAnchors.filter((v): v is Vertex => !!v));
    const path1 = this.walkBoundaryBetweenVertices(start, target, 0, blocked);
    const path2 = this.walkBoundaryBetweenVertices(start, target, 1, blocked);

    if (!path1 && !path2) {
      console.warn(`findArcBetweenAnchorsAvoiding [${debugTag}]: no path from ${start.id} to ${target.id}`);
      return null;
    }
    if (!path1) return path2;
    if (!path2) return path1;

    const chosen = path1.length <= path2.length ? path1 : path2;
    console.log(
      `findArcBetweenAnchorsAvoiding [${debugTag}]: chose ${chosen.length} verts ` +
        `(dir0=${path1.length}, dir1=${path2.length})`,
    );
    return chosen;
  }

  /**
   * For edge teeth: walks from start to target in both directions,
   * returns the shorter path (since edge teeth have one side exposed).
   */
  private walkBoundaryToTarget(
    start: Vertex,
    target: Vertex,
  ): Vertex[] | null {
    // Try both directions
    const path1 = this.walkBoundaryBetweenVertices(start, target, 0);
    const path2 = this.walkBoundaryBetweenVertices(start, target, 1);
    
    if (!path1 && !path2) return null;
    if (!path1) return path2;
    if (!path2) return path1;
    
    // Return the shorter path (the exposed edge of the tooth)
    return path1.length <= path2.length ? path1 : path2;
  }
  
  /**
   * Walks along boundary vertices from start to end using getAdjacenceBoundaryVertices.
   * Tries both possible directions from start (each boundary vertex typically has 2 adjacent boundary vertices).
   * @param start Starting vertex
   * @param end Ending vertex
   * @param directionIndex 0 for first adjacent vertex, 1 for second adjacent vertex
   * @returns Array of vertices from start to end (inclusive), or null if end not reached
   */
  private walkBoundaryBetweenVertices(
    start: Vertex,
    end: Vertex,
    directionIndex: 0 | 1,
    blockedAnchors: Set<Vertex> = new Set(),
  ): Vertex[] | null {
    const path: Vertex[] = [start];
    const visited = new Set<Vertex>([start]);
    const maxIterations = 10000;
    
    // Get initial direction from start vertex
    const startNeighbors = start.getAdjacenceBoundaryVertices();
    if (startNeighbors.length === 0) {
      return null;
    }
    
    // Pick the neighbor based on directionIndex
    let nextVertex: Vertex | null = startNeighbors[directionIndex % startNeighbors.length] || null;
    if (!nextVertex) {
      return null;
    }
    
    // Check if we immediately found end
    if (nextVertex === end) {
      path.push(end);
      return path;
    }
    if (blockedAnchors.has(nextVertex)) {
      return null;
    }
    
    visited.add(nextVertex);
    path.push(nextVertex);
    let current = nextVertex;
    
    for (let i = 0; i < maxIterations; i++) {
      // Get adjacent boundary vertices
      const neighbors = current.getAdjacenceBoundaryVertices();
      
      // Find the next unvisited neighbor (continuing in the same direction)
      nextVertex = null;
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          nextVertex = neighbor;
          break;
        }
      }
      
      if (!nextVertex) {
        // No unvisited neighbors - dead end
        return null;
      }
      
      if (nextVertex === end) {
        path.push(end);
        return path;
      }
      if (blockedAnchors.has(nextVertex)) {
        return null;
      }
      
      visited.add(nextVertex);
      path.push(nextVertex);
      current = nextVertex;
    }
    
    console.warn(`walkBoundaryBetweenVertices: exceeded max iterations`);
    return null;
  }


  

 

  /**
   * Creates the interdental gum bridge between two teeth.
   * Simply connects the 4 gum vertices (vest/ling from each tooth) to form a quad.
   */


  /**
   * True when both vertices lie on the SAME gum boundary loop (walk the free
   * halfedge cycle from `a`; boundary halfedges chain via `next`). Separate
   * loops at an interdental gap mean the scan papilla still separates the two
   * sockets — the gap needs no bridge.
   */
  private onSameGumBoundaryLoop(a: Vertex, b: Vertex): boolean {
    if (a === b) return true;
    for (const start of a.boundaryHalfedgesOutLoop()) {
      let halfedge = start;
      for (let step = 0; step < 20000; step++) {
        if (halfedge.vertex === b) return true;
        halfedge = halfedge.next;
        if (halfedge === start || halfedge.face !== null) break;
      }
    }
    return false;
  }


  private createInterdentalGumBridge(
    interdentalBoundarySides: InterdentalBoundarySides,
    tooth1: HalfEdgeMesh,
    tooth2: HalfEdgeMesh,
    tooth1Index: number,
    tooth2Index: number,
  ): void {
    // Get the 4 gum anchor vertices
    const vest1 = interdentalBoundarySides.vestibular.startVertex; // 🟠 tooth1 vest
    const ling1 = interdentalBoundarySides.lingual.startVertex;    // 🩵 tooth1 ling
    let vest2 = interdentalBoundarySides.vestibular.endVertex;     // 🔴 tooth2 vest
    let ling2 = interdentalBoundarySides.lingual.endVertex;        // 🔵 tooth2 ling

    console.log(`createInterdentalGumBridge [${tooth1Index}, ${tooth2Index}]: connecting 4 gum vertices`);
    this.debugLogBridgeAnchors(tooth1Index, tooth2Index, 'pre-collapse', { vest1, ling1, vest2, ling2 });

    // Correct a per-tooth vest/ling swap. The 4 anchors must regroup so the two
    // vestibular anchors meet at the buccal papilla and the two lingual anchors at
    // the lingual papilla (small v1·v2 and l1·l2). If instead vest1 sits next to
    // ling2 and ling1 next to vest2 (one tooth's labels flipped relative to the
    // other — which correctVestLingSwapIfNeeded can't catch, as it flips both
    // teeth together), the bridge would stitch across the diagonal and tear a hole.
    // Detect by distance and swap tooth 2's labels to regroup.
    const wpos = (v: Vertex) => this.localToWorld(v.position.clone());
    const groupedCost =
      wpos(vest1).distanceTo(wpos(vest2)) + wpos(ling1).distanceTo(wpos(ling2));
    const crossedCost =
      wpos(vest1).distanceTo(wpos(ling2)) + wpos(ling1).distanceTo(wpos(vest2));
    if (crossedCost < groupedCost) {
      console.warn(
        `createInterdentalGumBridge [${tooth1Index}, ${tooth2Index}]: cross-paired anchors detected ` +
          `(grouped=${groupedCost.toFixed(2)} > crossed=${crossedCost.toFixed(2)}) — swapping tooth ${tooth2Index} vest/ling`,
      );
      [vest2, ling2] = [ling2, vest2];
    }

    // INTACT PAPILLA — nothing to bridge. The corridor machinery exists for
    // gaps whose interdental gum is MISSING, i.e. the two socket holes merged
    // into ONE boundary loop. When the anchors sit on SEPARATE loops the scan
    // papilla is still there — every historical fallback then stretched its
    // quad straight THROUGH the healthy gum (the flat band the walls stab
    // through in the export). Skip the gap entirely instead.
    if (
      !this.onSameGumBoundaryLoop(vest1, vest2) ||
      !this.onSameGumBoundaryLoop(ling1, ling2)
    ) {
      console.log(
        `createInterdentalGumBridge [${tooth1Index}, ${tooth2Index}]: anchors on separate ` +
          `boundary loops — papilla intact, no bridge needed`,
      );
      return;
    }

    // The gum bridge is the historical stitch and stays that way: collapse
    // the papilla neck onto the four anchors and close the mouth with a
    // single quad. Like the tooth walls, this is DISPLAY geometry — the
    // export deletes it and rebuilds the interdental space between the posed
    // crowns — so it only has to close the hole, not model a papilla.
    {

      // FALLBACK — the historical stitch: collapse the neck onto the anchors
      // and close the mouth with a single quad. Each side must AVOID the
      // opposite side's anchors: the interdental gap is a single boundary
      // loop carrying all 4 anchors, so the shortest boundary path between
      // the two vestibular anchors can otherwise run straight through the
      // lingual anchors (and vice versa) — collapsing it would merge
      // ling1→vest1 / ling2→vest2 and destroy that pair.
      this.collapseVerticesBetweenAnchors(vest1, vest2, [ling1, ling2], tooth1Index, tooth2Index, 'vest');
      this.collapseVerticesBetweenAnchors(ling1, ling2, [vest1, vest2], tooth1Index, tooth2Index, 'ling');

      this.debugLogBridgeAnchors(tooth1Index, tooth2Index, 'post-collapse', { vest1, ling1, vest2, ling2 });

      // Now bridge the 4 anchor points
      // Path 1: vest1 → ling1 (tooth1 side)
      // Path 2: vest2 → ling2 (tooth2 side)
      const path1 = [vest1, ling1];
      const path2 = [vest2, ling2];

      // Connect vertices in each path (if not already connected)
      this.halfEdges.connectVertices(path1);
      this.halfEdges.connectVertices(path2);

      // Bridge the two paths to create faces between them
      const facesBefore = this.halfEdges.faces.length;
      this.halfEdges.bridgeSegments(path1, path2);
      const facesCreated = this.markGeneratedFacesFrom(facesBefore);

      if (facesCreated < 2) {
        console.warn(
          `createInterdentalGumBridge [${tooth1Index}, ${tooth2Index}]: bridge created only ${facesCreated}/2 faces — ` +
            `createFace rejected a degenerate/coincident triangle (this is the hole)`,
        );
      }

      console.log(
        `createInterdentalGumBridge [${tooth1Index}, ${tooth2Index}]: gum bridge completed ` +
          `(${facesCreated} face(s) created, gum generated faces from index ` +
          `${this.getGeneratedFacesFrom()} of ${this.halfEdges.faces.length})`,
      );
    }
    this.calculateBorderVertices();
  }

  /**
   * Debug: dumps the 4 interdental gum-bridge anchors — identity collisions,
   * world distances, boundary status, and the area2 degeneracy test that
   * `createFace` actually uses (area2 <= 1e-12 → face silently rejected →
   * hole). Also drops colored spheres in the scene: vest1 orange, ling1 cyan,
   * vest2 red, ling2 blue.
   */
  private debugLogBridgeAnchors(
    tooth1Index: number,
    tooth2Index: number,
    stage: string,
    a: { vest1: Vertex; ling1: Vertex; vest2: Vertex; ling2: Vertex },
  ): void {
    const tag = `[${tooth1Index}-${tooth2Index}] ${stage}`;
    const w = (v: Vertex) => this.localToWorld(v.position.clone());
    const area2 = (p: Vertex, q: Vertex, r: Vertex) =>
      new Vector3().subVectors(q.position, p.position).cross(new Vector3().subVectors(r.position, p.position)).length();
    const bnd = (v: Vertex) => v.getAdjacenceBoundaryVertices().length;

    const ids = { vest1: a.vest1.id, ling1: a.ling1.id, vest2: a.vest2.id, ling2: a.ling2.id };
    const uniqueIds = new Set(Object.values(ids));
    if (uniqueIds.size < 4) {
      console.warn(`debugBridgeAnchors ${tag}: ONLY ${uniqueIds.size}/4 distinct anchors — coincident!`, ids);
    }

    const d = (x: Vertex, y: Vertex) => w(x).distanceTo(w(y)).toFixed(2);
    // Flat strings so the values are visible in a pasted console log (objects collapse to {…}).
    console.log(
      `debugBridgeAnchors ${tag} | ids v1=${ids.vest1} l1=${ids.ling1} v2=${ids.vest2} l2=${ids.ling2} | ` +
        `bndDeg v1=${bnd(a.vest1)} l1=${bnd(a.ling1)} v2=${bnd(a.vest2)} l2=${bnd(a.ling2)} | ` +
        `dist v1·v2=${d(a.vest1, a.vest2)} l1·l2=${d(a.ling1, a.ling2)} v1·l1=${d(a.vest1, a.ling1)} v2·l2=${d(a.vest2, a.ling2)} | ` +
        `area2 t1=${area2(a.vest1, a.ling1, a.ling2).toExponential(2)} t2=${area2(a.vest1, a.ling2, a.vest2).toExponential(2)}`,
    );
    console.log(`debugBridgeAnchors ${tag} | boundary-loop order from vest1: ${this.debugBoundaryLoopOrder(a)}`);

    if (stage === 'post-collapse') {
      const spheres: Array<[Vertex, number]> = [
        [a.vest1, 0xff8800],
        [a.ling1, 0x00e5ff],
        [a.vest2, 0xff0000],
        [a.ling2, 0x0000ff],
      ];
      this.removeSceneObjectsByNamePrefix(`debug-gum-bridge-${tooth1Index}-`);
      spheres.forEach(([v, color], k) => {
        const m = new Mesh(new SphereGeometry(0.35, 10, 10), new MeshBasicMaterial({ color }));
        m.position.copy(w(v));
        m.name = `debug-gum-bridge-${tooth1Index}-${k}`;
        this.rootState.scene.add(m);
      });
    }
  }

  /**
   * Debug: walks the gum boundary loop starting at vest1 in one consistent
   * direction and reports the cyclic position at which each anchor appears.
   * - grouped (correct): v1@0 … v2 … l2 … l1   → the quad closes cleanly
   * - alternating (bad): v1@0 … l1 … v2 … l2   → mislabeled side, twisted bridge
   * - separate loops:    some anchors never appear → "NOT-ON-LOOP"
   */
  private debugBoundaryLoopOrder(a: { vest1: Vertex; ling1: Vertex; vest2: Vertex; ling2: Vertex }): string {
    const marks = new Map<Vertex, string>([
      [a.vest1, 'v1'],
      [a.ling1, 'l1'],
      [a.vest2, 'v2'],
      [a.ling2, 'l2'],
    ]);
    const found: string[] = [];
    const visited = new Set<Vertex>();
    let prev: Vertex | null = null;
    let cur: Vertex | null = a.vest1;

    for (let i = 0; i < 5000 && cur && !visited.has(cur); i++) {
      visited.add(cur);
      const mark = marks.get(cur);
      if (mark) found.push(`${mark}@${i}`);
      const neighbors = cur.getAdjacenceBoundaryVertices();
      // Continue along the loop: prefer an unvisited neighbor that isn't where we came from.
      const next: Vertex | null =
        neighbors.find((n) => n !== prev && !visited.has(n)) ?? neighbors.find((n) => n !== prev) ?? null;
      prev = cur;
      cur = next;
    }

    const missing = [...marks.values()].filter((m) => !found.some((f) => f.startsWith(`${m}@`)));
    const tail = missing.length ? ` | NOT-ON-LOOP: ${missing.join(',')}` : '';
    return `loopLen=${visited.size} order=[${found.join(' ')}]${tail}`;
  }

  /**
   * Collapses intermediate vertices between two anchor points on the gum boundary.
   * Vertices are collapsed onto the nearest anchor (first half → anchor1, second half → anchor2).
   */
  private collapseVerticesBetweenAnchors(
    anchor1: Vertex,
    anchor2: Vertex,
    blockedAnchors: Vertex[],
    tooth1Index: number,
    tooth2Index: number,
    side: string,
  ): void {
    // Find the shortest boundary path between the anchors, never routing through
    // the opposite side's anchors.
    const blocked = new Set(blockedAnchors);
    const path = this.findGumBoundaryPathBetweenAnchors(anchor1, anchor2, blocked);

    if (!path) {
      // No boundary path avoiding the opposite anchors — anchors may be on
      // different boundary loops, or the only connection runs through the
      // blocked pair. Nothing to collapse, but the rim is NOT closed.
      console.warn(
        `collapseVerticesBetweenAnchors [${tooth1Index}-${tooth2Index}] ${side}: ` +
          `NO boundary path between anchors ${anchor1.id}↔${anchor2.id} (avoiding opposite pair)`,
      );
      return;
    }
    if (path.length <= 2) {
      // Anchors are directly adjacent on the boundary - nothing between them.
      console.log(
        `collapseVerticesBetweenAnchors [${tooth1Index}-${tooth2Index}] ${side}: ` +
          `anchors adjacent (${anchor1.id}↔${anchor2.id}), no intermediate vertices`,
      );
      return;
    }

    // Runaway-path guard: the anchor pair is chosen by SPATIAL proximity
    // (findClosestSnapPair), but this walk measures BOUNDARY-loop distance. When
    // the two disagree — e.g. the boundary detours all the way around an edge
    // tooth's hole — the path is hundreds of verts long and these anchors are NOT
    // a thin interdental papilla neck. Collapsing then merges a huge swath of gum
    // onto the anchors and wrecks the bridge. Bail out and let bridgeSegments join
    // the (still spatially-close) anchors directly.
    const intermediateCount = path.length - 2;
    if (intermediateCount > JawSegmentation.MAX_PAPILLA_COLLAPSE_PATH) {
      console.warn(
        `collapseVerticesBetweenAnchors [${tooth1Index}-${tooth2Index}] ${side}: ` +
          `path of ${path.length} verts (${intermediateCount} intermediate) is not a papilla neck — ` +
          `skipping collapse to avoid destroying gum`,
      );
      return;
    }

    console.log(`collapseVerticesBetweenAnchors [${tooth1Index}-${tooth2Index}] ${side}: collapsing ${intermediateCount} vertices`);
    
    let collapsed = 0;
    const midIndex = Math.floor(path.length / 2);
    
    const isProtected = (v: Vertex | undefined): boolean =>
      !v || v === anchor1 || v === anchor2 || blocked.has(v);

    // First half: collapse onto anchor1
    for (let i = 1; i < midIndex; i++) {
      const vertex = path[i];
      if (!isProtected(vertex)) {
        try {
          const success = this.halfEdges.edgeCollapse(vertex, anchor1, false);
          if (success) collapsed++;
        } catch (e) {
          // Collapse might fail
        }
      }
    }

    // Second half: collapse onto anchor2
    for (let i = path.length - 2; i >= midIndex; i--) {
      const vertex = path[i];
      if (!isProtected(vertex)) {
        try {
          const success = this.halfEdges.edgeCollapse(vertex, anchor2, false);
          if (success) collapsed++;
        } catch (e) {
          // Collapse might fail
        }
      }
    }
    
    console.log(`collapseVerticesBetweenAnchors [${tooth1Index}-${tooth2Index}] ${side}: collapsed ${collapsed} vertices`);
    
    // Recalculate border after collapse
    this.calculateBorderVertices();
  }
  
  /**
   * Finds the genuinely shortest path between two anchor vertices on the gum
   * boundary, via breadth-first search over the boundary-vertex graph.
   *
   * The old two-direction greedy walk only ever tried boundary-neighbor index
   * 0 and 1, then followed the first unvisited neighbor. At a pinch vertex —
   * where two adjacent tooth holes meet and a vertex has >2 boundary neighbors —
   * it could miss the short interdental crossing and detour all the way around a
   * tooth hole (the 168/170-vertex "paths" seen at gap [0-1]). BFS explores every
   * boundary neighbor, so it returns the true shortest neck when one exists.
   */
  private findGumBoundaryPathBetweenAnchors(
    anchor1: Vertex,
    anchor2: Vertex,
    blocked: Set<Vertex> = new Set(),
  ): Vertex[] | null {
    if (anchor1 === anchor2) return [anchor1];

    const cameFrom = new Map<Vertex, Vertex | null>([[anchor1, null]]);
    const queue: Vertex[] = [anchor1];
    let reached = false;

    while (queue.length > 0) {
      const current = queue.shift() as Vertex;
      if (current === anchor2) {
        reached = true;
        break;
      }
      for (const neighbor of current.getAdjacenceBoundaryVertices()) {
        // Never route through a blocked anchor (the opposite side's pair): the
        // path must reach anchor2 along its own rim, not by cutting across.
        if (neighbor !== anchor2 && blocked.has(neighbor)) continue;
        if (!cameFrom.has(neighbor)) {
          cameFrom.set(neighbor, current);
          queue.push(neighbor);
        }
      }
    }

    if (!reached) return null;

    // Reconstruct anchor1 → anchor2.
    const path: Vertex[] = [];
    for (let v: Vertex | null = anchor2; v !== null; v = cameFrom.get(v) ?? null) {
      path.push(v);
    }
    path.reverse();
    return path;
  }











  public clearAnchors() {
    this.anchorCollection.removeAll();
    this.toothBoundaryCurves.forEach((line) => {
      line.dispose();
    });
    this.toothBoundaryCurves = [];
  }

  public removeLastAnchor() {
    this.anchorCollection.removeLast();
    const lastCurve = this.toothBoundaryCurves.pop();
    lastCurve?.dispose();
  }

  /** Dev-only: export anchors + edited tooth boundary curves for fast reload. */
  public exportDevSnapshot(scanId: string | null, jaw: 'upper' | 'lower'): SegmentationJawDevSnapshot {
    const { startStop, endStop } = teethSelectionStore.getState();
    const teeth: SegmentationToothDevSnapshot[] = [];

    for (let toothIndex = 0; toothIndex < this.toothBoundaryCurves.length; toothIndex++) {
      const anchor = this.anchorCollection[toothIndex];
      const spline = this.toothBoundaryCurves[toothIndex];
      if (!anchor || !spline) continue;

      const renderId = this.halfEdges.vertexToRenderId.get(anchor.intersectVertex) ?? null;
      const controlPoints: Vec3Snapshot[] = [];
      spline.linePoints.forEach((point) => {
        controlPoints.push(vec3ToSnapshot(point.position));
      });

      const snapPartners: SegmentationSnapPartnerSnapshot[] = [];
      spline.linePoints.forEach((point, localIndex) => {
        const partner = point.getSnappedPoint();
        if (!partner) return;
        const partnerSpline = partner.getInteractiveSpline();
        const partnerToothIndex = this.toothBoundaryCurves.indexOf(partnerSpline);
        if (partnerToothIndex < 0 || partnerToothIndex < toothIndex) return;
        snapPartners.push({
          localIndex,
          partnerToothIndex,
          partnerLocalIndex: partnerSpline.indexOfControlPoint(partner),
        });
      });

      teeth.push({
        anchor: {
          position: vec3ToSnapshot(anchor.position),
          vertexRenderId: renderId,
          cameraPosition: vec3ToSnapshot(anchor.cameraPosition),
        },
        boundaryCurve: { controlPoints, snapPartners },
      });
    }

    return {
      version: SEGMENTATION_DEV_SNAPSHOT_VERSION,
      scanId,
      jaw,
      startStop,
      endStop,
      missingAfter: Array.from(this.missingAfterAnchor),
      teeth,
    };
  }

  /** Dev-only: restore anchors + edited boundary curves without re-clicking teeth. */
  public importDevSnapshot(snapshot: SegmentationJawDevSnapshot): void {
    if (snapshot.version !== SEGMENTATION_DEV_SNAPSHOT_VERSION) {
      throw new Error(`Unsupported snapshot version: ${snapshot.version}`);
    }
    if (snapshot.teeth.length === 0) {
      throw new Error('Snapshot has no teeth');
    }

    this.clearAnchors();
    this.setMissingAfter(snapshot.missingAfter);
    teethSelectionStore.getState().setStartStop(snapshot.startStop);
    teethSelectionStore.getState().setEndStop(snapshot.endStop);

    for (const tooth of snapshot.teeth) {
      const vertex = this.resolveVertexForDevSnapshot(tooth.anchor);
      const anchorPos = new Vector3(tooth.anchor.position.x, tooth.anchor.position.y, tooth.anchor.position.z);
      const cameraPos = tooth.anchor.cameraPosition
        ? new Vector3(
            tooth.anchor.cameraPosition.x,
            tooth.anchor.cameraPosition.y,
            tooth.anchor.cameraPosition.z,
          )
        : anchorPos.clone().add(new Vector3(0, 0, 100));

      this.anchorCollection.addAnchor(anchorPos, vertex, cameraPos);

      const spline = new InteractiveSpline([], this);
      spline.addToScene();
      const points = tooth.boundaryCurve.controlPoints.map(
        (p) => new Vector3(p.x, p.y, p.z),
      );
      if (points.length >= 2) {
        spline.createLine(points, { preordered: true, preserveAllPoints: true });
      }
      spline.setCanInteract(true);
      this.toothBoundaryCurves.push(spline);
    }

    this.isAnchorInsertionEnabled = false;

    for (let toothIndex = 0; toothIndex < snapshot.teeth.length; toothIndex++) {
      const tooth = snapshot.teeth[toothIndex];
      const splineA = this.toothBoundaryCurves[toothIndex];
      if (!splineA) continue;

      for (const snap of tooth.boundaryCurve.snapPartners) {
        const splineB = this.toothBoundaryCurves[snap.partnerToothIndex];
        if (!splineB) continue;

        const pointA = this.getSplineControlPointAtIndex(splineA, snap.localIndex);
        const pointB = this.getSplineControlPointAtIndex(splineB, snap.partnerLocalIndex);
        if (!pointA || !pointB) continue;

        pointA.setSnappedPoint(pointB);
        pointB.setSnappedPoint(pointA);
      }
    }

    this.toothBoundaryCurves.forEach((curve) => curve.updateLine());
  }

  private resolveVertexForDevSnapshot(anchor: SegmentationToothDevSnapshot['anchor']) {
    const world = new Vector3(anchor.position.x, anchor.position.y, anchor.position.z);
    const local = this.worldToLocal(world.clone());

    // The stored renderId is only a fast path: any DS rebuild since the
    // snapshot was saved (e.g. the gum-edit bridge) renumbers the buffer, so
    // an old id can silently resolve to a DIFFERENT vertex — and the tooth
    // BFS then grows from the wrong spot. Trust the id only when the vertex
    // still sits where the anchor was clicked; otherwise resolve by position.
    if (anchor.vertexRenderId != null) {
      const byRenderId = this.halfEdges.renderIdToVertex.get(anchor.vertexRenderId);
      if (byRenderId && byRenderId.position.distanceToSquared(local) < 2.25) return byRenderId;
    }

    let best: Vertex | null = null;
    let bestSq = Infinity;
    for (const v of this.halfEdges.vertices) {
      const d = v.position.distanceToSquared(local);
      if (d < bestSq) {
        bestSq = d;
        best = v;
      }
    }
    if (!best) {
      throw new Error('Could not resolve anchor vertex from snapshot');
    }
    return best;
  }

  private getSplineControlPointAtIndex(
    spline: InteractiveSpline,
    index: number,
  ): SplineControlPoint | null {
    let found: SplineControlPoint | null = null;
    spline.linePoints.forEach((point, i) => {
      if (i === index) found = point;
    });
    return found;
  }

  // ==================== GUM EDIT PHASE ====================
  // Pre-selection gum repair: bridge scan bays between two picked border
  // vertices (shortest border arc + advancing-front fill) and trim the gum
  // edge with a horizontal plane to a clean, aligned rim.

  private gumEditSelection: Vertex[] = [];
  private gumEditHoverMarker: Mesh | null = null;
  private gumEditSelectionMarkers: Mesh[] = [];

  private gumEditMarkerRadius(): number {
    const meanEdge = Math.max(this.halfEdges.getMeanEdgeLength?.() ?? 0.2, 1e-6);
    return Math.max(0.35, meanEdge * 2);
  }

  /** Recomputes border caches when the phase starts. */
  public prepareGumEdit(): void {
    this.calculateBorderVertices();
  }

  /** Nearest free-border vertex to a world-space point (visual snap target). */
  public findNearestBorderVertex(worldPoint: Vector3, maxDistanceMm = 3): Vertex | null {
    if (this.getBorderVertices().length === 0) this.calculateBorderVertices();
    const local = this.worldToLocal(worldPoint.clone());
    let best: Vertex | null = null;
    let bestSq = maxDistanceMm * maxDistanceMm;
    for (const v of this.getBorderVertices()) {
      const d = v.position.distanceToSquared(local);
      if (d < bestSq) {
        bestSq = d;
        best = v;
      }
    }
    return best;
  }

  private makeGumEditMarker(color: number): Mesh {
    const marker = new Mesh(
      new SphereGeometry(this.gumEditMarkerRadius(), 12, 12),
      new MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.9 }),
    );
    marker.renderOrder = 999;
    this.add(marker);
    return marker;
  }

  /** Shows/moves the hover snap marker (cyan) on a border vertex; null hides it. */
  public showGumEditHover(vertex: Vertex | null): void {
    if (!vertex) {
      if (this.gumEditHoverMarker) this.gumEditHoverMarker.visible = false;
      return;
    }
    if (!this.gumEditHoverMarker) this.gumEditHoverMarker = this.makeGumEditMarker(0x22d3ee);
    this.gumEditHoverMarker.visible = true;
    this.gumEditHoverMarker.position.copy(vertex.position);
  }

  /**
   * Toggles selection of a border vertex (max 2). Clicking a selected vertex
   * deselects it; a third pick is ignored. Returns the selection count.
   */
  public toggleGumEditSelection(vertex: Vertex): number {
    const idx = this.gumEditSelection.indexOf(vertex);
    if (idx >= 0) {
      this.gumEditSelection.splice(idx, 1);
    } else if (this.gumEditSelection.length < 2) {
      this.gumEditSelection.push(vertex);
    }
    this.refreshGumEditSelectionMarkers();
    return this.gumEditSelection.length;
  }

  public getGumEditSelectionCount(): number {
    return this.gumEditSelection.length;
  }

  public clearGumEditSelection(): void {
    this.gumEditSelection = [];
    this.refreshGumEditSelectionMarkers();
  }

  private refreshGumEditSelectionMarkers(): void {
    for (const m of this.gumEditSelectionMarkers) {
      this.remove(m);
      m.geometry.dispose();
      (m.material as MeshBasicMaterial).dispose();
    }
    this.gumEditSelectionMarkers = this.gumEditSelection.map((v) => {
      const marker = this.makeGumEditMarker(0xf97316);
      marker.position.copy(v.position);
      return marker;
    });
  }

  /** Removes every gum-edit visual (hover + selection markers). */
  public clearGumEditVisuals(): void {
    this.clearGumEditSelection();
    if (this.gumEditHoverMarker) {
      this.remove(this.gumEditHoverMarker);
      this.gumEditHoverMarker.geometry.dispose();
      (this.gumEditHoverMarker.material as MeshBasicMaterial).dispose();
      this.gumEditHoverMarker = null;
    }
  }

  /**
   * The shorter of the two arcs between `a` and `b` along their shared border
   * loop (by accumulated length). For a scan bay this is the bay coastline.
   */
  private shortestBorderArc(a: Vertex, b: Vertex): Vertex[] | null {
    const loops = this.getBorderLoops();
    const loop = loops.find((l) => l.includes(a) && l.includes(b));
    if (!loop) return null;
    const ia = loop.indexOf(a);
    const ib = loop.indexOf(b);
    const L = loop.length;

    const arcFrom = (start: number, end: number): Vertex[] => {
      const arc: Vertex[] = [];
      for (let i = start; ; i = (i + 1) % L) {
        arc.push(loop[i]);
        if (i === end) break;
      }
      return arc;
    };
    const arcLength = (arc: Vertex[]) => {
      let sum = 0;
      for (let i = 0; i < arc.length - 1; i++) sum += arc[i].position.distanceTo(arc[i + 1].position);
      return sum;
    };

    const arc1 = arcFrom(ia, ib);
    const arc2 = arcFrom(ib, ia);
    return arcLength(arc1) <= arcLength(arc2) ? arc1 : arc2;
  }

  /**
   * Bridges the bay between the two selected border vertices by zipping its
   * two shores together with a quad strip (deterministic, no new vertices)
   * and relaxing the former coastline. Returns the number of faces added, or
   * null when the selection/arc is unusable.
   */
  public bridgeSelectedGumGap(): number | null {
    if (this.gumEditSelection.length !== 2) return null;
    const [a, b] = this.gumEditSelection;
    const arc = this.shortestBorderArc(a, b);
    if (!arc || arc.length < 4) {
      console.warn('bridgeSelectedGumGap: arc too short or vertices not on one border loop');
      return null;
    }

    // zipBorderArc rebuilds the DS (every Vertex reference dies) — drop ours
    // first and have the segmentation stop-set (precomputed tooth feature
    // edges) remapped onto the new vertices, otherwise tooth selection breaks.
    this.clearGumEditSelection();
    const facesAdded = zipBorderArc(this, arc, 6, [this.teethEdges]);
    console.log(`bridgeSelectedGumGap: zipped bay of ${arc.length} border verts, +${facesAdded} faces`);
    return facesAdded;
  }

  // --- Arch-following gum trim (post-segmentation review step) ---
  // Geodesic collar: every gum vertex knows its SURFACE (Dijkstra) distance
  // to the nearest tooth seam ring, with bridge seeds between arch-adjacent
  // seams so a missing-tooth gap doesn't split the collar. At margin 0 the
  // cut hugs the tooth necks; growing the margin expands the kept collar
  // along the surface in all directions. Surface distance works on vertical
  // walls and under tilted crowns, where a top-down projected curve fails.

  private gumTrimDistances: Map<Vertex, number> | null = null;
  private gumTrimVertexGrid: Map<number, Vertex[]> | null = null;
  private gumTrimSavedColors: Float32Array | null = null;
  /** Half-width of the bright band marking the cut line in the preview (mm). */
  private static readonly GUM_TRIM_BAND = 0.3;
  /** Gum vertices within this 3D distance of a seam/bridge become Dijkstra seeds (mm). */
  private static readonly GUM_TRIM_SEED_RADIUS = 2;
  private static readonly GUM_TRIM_GRID_CELL = 2;

  private gumTrimGridKey(x: number, y: number, z: number): number {
    const cell = JawSegmentation.GUM_TRIM_GRID_CELL;
    return (
      (Math.floor(x / cell) + 1024) * 2048 * 2048 +
      (Math.floor(y / cell) + 1024) * 2048 +
      (Math.floor(z / cell) + 1024)
    );
  }

  /** One-ring neighbors via a bounded fan walk (corrupted fans can't hang it). */
  private gumTrimNeighbors(v: Vertex): Vertex[] {
    const out: Vertex[] = [];
    const start = v.halfedge;
    if (!start || start.vertex !== v) return out;
    let curr = start;
    for (let i = 0; i < 64; i++) {
      out.push(curr.twin.vertex);
      curr = curr.twin.next;
      if (curr === start) break;
    }
    return out;
  }

  /**
   * Computes the geodesic collar field from the segmented teeth. Call when
   * entering the review step (after segmentation). Returns the margin range
   * for the slider, or null when no teeth exist.
   */
  public prepareGumTrimByTeeth(): { min: number; max: number } | null {
    if (this.segmentedToothMeshes.length === 0) return null;

    // Seam ring of each tooth (the border loop where it was cut from the
    // gum) in gum-local space — the line the collar is measured from.
    const seams: Vector3[][] = this.segmentedToothMeshes.map((tooth) => {
      let seam: Vertex[] = [];
      for (const loop of tooth.getBorderLoops()) {
        if (loop.length > seam.length) seam = loop;
      }
      const verts = seam.length >= 8 ? seam : tooth.halfEdges.vertices;
      const stride = Math.max(1, Math.floor(verts.length / 400));
      const samples: Vector3[] = [];
      for (let i = 0; i < verts.length; i += stride) {
        samples.push(this.worldToLocal(tooth.localToWorld(verts[i].position.clone())));
      }
      return samples;
    });

    // Spatial hash of all seam samples for the seed pass.
    const seedRadius = JawSegmentation.GUM_TRIM_SEED_RADIUS;
    const seamGrid = new Map<number, Vector3[]>();
    for (const seam of seams) {
      for (const p of seam) {
        const key = this.gumTrimGridKey(p.x, p.y, p.z);
        let bucket = seamGrid.get(key);
        if (!bucket) seamGrid.set(key, (bucket = []));
        bucket.push(p);
      }
    }

    // Bridge segments between arch-consecutive seam centroids keep the
    // collar continuous across missing-tooth gaps.
    const centroids = seams.map((seam) => {
      const c = new Vector3();
      for (const p of seam) c.add(p);
      return c.divideScalar(Math.max(1, seam.length));
    });
    const bridges: Array<[Vector3, Vector3]> = consecutiveArchPairs(
      centroids.map((c): XZ => ({ x: c.x, z: c.z })),
    ).map(([a, b]) => [centroids[a], centroids[b]]);

    // Seed pass: distance to the nearest seam sample (grid-accelerated, one
    // cell ring covers the seed radius) or to a bridge segment.
    const cell = JawSegmentation.GUM_TRIM_GRID_CELL;
    const distances = new Map<Vertex, number>();
    const heapD: number[] = [];
    const heapV: Vertex[] = [];
    const heapPush = (d: number, v: Vertex) => {
      let i = heapD.length;
      heapD.push(d);
      heapV.push(v);
      while (i > 0) {
        const parent = (i - 1) >> 1;
        if (heapD[parent] <= heapD[i]) break;
        [heapD[parent], heapD[i]] = [heapD[i], heapD[parent]];
        [heapV[parent], heapV[i]] = [heapV[i], heapV[parent]];
        i = parent;
      }
    };
    const heapPop = (): [number, Vertex] | null => {
      if (heapD.length === 0) return null;
      const top: [number, Vertex] = [heapD[0], heapV[0]];
      const lastD = heapD.pop()!;
      const lastV = heapV.pop()!;
      if (heapD.length > 0) {
        heapD[0] = lastD;
        heapV[0] = lastV;
        let i = 0;
        for (;;) {
          const l = i * 2 + 1;
          const r = l + 1;
          let m = i;
          if (l < heapD.length && heapD[l] < heapD[m]) m = l;
          if (r < heapD.length && heapD[r] < heapD[m]) m = r;
          if (m === i) break;
          [heapD[m], heapD[i]] = [heapD[i], heapD[m]];
          [heapV[m], heapV[i]] = [heapV[i], heapV[m]];
          i = m;
        }
      }
      return top;
    };

    const segClosest = new Vector3();
    const segDir = new Vector3();
    const distToSegment = (p: Vector3, a: Vector3, b: Vector3): number => {
      segDir.subVectors(b, a);
      const lenSq = segDir.lengthSq();
      const t = lenSq > 1e-12 ? Math.min(1, Math.max(0, segClosest.subVectors(p, a).dot(segDir) / lenSq)) : 0;
      return segClosest.copy(a).addScaledVector(segDir, t).distanceTo(p);
    };

    for (const v of this.halfEdges.vertices) {
      const p = v.position;
      let best = Infinity;
      const cx = Math.floor(p.x / cell);
      const cy = Math.floor(p.y / cell);
      const cz = Math.floor(p.z / cell);
      for (let ix = cx - 1; ix <= cx + 1; ix++) {
        for (let iy = cy - 1; iy <= cy + 1; iy++) {
          for (let iz = cz - 1; iz <= cz + 1; iz++) {
            const bucket = seamGrid.get((ix + 1024) * 2048 * 2048 + (iy + 1024) * 2048 + (iz + 1024));
            if (!bucket) continue;
            for (const s of bucket) {
              const d = p.distanceTo(s);
              if (d < best) best = d;
            }
          }
        }
      }
      for (const [a, b] of bridges) {
        const d = distToSegment(p, a, b);
        if (d < best) best = d;
      }
      if (best <= seedRadius) {
        distances.set(v, best);
        heapPush(best, v);
      }
    }
    if (distances.size === 0) {
      console.warn('prepareGumTrimByTeeth: no gum vertices near the tooth seams — trim disabled');
      return null;
    }

    // Dijkstra over the gum surface (lazy-deletion heap).
    for (;;) {
      const top = heapPop();
      if (!top) break;
      const [d, v] = top;
      if (d !== distances.get(v)) continue;
      for (const n of this.gumTrimNeighbors(v)) {
        const nd = d + v.position.distanceTo(n.position);
        const prev = distances.get(n);
        if (prev === undefined || nd < prev) {
          distances.set(n, nd);
          heapPush(nd, n);
        }
      }
    }

    // Light field smoothing rounds the interdental scallops of the iso lines.
    for (let iter = 0; iter < 2; iter++) {
      const next = new Map<Vertex, number>();
      for (const [v, d] of distances) {
        const ns = this.gumTrimNeighbors(v);
        let sum = 0;
        let count = 0;
        for (const n of ns) {
          const nd = distances.get(n);
          if (nd !== undefined) {
            sum += nd;
            count++;
          }
        }
        if (count >= 2) next.set(v, d * 0.5 + (sum / count) * 0.5);
      }
      next.forEach((d, v) => distances.set(v, d));
    }

    // Unreachable islands (scan debris) are always beyond any margin.
    let max = 0;
    for (const d of distances.values()) {
      if (d > max) max = d;
    }
    for (const v of this.halfEdges.vertices) {
      if (!distances.has(v)) distances.set(v, Infinity);
    }
    this.gumTrimDistances = distances;

    // Vertex hash grid for drag-to-set-margin lookups.
    const vertexGrid = new Map<number, Vertex[]>();
    for (const v of this.halfEdges.vertices) {
      const key = this.gumTrimGridKey(v.position.x, v.position.y, v.position.z);
      let bucket = vertexGrid.get(key);
      if (!bucket) vertexGrid.set(key, (bucket = []));
      bucket.push(v);
    }
    this.gumTrimVertexGrid = vertexGrid;

    // Snapshot vertex colors so the preview tint can be undone cheaply.
    const colorAttr = this.geometry.getAttribute('color') as BufferAttribute | undefined;
    this.gumTrimSavedColors = colorAttr ? new Float32Array(colorAttr.array as Float32Array) : null;

    return { min: 0, max: Math.max(1, Math.ceil(max)) };
  }

  // --- Fine-tune spline over the cut line ---
  // After the uniform offset is chosen, the cut contour becomes an editable
  // spline: dragging its control points on the gum shifts the margin locally
  // (Gaussian-blended per-point deltas over the geodesic field), so the cut
  // follows the edited points while staying an iso-line of the field — it
  // cannot self-intersect or flip sides.

  private gumTrimSpline: InteractiveSpline | null = null;
  private gumTrimSplineBaseMargin = 0;
  private gumTrimLocalMargins: Map<Vertex, number> | null = null;

  public hasGumTrimSpline(): boolean {
    return this.gumTrimSpline !== null;
  }

  /** Iso-contour of the collar field at `marginMm` as an ordered world-space loop. */
  private extractGumTrimContour(marginMm: number): Vector3[] | null {
    const distances = this.gumTrimDistances;
    if (!distances) return null;

    // Crossing points live on mesh edges → exact ids, no float hashing.
    const keyOf = (a: Vertex, b: Vertex) => (a.id < b.id ? `${a.id}-${b.id}` : `${b.id}-${a.id}`);
    const points = new Map<string, Vector3>();
    const adjacency = new Map<string, string[]>();

    for (const face of this.halfEdges.faces) {
      const vs = face.getVertices();
      if (vs.length !== 3) continue;
      const keys: string[] = [];
      for (let i = 0; i < 3; i++) {
        const a = vs[i];
        const b = vs[(i + 1) % 3];
        const da = distances.get(a);
        const db = distances.get(b);
        if (da === undefined || db === undefined) continue;
        if (da < marginMm === db < marginMm) continue;
        const key = keyOf(a, b);
        if (!points.has(key)) {
          const t = Math.min(1, Math.max(0, (marginMm - da) / (db - da)));
          points.set(key, a.position.clone().lerp(b.position, Number.isFinite(t) ? t : 0));
        }
        keys.push(key);
      }
      if (keys.length !== 2) continue;
      let l0 = adjacency.get(keys[0]);
      if (!l0) adjacency.set(keys[0], (l0 = []));
      l0.push(keys[1]);
      let l1 = adjacency.get(keys[1]);
      if (!l1) adjacency.set(keys[1], (l1 = []));
      l1.push(keys[0]);
    }

    // Chain the segments and keep the longest polyline. Open chains are
    // valid too — the iso-line ends wherever it runs off the scan border.
    const visited = new Set<string>();
    const walk = (start: string): string[] => {
      const path = [start];
      visited.add(start);
      let prev: string | null = null;
      let curr = start;
      for (let guard = 0; guard <= points.size; guard++) {
        const next = adjacency.get(curr)?.find((n) => n !== prev && !visited.has(n));
        if (next === undefined) break;
        visited.add(next);
        path.push(next);
        prev = curr;
        curr = next;
      }
      return path;
    };

    const chains: string[][] = [];
    // Endpoints (degree 1) first so open chains are walked end-to-end...
    for (const [key, neighbors] of adjacency) {
      if (!visited.has(key) && neighbors.length === 1) chains.push(walk(key));
    }
    // ...then whatever remains are closed loops.
    for (const key of adjacency.keys()) {
      if (!visited.has(key)) chains.push(walk(key));
    }

    let best: Vector3[] | null = null;
    let bestLen = 0;
    for (const chain of chains) {
      const line = chain.map((k) => points.get(k)!);
      let len = 0;
      for (let i = 0; i < line.length - 1; i++) len += line[i].distanceTo(line[i + 1]);
      if (len > bestLen) {
        bestLen = len;
        best = line;
      }
    }
    return best ? best.map((p) => this.localToWorld(p.clone())) : null;
  }

  /**
   * Turns the current cut line into a draggable spline (fine-tune step).
   * Returns false when no closed contour exists at this margin.
   */
  public startGumTrimSplineEdit(marginMm: number): boolean {
    if (!this.gumTrimDistances) return false;
    this.disposeGumTrimSpline();
    const contour = this.extractGumTrimContour(marginMm);
    if (!contour || contour.length < 8) return false;

    let perimeter = 0;
    for (let i = 0; i < contour.length; i++) {
      perimeter += contour[i].distanceTo(contour[(i + 1) % contour.length]);
    }
    this.gumTrimSplineBaseMargin = marginMm;
    const spline = new InteractiveSpline(contour, this, {
      preordered: true,
      minSpacing: Math.max(1.5, perimeter / 48),
    });
    this.rootState.scene.add(spline);
    this.gumTrimSpline = spline;
    return true;
  }

  /**
   * Recomputes the per-vertex margins from the spline's dragged control
   * points and refreshes the preview. Call after a control-point drag ends.
   */
  public updateGumTrimFromSpline(): void {
    const spline = this.gumTrimSpline;
    if (!spline || !this.gumTrimDistances) return;
    const base = this.gumTrimSplineBaseMargin;

    // Each control point's delta = collar distance at its CURRENT position
    // minus the base margin (0 for unmoved points, since they sit on the iso).
    const controls: Array<{ p: Vector3; delta: number }> = [];
    spline.linePoints.forEach((cp) => {
      const d = this.gumTrimDistanceAtWorldPoint(cp.position);
      if (d === null) return;
      controls.push({ p: this.worldToLocal(cp.position.clone()), delta: d - base });
    });
    if (controls.length < 3) return;

    let spacing = 0;
    for (let i = 0; i < controls.length; i++) {
      spacing += controls[i].p.distanceTo(controls[(i + 1) % controls.length].p);
    }
    spacing /= controls.length;
    const sigma = Math.max(1.5, spacing * 1.5);
    const inv2Sigma2 = 1 / (2 * sigma * sigma);
    const supportSq = 9 * sigma * sigma;

    const margins = new Map<Vertex, number>();
    for (const v of this.halfEdges.vertices) {
      let wSum = 0;
      let dSum = 0;
      for (const c of controls) {
        const distSq = v.position.distanceToSquared(c.p);
        if (distSq > supportSq) continue;
        const w = Math.exp(-distSq * inv2Sigma2);
        wSum += w;
        dSum += w * c.delta;
      }
      if (wSum < 1e-9) continue;
      const adjusted = base + dSum / wSum;
      if (Math.abs(adjusted - base) > 1e-3) margins.set(v, Math.max(0, adjusted));
    }
    this.gumTrimLocalMargins = margins;
    this.previewGumTrimByTeeth(base);
  }

  /** Removes the fine-tune spline and its local margin adjustments. */
  public disposeGumTrimSpline(): void {
    if (this.gumTrimSpline) {
      this.rootState.scene.remove(this.gumTrimSpline);
      this.gumTrimSpline.dispose();
      this.gumTrimSpline = null;
    }
    this.gumTrimLocalMargins = null;
  }

  /** Collar distance of the gum vertex nearest the world point (drag-to-set-margin). */
  public gumTrimDistanceAtWorldPoint(worldPoint: Vector3): number | null {
    if (!this.gumTrimVertexGrid || !this.gumTrimDistances) return null;
    const p = this.worldToLocal(worldPoint.clone());
    const cell = JawSegmentation.GUM_TRIM_GRID_CELL;
    const cx = Math.floor(p.x / cell);
    const cy = Math.floor(p.y / cell);
    const cz = Math.floor(p.z / cell);
    let best: Vertex | null = null;
    let bestSq = Infinity;
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iy = cy - 1; iy <= cy + 1; iy++) {
        for (let iz = cz - 1; iz <= cz + 1; iz++) {
          const bucket = this.gumTrimVertexGrid.get((ix + 1024) * 2048 * 2048 + (iy + 1024) * 2048 + (iz + 1024));
          if (!bucket) continue;
          for (const v of bucket) {
            const d = v.position.distanceToSquared(p);
            if (d < bestSq) {
              bestSq = d;
              best = v;
            }
          }
        }
      }
    }
    if (!best) return null;
    const d = this.gumTrimDistances.get(best);
    return d === undefined || !Number.isFinite(d) ? null : d;
  }

  /**
   * Removal preview: the zone beyond the margin darkens to a muted red and the
   * cut line itself glows as a bright amber band, so the boundary reads
   * clearly against both the kept gum and the removed zone.
   */
  public previewGumTrimByTeeth(marginMm: number): void {
    if (!this.gumTrimDistances || !this.gumTrimSavedColors) return;
    const colorAttr = this.geometry.getAttribute('color') as BufferAttribute;
    const saved = this.gumTrimSavedColors;
    if ((colorAttr.array as Float32Array).length !== saved.length) return;
    (colorAttr.array as Float32Array).set(saved);
    const band = JawSegmentation.GUM_TRIM_BAND;
    for (const [v, d] of this.gumTrimDistances) {
      const renderId = this.halfEdges.vertexToRenderId.get(v);
      if (renderId === undefined) continue;
      const margin = this.gumTrimLocalMargins?.get(v) ?? marginMm;
      if (Math.abs(d - margin) <= band) {
        colorAttr.setXYZ(renderId, 1, 0.72, 0.08);
      } else if (d > margin) {
        // Unmistakable red over the whole removal delta, modulated by the
        // original color so the surface shape stays readable.
        colorAttr.setXYZ(
          renderId,
          0.2 + 0.8 * saved[renderId * 3],
          0.22 * saved[renderId * 3 + 1],
          0.22 * saved[renderId * 3 + 2],
        );
      }
    }
    colorAttr.needsUpdate = true;
  }

  /** Restores the original vertex colors (drops the trim preview tint). */
  public clearGumTrimPreview(): void {
    if (!this.gumTrimSavedColors) return;
    const colorAttr = this.geometry.getAttribute('color') as BufferAttribute | undefined;
    // Skip when the geometry was swapped since the snapshot (e.g. the revert
    // to the pre-segmentation backup) — a partial write would garble colors.
    if (colorAttr && (colorAttr.array as Float32Array).length === this.gumTrimSavedColors.length) {
      (colorAttr.array as Float32Array).set(this.gumTrimSavedColors);
      colorAttr.needsUpdate = true;
    }
  }

  /**
   * Cuts the gum along the trim curve offset by `marginMm`: removes every
   * vertex on the outside (with its faces) and relaxes the fresh boundary so
   * the cut edge comes out smooth. Rebuilds the geometry — the prepared
   * distance field is invalidated and recomputed. Returns removed count.
   */
  public applyGumTrimByTeeth(marginMm: number): number {
    if (!this.gumTrimDistances) return 0;
    const distances = this.gumTrimDistances;

    const toRemove: Vertex[] = [];
    for (const [v, d] of distances) {
      if (d > (this.gumTrimLocalMargins?.get(v) ?? marginMm)) toRemove.push(v);
    }
    if (toRemove.length === 0) return 0;
    if (toRemove.length >= this.halfEdges.vertices.length - 3) {
      console.warn('applyGumTrimByTeeth: margin would remove the whole gum — aborted');
      return 0;
    }
    // Only vertices that BECOME boundary by this cut get relaxed — tooth
    // seams and pre-existing scan holes must keep their exact shape.
    this.calculateBorderVertices();
    const boundaryBefore = new Set(this.getBorderVertices());

    for (const v of toRemove) {
      this.halfEdges.removeVertex(v, false);
    }

    this.calculateBorderVertices();
    const cutEdge = this.getBorderVertices().filter((v) => !boundaryBefore.has(v));
    for (let iter = 0; iter < 4; iter++) {
      const targets = new Map<Vertex, Vector3>();
      for (const v of cutEdge) {
        const ns = v.getAdjacenceBoundaryVertices();
        if (ns.length !== 2) continue;
        const mid = ns[0].position.clone().add(ns[1].position).multiplyScalar(0.5);
        targets.set(v, v.position.clone().lerp(mid, 0.5));
      }
      targets.forEach((pos, v) => v.position.copy(pos));
    }

    this.updateGeometryFromHalfedges();
    // Vertex objects survived (no DS rebuild until updateGeometryFromHalfedges
    // re-created the buffer), but renderIds and colors changed — re-prepare.
    // The fine-tune spline (if any) belongs to the pre-cut field — drop it.
    this.disposeGumTrimSpline();
    this.gumTrimDistances = null;
    this.gumTrimSavedColors = null;
    this.prepareGumTrimByTeeth();
    console.log(`applyGumTrimByTeeth: removed ${toRemove.length} vertices at margin ${marginMm.toFixed(1)} mm`);
    return toRemove.length;
  }

  /** Drops all trim state (leaving the review step). */
  public disposeGumTrim(): void {
    this.disposeGumTrimSpline();
    this.clearGumTrimPreview();
    this.gumTrimDistances = null;
    this.gumTrimVertexGrid = null;
    this.gumTrimSavedColors = null;
  }
}